import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { 
  Play, Pause, Eye, Users, Shield, Mail, Clock, Check, 
  ArrowLeft, Send, AlertTriangle, MessageSquare, Globe,
  Newspaper, Radio, Activity, Terminal, Lock, Zap,
  User, Building, FileText, Phone, PhoneOff, Volume2,
  Heart, MessageCircle, Share2, ThumbsUp, PenLine, Loader2,
  Target, X, Wrench, Smartphone, PhoneIncoming, PhoneMissed, PhoneCall, PhoneOutgoing,
  Database, Settings, LayoutGrid, Square
} from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import { Badge } from "./components/ui/badge";
import { ScrollArea } from "./components/ui/scroll-area";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./components/ui/tabs";
import { MediaLogo, SocialLogo } from "./MediaLogos";
import { InputsBankBrowser, PlayerConfiguration, InputsGenerator } from "./InputsManager";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WS_URL = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');

const APP_VERSION = "V 0.94";
const APP_DATE = "5 février 2026";
const APP_EDITOR = "Le Pool Presse SAS";
const EGERIE_IMAGE = "https://customer-assets.emergentagent.com/job_crisis-sim-ai/artifacts/bgij8dxp_image.png";

const ScanlineOverlay = () => <div className="scanline-overlay" />;

// Custom hook for demo session synchronization
const useDemoSync = (dashboardType) => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session');
  const [isConnected, setIsConnected] = useState(false);
  const [syncState, setSyncState] = useState(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  // Connect to WebSocket
  useEffect(() => {
    if (!sessionId) return;

    const connect = () => {
      try {
        // Use /api/ws/ path to go through ingress
        const ws = new WebSocket(`${WS_URL}/api/ws/demo/${sessionId}?dashboard=${dashboardType}`);
        wsRef.current = ws;

        ws.onopen = () => {
          setIsConnected(true);
          console.log(`[DemoSync] Connected to session ${sessionId} as ${dashboardType}`);
          // Request current state
          ws.send(JSON.stringify({ type: 'request_sync' }));
        };

        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            console.log(`[DemoSync] Received message type: ${data.type}`);
            
            if (data.type === 'sync_state') {
              console.log('[DemoSync] Full sync state received:', data.state);
              setSyncState(data.state);
            } else if (data.type === 'time_update') {
              // Always update time, create minimal state if needed
              setSyncState(prev => ({
                ...(prev || { is_running: false, triggered_events: [] }),
                time: data.time
              }));
            } else if (data.type === 'running_update') {
              // Always update running state, create minimal state if needed
              console.log('[DemoSync] Running update received:', data.is_running);
              setSyncState(prev => ({
                ...(prev || { time: '08:00', triggered_events: [] }),
                is_running: data.is_running
              }));
            } else if (data.type === 'event_triggered') {
              // Always process events, create minimal state if needed
              setSyncState(prev => ({
                ...(prev || { time: '08:00', is_running: false }),
                triggered_events: [...((prev?.triggered_events) || []), data.event]
              }));
            }
          } catch (e) {
            console.error('[DemoSync] Parse error:', e);
          }
        };

        ws.onclose = () => {
          setIsConnected(false);
          console.log('[DemoSync] Disconnected, reconnecting in 3s...');
          reconnectTimeoutRef.current = setTimeout(connect, 3000);
        };

        ws.onerror = (error) => {
          console.error('[DemoSync] WebSocket error:', error);
        };
      } catch (e) {
        console.error('[DemoSync] Connection error:', e);
      }
    };

    connect();

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [sessionId, dashboardType]);

  // Send methods (only animator should use these)
  const sendTimeUpdate = useCallback((time) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'time_update', time }));
    }
  }, []);

  const sendRunningUpdate = useCallback((isRunning) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'running_update', is_running: isRunning }));
    }
  }, []);

  const sendTriggerEvent = useCallback((event) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'trigger_event', event }));
    }
  }, []);

  return {
    sessionId,
    isConnected,
    syncState,
    sendTimeUpdate,
    sendRunningUpdate,
    sendTriggerEvent,
    isSynced: !!sessionId && isConnected
  };
};

// Radio Jingles - Local files for CORS compatibility
const FRANCE_INFO_JINGLE = "/france_info_jingle.mp3";
const EUROPE1_CARILLON = "/europe1_jingle.mp3";
const RTL_JINGLE = "/rtl_jingle.mp3";
const BFM_GENERIQUE = "/bfm_generique.mp4";
const BFM_MICRO_CAMERA = "/bfm_micro_camera.png";

// Radio Flash Player Component - Can replay flashes and share internally
const RadioFlashPlayer = ({ flash, onClose, onShareInternal, comexMembers = [], compact = false }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [phase, setPhase] = useState('idle'); // 'idle', 'tuning', 'jingle', 'playing'
  const [showShareModal, setShowShareModal] = useState(false);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [shareMessage, setShareMessage] = useState('');
  const audioRef = useRef(null);
  
  const playFlash = async () => {
    if (isPlaying) return;
    setIsPlaying(true);
    
    // Phase 1: Tuning static
    setPhase('tuning');
    await playTuningStatic();
    
    // Phase 2: Jingle
    setPhase('jingle');
    await playJingle(flash.station);
    
    // Phase 3: TTS
    setPhase('playing');
    await playTTS(flash);
    
    setPhase('idle');
    setIsPlaying(false);
  };
  
  const playTuningStatic = () => {
    return new Promise((resolve) => {
      try {
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const bufferSize = audioContext.sampleRate * 1.5;
        const noiseBuffer = audioContext.createBuffer(1, bufferSize, audioContext.sampleRate);
        const output = noiseBuffer.getChannelData(0);
        
        for (let i = 0; i < bufferSize; i++) {
          const noise = Math.random() * 2 - 1;
          const sweep = Math.sin(i / (audioContext.sampleRate / 20)) * 0.3;
          const envelope = Math.sin((i / bufferSize) * Math.PI);
          output[i] = (noise * 0.4 + sweep) * envelope * 0.4;
        }
        
        const noiseSource = audioContext.createBufferSource();
        noiseSource.buffer = noiseBuffer;
        
        const filter = audioContext.createBiquadFilter();
        filter.type = 'bandpass';
        filter.frequency.setValueAtTime(200, audioContext.currentTime);
        filter.frequency.linearRampToValueAtTime(2500, audioContext.currentTime + 0.7);
        filter.frequency.linearRampToValueAtTime(800, audioContext.currentTime + 1.2);
        
        const gainNode = audioContext.createGain();
        gainNode.gain.setValueAtTime(0.5, audioContext.currentTime);
        gainNode.gain.linearRampToValueAtTime(0.1, audioContext.currentTime + 1.2);
        
        noiseSource.connect(filter);
        filter.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        noiseSource.start();
        setTimeout(() => {
          audioContext.close().catch(() => {});
          resolve();
        }, 1600);
      } catch (e) {
        setTimeout(resolve, 500);
      }
    });
  };
  
  const playJingle = (station) => {
    return new Promise((resolve) => {
      const stationLower = station?.toLowerCase() || "";
      let jingleUrl = FRANCE_INFO_JINGLE;
      let duration = 5000;
      
      if (stationLower.includes('europe')) {
        jingleUrl = EUROPE1_CARILLON;
        duration = 8000;
      } else if (stationLower.includes('rtl')) {
        jingleUrl = RTL_JINGLE;
        duration = 6000;
      } else if (stationLower.includes('bfm')) {
        jingleUrl = BFM_GENERIQUE;
        duration = 4000;
      }
      
      const jingle = new Audio(jingleUrl);
      jingle.onended = () => resolve();
      jingle.onerror = () => setTimeout(resolve, 500);
      jingle.play().catch(() => setTimeout(resolve, 500));
      setTimeout(resolve, duration);
    });
  };
  
  const playTTS = async (flashData) => {
    try {
      const formattedText = `${flashData.station}, il est ${flashData.scenarioTime || "08:00"}. ${flashData.content} De plus amples informations dans notre prochain flash d'actualité.`;
      
      const res = await axios.post(`${API}/tts/generate`, {
        text: formattedText,
        voice: "nova",
        caller_name: flashData.station,
        speed: 1.2 // Faster for urgent news feel
      });
      
      // Handle both response formats: audio or audio_base64
      const audioData = res.data.audio || res.data.audio_base64;
      if (audioData) {
        return new Promise((resolve) => {
          const audio = new Audio(`data:audio/mp3;base64,${audioData}`);
          audioRef.current = audio;
          audio.onended = () => resolve();
          audio.onerror = () => resolve();
          audio.play().catch(() => resolve());
        });
      }
    } catch (e) {
      console.error("TTS error:", e);
    }
  };
  
  const stopPlayback = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setIsPlaying(false);
    setPhase('idle');
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      className={`${compact ? 'bg-gradient-to-b from-red-900 to-red-950 rounded-lg p-3 border border-red-700' : 'fixed inset-0 bg-black/90 z-[200] flex items-center justify-center p-4'}`}
      onClick={compact ? undefined : onClose}
    >
      <motion.div
        className={`${compact ? 'w-full' : 'bg-gradient-to-b from-red-900 to-red-950 rounded-lg p-6 max-w-2xl w-full border border-red-700'}`}
        onClick={e => e.stopPropagation()}
      >
        <div className={`flex items-center justify-between ${compact ? 'mb-2' : 'mb-4'}`}>
          <Badge className={`bg-white text-red-900 font-bold ${compact ? 'text-xs px-2 py-0.5' : 'text-lg px-4 py-1'}`}>
            📻 {compact ? flash.station?.toUpperCase() || "INFO" : `FLASH ${flash.station?.toUpperCase() || "INFO"}`}
          </Badge>
          <Button 
            variant="outline" 
            onClick={onClose} 
            size={compact ? "sm" : "default"}
            className={`text-white border-white/50 hover:bg-white/20 ${compact ? 'h-6 px-2 text-xs' : 'px-4'}`}
          >
            <X className={`${compact ? 'w-3 h-3' : 'w-4 h-4 mr-2'}`} />
            {!compact && "Fermer"}
          </Button>
        </div>
        
        <div className={`text-center ${compact ? 'mb-2' : 'mb-6'}`}>
          {!compact && (
            <div className="flex justify-center mb-4">
              <motion.div
                animate={isPlaying ? { scale: [1, 1.1, 1] } : {}}
                transition={{ repeat: Infinity, duration: 0.5 }}
                className="w-20 h-20 rounded-full bg-red-800 flex items-center justify-center"
              >
                <Radio className="w-10 h-10 text-white" />
              </motion.div>
            </div>
          )}
          
          {phase !== 'idle' && (
            <div className={compact ? 'mb-1' : 'mb-4'}>
              <Badge variant="outline" className={`text-yellow-400 border-yellow-400 ${compact ? 'text-xs' : ''}`}>
                {phase === 'tuning' && '📡 Syntonisation...'}
                {phase === 'jingle' && '🎵 Jingle...'}
                {phase === 'playing' && '● EN DIRECT'}
              </Badge>
            </div>
          )}
          
          <p className={`text-white/90 leading-relaxed ${compact ? 'text-xs mb-1' : 'text-base mb-2'}`}>
            <span className={`text-yellow-400 font-mono ${compact ? 'text-[10px]' : 'text-sm'}`}>[{flash.scenarioTime}]</span>
          </p>
          <p className={`text-white leading-relaxed ${compact ? 'text-xs line-clamp-2' : 'text-lg'}`}>
            {flash.content}
          </p>
        </div>
        
        <div className={`flex justify-center gap-2 ${compact ? '' : 'flex-wrap gap-4'}`}>
          {!isPlaying ? (
            <Button
              onClick={playFlash}
              size={compact ? "sm" : "default"}
              className={`bg-green-600 hover:bg-green-700 text-white ${compact ? 'text-xs px-3 h-7' : 'px-8 py-3 text-lg'}`}
            >
              <Play className={`${compact ? 'w-3 h-3 mr-1' : 'w-5 h-5 mr-2'}`} /> {compact ? 'Écouter' : 'Écouter le flash'}
            </Button>
          ) : (
            <Button
              onClick={stopPlayback}
              size={compact ? "sm" : "default"}
              className={`bg-red-600 hover:bg-red-700 text-white ${compact ? 'text-xs px-3 h-7' : 'px-8 py-3 text-lg'}`}
            >
              <Square className={`${compact ? 'w-3 h-3 mr-1' : 'w-5 h-5 mr-2'}`} /> {compact ? 'Stop' : 'Arrêter'}
            </Button>
          )}
          {!compact && (
            <Button
              onClick={() => setShowShareModal(true)}
              variant="outline"
              className="border-white/50 text-white hover:bg-white/10 px-6 py-3"
            >
              <Mail className="w-5 h-5 mr-2" /> Envoyer au COMEX
            </Button>
          )}
        </div>
        
        {/* Internal Share Modal */}
        <AnimatePresence>
          {showShareModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/80 z-[250] flex items-center justify-center p-4"
              onClick={() => setShowShareModal(false)}
            >
              <motion.div
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.9 }}
                className="bg-card border border-border rounded-lg p-6 max-w-md w-full"
                onClick={e => e.stopPropagation()}
              >
                <h3 className="text-lg font-semibold text-primary mb-4 flex items-center gap-2">
                  <Mail className="w-5 h-5" /> Envoyer aux membres du COMEX
                </h3>
                
                <p className="text-sm text-muted-foreground mb-4">
                  📻 Flash {flash.station} [{flash.scenarioTime}]
                </p>
                
                {/* Recipients selection */}
                <div className="mb-4">
                  <label className="text-sm font-medium mb-2 block">Destinataires :</label>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {[
                      { id: 'DG', name: 'Directeur Général' },
                      { id: 'DirCom', name: 'Directeur Communication' },
                      { id: 'DRH', name: 'DRH' },
                      { id: 'DirOps', name: 'Directeur Opérations' },
                      { id: 'DirQualite', name: 'Directeur Qualité' },
                      { id: 'DirJuridique', name: 'Directeur Juridique' },
                      { id: 'all', name: '📢 Tout le COMEX' }
                    ].map(member => (
                      <label key={member.id} className="flex items-center gap-2 cursor-pointer hover:bg-secondary/50 p-2 rounded">
                        <input
                          type="checkbox"
                          checked={selectedRecipients.includes(member.id) || selectedRecipients.includes('all')}
                          onChange={(e) => {
                            if (member.id === 'all') {
                              setSelectedRecipients(e.target.checked ? ['all'] : []);
                            } else {
                              setSelectedRecipients(prev => 
                                e.target.checked 
                                  ? [...prev.filter(r => r !== 'all'), member.id]
                                  : prev.filter(r => r !== member.id)
                              );
                            }
                          }}
                          className="w-4 h-4"
                        />
                        <span className="text-sm">{member.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
                
                {/* Optional message */}
                <div className="mb-4">
                  <label className="text-sm font-medium mb-2 block">Message (optionnel) :</label>
                  <Textarea
                    className="input-matrix text-sm"
                    placeholder="Ajoutez un commentaire pour le COMEX..."
                    value={shareMessage}
                    onChange={(e) => setShareMessage(e.target.value)}
                    rows={2}
                  />
                </div>
                
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => setShowShareModal(false)}
                  >
                    Annuler
                  </Button>
                  <Button
                    className="flex-1 bg-primary"
                    disabled={selectedRecipients.length === 0}
                    onClick={() => {
                      // Send internal message with flash link
                      if (onShareInternal) {
                        onShareInternal({
                          flash,
                          recipients: selectedRecipients,
                          message: shareMessage,
                          sender: 'DirCom'
                        });
                      }
                      toast.success("📨 Message envoyé !", {
                        description: `Flash partagé avec ${selectedRecipients.includes('all') ? 'tout le COMEX' : selectedRecipients.length + ' membre(s)'}`
                      });
                      setShowShareModal(false);
                      setSelectedRecipients([]);
                      setShareMessage('');
                    }}
                  >
                    <Send className="w-4 h-4 mr-2" /> Envoyer
                  </Button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </motion.div>
  );
};

// Radio Flash Component - Auto-playing radio announcement with full sequence
// Radio Flash Notification - Responsive notification with big LISTEN button
const RadioFlashNotification = ({ station, content, scenarioTime, onListen, onDismiss, isVisible }) => {
  if (!isVisible) return null;

  return (
    <motion.div
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: -100, opacity: 0 }}
      className="fixed top-2 left-2 right-2 sm:top-4 sm:left-1/2 sm:-translate-x-1/2 sm:left-auto sm:right-auto z-[150] sm:w-full sm:max-w-md"
    >
      <div className="bg-gradient-to-br from-red-900 via-red-800 to-red-900 rounded-xl shadow-2xl border-2 border-red-500 overflow-hidden">
        {/* Header */}
        <div className="bg-red-950 px-3 py-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <motion.div
              animate={{ scale: [1, 1.3, 1] }}
              transition={{ repeat: Infinity, duration: 0.6 }}
              className="w-2 h-2 sm:w-3 sm:h-3 bg-red-500 rounded-full"
            />
            <span className="text-white font-bold text-sm sm:text-base">📻 FLASH RADIO</span>
          </div>
          <span className="text-yellow-400 font-mono text-xs sm:text-sm">{scenarioTime}</span>
        </div>
        
        {/* Content */}
        <div className="p-3 sm:p-4">
          <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center flex-shrink-0">
              <Radio className="w-5 h-5 sm:w-6 sm:h-6 text-red-900" />
            </div>
            <div className="min-w-0">
              <h3 className="text-white font-bold text-base sm:text-lg truncate">{station || "France Info"}</h3>
              <p className="text-red-300 text-xs">Flash d'information</p>
            </div>
          </div>
          
          <p className="text-white/90 text-xs sm:text-sm leading-relaxed mb-3 sm:mb-4 line-clamp-2">
            {content}
          </p>
          
          {/* BIG LISTEN BUTTON */}
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={onListen}
            className="w-full py-3 sm:py-4 bg-gradient-to-r from-green-600 to-green-500 hover:from-green-500 hover:to-green-400 text-white rounded-lg font-bold text-base sm:text-lg flex items-center justify-center gap-2 sm:gap-3 shadow-lg"
          >
            <Volume2 className="w-5 h-5 sm:w-6 sm:h-6" />
            ÉCOUTER LA RADIO
          </motion.button>
          
          {/* Small dismiss link */}
          <button
            onClick={onDismiss}
            className="w-full mt-2 py-1 text-red-400 hover:text-red-300 text-xs"
          >
            Fermer
          </button>
        </div>
      </div>
    </motion.div>
  );
};

// BFM TV Micro-Trottoir Component - Plays testimonials with camera image
const BFMTVMicroTrottoir = ({ isOpen, onClose, scenarioContext, scenarioTime }) => {
  const [phase, setPhase] = useState('intro'); // 'intro', 'playing', 'done'
  const [currentTestimony, setCurrentTestimony] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const audioRef = useRef(null);
  const videoRef = useRef(null);
  const isPlayingRef = useRef(false);
  const hasStartedRef = useRef(false);
  const mountedRef = useRef(true);
  
  const testimonies = [
    { role: "SALARIÉ ANONYME", text: "On nous a dit de sortir précipitamment. On ne sait pas ce qui se passe vraiment. Les responsables ne nous disent rien. C'est angoissant.", voice: "onyx" },
    { role: "RIVERAINE INQUIÈTE", text: "J'habite à 500 mètres. L'odeur est insupportable depuis tout à l'heure. J'ai peur pour mes enfants, on ne sait pas ce qu'on respire.", voice: "nova" },
    { role: "COMMERÇANT DU QUARTIER", text: "Ça fait deux heures que les pompiers sont là. Les clients ne viennent plus, tout le monde a peur. C'est une catastrophe pour nous aussi.", voice: "onyx" },
    { role: "ANCIENNE EMPLOYÉE", text: "Je ne suis pas surprise. Quand je travaillais là-bas, on voyait bien que la sécurité n'était pas la priorité. C'était prévisible.", voice: "nova" }
  ];
  
  const [userStarted, setUserStarted] = useState(false);
  
  // Cleanup on unmount AND reset when reopening
  useEffect(() => {
    mountedRef.current = true;
    // Reset all states when component opens to allow replay
    hasStartedRef.current = false;
    setUserStarted(false);
    setPhase('waiting'); // New phase: waiting for user tap
    setCurrentTestimony(0);
    
    return () => {
      mountedRef.current = false;
      hasStartedRef.current = false;
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, [isOpen]);
  
  // Start playback when user taps
  const handleUserStart = () => {
    console.log(`[BFM TV] User tapped START`);
    setUserStarted(true);
    playIntro();
  };
  
  const playIntro = async () => {
    if (hasStartedRef.current || !mountedRef.current) {
      console.log(`[BFM TV] playIntro skipped: hasStarted=${hasStartedRef.current}, mounted=${mountedRef.current}`);
      return;
    }
    hasStartedRef.current = true;
    console.log(`[BFM TV] Starting intro video...`);
    
    setPhase('intro');
    if (videoRef.current) {
      try {
        videoRef.current.currentTime = 0;
        await videoRef.current.play();
        console.log(`[BFM TV] Intro video playing, waiting for end...`);
        // Wait for video to end naturally, with a max timeout of 20s
        await new Promise(resolve => {
          const onEnd = () => {
            console.log(`[BFM TV] Intro video ended`);
            videoRef.current?.removeEventListener('ended', onEnd);
            resolve();
          };
          videoRef.current.addEventListener('ended', onEnd);
          setTimeout(() => {
            console.log(`[BFM TV] Intro video timeout (20s)`);
            resolve();
          }, 20000);
        });
      } catch (e) {
        console.log(`[BFM TV] Video play error:`, e.message);
        await new Promise(resolve => setTimeout(resolve, 3000));
      }
    } else {
      console.log(`[BFM TV] No video ref, skipping intro`);
      await new Promise(resolve => setTimeout(resolve, 2000));
    }
    
    if (!mountedRef.current) {
      console.log(`[BFM TV] Component unmounted after intro`);
      return;
    }
    
    console.log(`[BFM TV] Transitioning to 'playing' phase`);
    setPhase('playing');
    
    // Small delay to ensure phase change is rendered
    await new Promise(resolve => setTimeout(resolve, 100));
    
    console.log(`[BFM TV] Calling playNextTestimony(0)`);
    playNextTestimony(0);
  };
  
  const playNextTestimony = async (index) => {
    if (!mountedRef.current) {
      console.log(`[BFM TV] Component unmounted, skipping testimony ${index}`);
      return;
    }
    
    if (index >= testimonies.length) {
      console.log(`[BFM TV] All testimonies played, setting phase to done`);
      setPhase('done');
      isPlayingRef.current = false;
      return;
    }
    
    // Prevent double play of same testimony
    if (isPlayingRef.current && index === currentTestimony) {
      console.log(`[BFM TV] Already playing testimony ${index}, skipping`);
      return;
    }
    isPlayingRef.current = true;
    
    setCurrentTestimony(index);
    setIsGenerating(true);
    
    // Clean up previous audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.onended = null;
      audioRef.current.onerror = null;
      audioRef.current = null;
    }
    
    try {
      console.log(`[BFM TV] Generating TTS for testimony ${index}: "${testimonies[index].text.substring(0, 50)}..." with voice: ${testimonies[index].voice}`);
      
      // FASTER speed (1.3) for stressed/dramatic effect
      const response = await axios.post(`${API}/tts/generate`, {
        text: testimonies[index].text,
        voice: testimonies[index].voice,
        speed: 1.3
      });
      
      console.log(`[BFM TV] TTS API response status: ${response.status}`);
      
      if (!mountedRef.current) {
        console.log(`[BFM TV] Component unmounted after TTS call`);
        return;
      }
      setIsGenerating(false);
      
      const audioData = response.data.audio || response.data.audio_base64;
      console.log(`[BFM TV] Audio data received: ${audioData ? `${audioData.length} chars` : 'NONE'}`);
      
      if (audioData) {
        const audio = new Audio(`data:audio/mp3;base64,${audioData}`);
        audioRef.current = audio;
        
        audio.onended = () => {
          console.log(`[BFM TV] Testimony ${index} audio ended naturally`);
          if (!mountedRef.current) return;
          isPlayingRef.current = false;
          setTimeout(() => {
            if (mountedRef.current) playNextTestimony(index + 1);
          }, 500);
        };
        
        audio.onerror = (e) => {
          console.error(`[BFM TV] Audio error for testimony ${index}:`, e);
          if (!mountedRef.current) return;
          isPlayingRef.current = false;
          setTimeout(() => {
            if (mountedRef.current) playNextTestimony(index + 1);
          }, 500);
        };
        
        // Try to play
        console.log(`[BFM TV] Attempting to play testimony ${index}...`);
        try {
          await audio.play();
          console.log(`[BFM TV] ✓ Playing testimony ${index} successfully`);
        } catch (playError) {
          console.error(`[BFM TV] ✗ Play blocked for testimony ${index}:`, playError.message);
          isPlayingRef.current = false;
          setTimeout(() => {
            if (mountedRef.current) playNextTestimony(index + 1);
          }, 500);
        }
      } else {
        console.log(`[BFM TV] No audio data for testimony ${index}, skipping`);
        isPlayingRef.current = false;
        setTimeout(() => {
          if (mountedRef.current) playNextTestimony(index + 1);
        }, 500);
      }
    } catch (error) {
      console.error(`[BFM TV] TTS generation error for testimony ${index}:`, error.message);
      if (!mountedRef.current) return;
      setIsGenerating(false);
      isPlayingRef.current = false;
      setTimeout(() => {
        if (mountedRef.current) playNextTestimony(index + 1);
      }, 500);
    }
  };
  
  // No more auto-start - removed the automatic playIntro trigger
  // User must tap to start (required for mobile audio)
  
  useEffect(() => {
    if (!isOpen) {
      hasStartedRef.current = false;
      isPlayingRef.current = false;
      setPhase('waiting');
      setCurrentTestimony(0);
      setUserStarted(false);
    }
  }, [isOpen]);
  
  if (!isOpen) return null;
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[200] bg-black"
    >
      {/* === WAITING PHASE - User must tap to start (mobile audio requirement) === */}
      {phase === 'waiting' && (
        <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-b from-red-900 to-black p-8">
          <img 
            src="/logo_bfmtv.jpg" 
            alt="BFM TV" 
            className="h-24 sm:h-32 rounded-xl shadow-2xl mb-8"
          />
          <h2 className="text-white text-2xl sm:text-4xl font-black text-center mb-4">
            MICRO-TROTTOIR
          </h2>
          <p className="text-white/80 text-lg text-center mb-8">
            Témoignages des riverains et salariés
          </p>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleUserStart}
            className="bg-red-600 hover:bg-red-500 text-white text-xl sm:text-2xl font-bold px-12 py-6 rounded-2xl shadow-2xl flex items-center gap-4"
          >
            <Play className="w-8 h-8" />
            DÉMARRER
          </motion.button>
          <button
            onClick={onClose}
            className="mt-8 text-white/60 hover:text-white text-lg underline"
          >
            Annuler
          </button>
        </div>
      )}
      
      {/* === INTRO PHASE === */}
      {phase === 'intro' && (
        <div className="w-full h-full flex items-center justify-center bg-black">
          <video
            ref={videoRef}
            src="/bfm_generique_video.mp4"
            className="w-full h-full object-contain"
            muted={false}
            playsInline
            autoPlay
          />
        </div>
      )}
      
      {/* === PLAYING PHASE === */}
      {phase === 'playing' && (
        <div className="w-full h-full relative">
          {/* Background image - Full screen */}
          <img 
            src={BFM_MICRO_CAMERA} 
            alt="BFM TV Micro-Trottoir" 
            className="absolute inset-0 w-full h-full object-cover"
          />
          
          {/* === OVERLAY ELEMENTS (z-index above image) === */}
          
          {/* TOP BAR - Logo + DIRECT + Time */}
          <div className="absolute top-0 left-0 right-0 z-20 bg-gradient-to-b from-black/80 to-transparent p-4">
            <div className="flex items-center justify-between">
              {/* Logo BFM */}
              <img 
                src="/logo_bfmtv.jpg" 
                alt="BFM TV" 
                className="h-14 sm:h-20 rounded-lg shadow-2xl"
              />
              
              {/* DIRECT + Time - BIGGER */}
              <div className="flex items-center gap-4 bg-black/70 px-6 py-3 rounded-xl">
                <motion.div
                  animate={{ opacity: [1, 0.2, 1], scale: [1, 1.2, 1] }}
                  transition={{ repeat: Infinity, duration: 0.8 }}
                  className="w-6 h-6 bg-red-600 rounded-full"
                />
                <span className="text-white text-2xl sm:text-4xl font-black tracking-widest">DIRECT</span>
                <span className="text-white text-2xl sm:text-3xl font-bold ml-4">{scenarioTime}</span>
              </div>
              
              {/* Close button - MORE VISIBLE */}
              <button
                onClick={onClose}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-3 rounded-xl transition-colors flex items-center gap-2"
              >
                <ArrowLeft className="w-6 h-6" />
                <span className="text-lg font-bold hidden sm:inline">Retour</span>
              </button>
            </div>
          </div>
          
          {/* SYNTHE / BANC-TITRE - Main title banner */}
          <div className="absolute top-28 sm:top-32 left-0 right-0 z-20">
            <div className="bg-red-700 py-3 px-6">
              <p className="text-white text-xl sm:text-3xl font-black tracking-wide text-center">
                🔴 INCENDIE ACME CORPORATION : RIVERAINS ET SALARIÉS TÉMOIGNENT
              </p>
            </div>
          </div>
          
          {/* MICRO-TROTTOIR Badge */}
          <motion.div
            animate={{ scale: [1, 1.05, 1] }}
            transition={{ repeat: Infinity, duration: 1 }}
            className="absolute top-44 sm:top-48 right-4 z-20 bg-red-600 text-white px-5 py-3 rounded-xl shadow-2xl"
          >
            <span className="text-xl sm:text-2xl font-black tracking-wide">🎙️ MICRO-TROTTOIR</span>
          </motion.div>
          
          {/* TESTIMONY OVERLAY - Bottom */}
          <div className="absolute bottom-20 left-0 right-0 z-20 bg-gradient-to-t from-black via-black/95 to-transparent p-6 pt-12">
            <div className="max-w-5xl mx-auto">
              <div className="flex items-center gap-4 mb-4">
                <motion.div
                  animate={{ scale: isGenerating ? 1 : [1, 1.3, 1] }}
                  transition={{ repeat: Infinity, duration: 0.4 }}
                  className="w-14 h-14 rounded-full bg-red-600 flex items-center justify-center"
                >
                  <Volume2 className="w-8 h-8 text-white" />
                </motion.div>
                <div>
                  <p className="text-red-400 font-black text-2xl sm:text-3xl tracking-wide">{testimonies[currentTestimony]?.role}</p>
                  <p className="text-white/60 text-base sm:text-lg">TÉMOIGNAGE {currentTestimony + 1}/{testimonies.length}</p>
                </div>
              </div>
              
              <p className="text-white text-2xl sm:text-3xl leading-relaxed italic font-medium">
                « {testimonies[currentTestimony]?.text} »
              </p>
              
              {/* Progress bar */}
              <div className="mt-6 flex gap-3">
                {testimonies.map((_, i) => (
                  <motion.div 
                    key={i}
                    className={`flex-1 h-3 rounded-full ${i <= currentTestimony ? 'bg-red-600' : 'bg-white/30'}`}
                    initial={i === currentTestimony ? { scaleX: 0 } : {}}
                    animate={i === currentTestimony ? { scaleX: 1 } : {}}
                    transition={{ duration: 2.5 }}
                  />
                ))}
              </div>
            </div>
          </div>
          
          {/* BOTTOM TICKER - News banner */}
          <div className="absolute bottom-0 left-0 right-0 z-20 bg-red-700 py-4 overflow-hidden">
            <motion.div
              animate={{ x: ['100%', '-200%'] }}
              transition={{ repeat: Infinity, duration: 35, ease: "linear" }}
              className="text-white whitespace-nowrap font-black text-2xl sm:text-3xl tracking-wider"
            >
              🔴 ÉDITION SPÉCIALE • INCENDIE INDUSTRIEL À PROXIMITÉ D'UN SITE SEVESO • LES RIVERAINS TÉMOIGNENT DE LEUR INQUIÉTUDE • LE PRÉFET ATTENDU SUR PLACE • PÉRIMÈTRE DE SÉCURITÉ ÉTABLI • 120 POMPIERS MOBILISÉS • QUALITÉ DE L'AIR EN QUESTION 🔴
            </motion.div>
          </div>
        </div>
      )}
      
      {/* === DONE PHASE === */}
      {phase === 'done' && (
        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-900 to-black">
          <div className="text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="w-28 h-28 mx-auto mb-6 rounded-full bg-green-500/20 flex items-center justify-center border-4 border-green-500"
            >
              <Radio className="w-14 h-14 text-green-500" />
            </motion.div>
            <h3 className="text-white text-4xl font-black mb-3 tracking-wide">FIN DU MICRO-TROTTOIR</h3>
            <p className="text-white/60 text-xl mb-10">4 TÉMOIGNAGES RECUEILLIS SUR LE TERRAIN</p>
            <Button 
              onClick={onClose} 
              className="bg-red-600 hover:bg-red-700 text-white text-xl px-10 py-4 font-bold"
            >
              RETOUR AU TABLEAU DE BORD
            </Button>
          </div>
        </div>
      )}
    </motion.div>
  );
};

// Radio Flash Active Player - Plays jingle with TTS voice overlaid using Web Audio API
const RadioFlashActivePlayer = ({ station, content, scenarioTime, onComplete, isVisible, onSaveFlash }) => {
  const [phase, setPhase] = useState('idle'); // 'idle', 'jingle', 'playing', 'done'
  const [progress, setProgress] = useState(0);
  const audioContextRef = useRef(null);
  const jingleSourceRef = useRef(null);
  const ttsSourceRef = useRef(null);
  const jingleGainRef = useRef(null);
  const isPlayingRef = useRef(false); // Lock to prevent double playback

  // Convert time to French words
  const timeToFrenchWords = (timeStr) => {
    const [hours, minutes] = (timeStr || "08:00").split(':').map(Number);
    const hoursWords = [
      'minuit', 'une heure', 'deux heures', 'trois heures', 'quatre heures',
      'cinq heures', 'six heures', 'sept heures', 'huit heures', 'neuf heures',
      'dix heures', 'onze heures', 'midi', 'treize heures', 'quatorze heures',
      'quinze heures', 'seize heures', 'dix-sept heures', 'dix-huit heures',
      'dix-neuf heures', 'vingt heures', 'vingt-et-une heures', 'vingt-deux heures', 'vingt-trois heures'
    ];
    
    let result = hoursWords[hours] || `${hours} heures`;
    
    if (minutes > 0) {
      if (minutes === 30) {
        result += ' trente';
      } else if (minutes === 15) {
        result += ' quinze';
      } else if (minutes === 45) {
        result += ' quarante-cinq';
      } else {
        result += ` ${minutes}`;
      }
    }
    
    return result;
  };

  // Stop all audio
  const stopAllAudio = useCallback(() => {
    isPlayingRef.current = false;
    try {
      if (jingleSourceRef.current) {
        jingleSourceRef.current.stop();
        jingleSourceRef.current = null;
      }
      if (ttsSourceRef.current) {
        ttsSourceRef.current.stop();
        ttsSourceRef.current = null;
      }
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
    } catch (e) {
      // Ignore cleanup errors
    }
  }, []);

  // Load audio buffer from URL or base64
  const loadAudioBuffer = async (audioContext, source, isBase64 = false) => {
    try {
      let arrayBuffer;
      if (isBase64) {
        const binaryString = atob(source);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        arrayBuffer = bytes.buffer;
      } else {
        const response = await fetch(source);
        arrayBuffer = await response.arrayBuffer();
      }
      return await audioContext.decodeAudioData(arrayBuffer);
    } catch (err) {
      console.error("Error loading audio:", err);
      return null;
    }
  };

  // Start the radio flash sequence with mixed audio
  const startPlaying = useCallback(async () => {
    // LOCK: Prevent double playback
    if (phase !== 'idle' || isPlayingRef.current) return;
    isPlayingRef.current = true;
    
    // Save flash for replay
    if (onSaveFlash) {
      onSaveFlash({ station, content, scenarioTime });
    }
    
    setPhase('jingle');
    
    try {
      // Create audio context
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      const audioContext = new AudioContext();
      audioContextRef.current = audioContext;
      
      // Determine jingle based on station
      const stationLower = station?.toLowerCase() || "";
      let jingleUrl = FRANCE_INFO_JINGLE;
      if (stationLower.includes('europe')) {
        jingleUrl = EUROPE1_CARILLON;
      } else if (stationLower.includes('rtl')) {
        jingleUrl = RTL_JINGLE;
      } else if (stationLower.includes('bfm')) {
        jingleUrl = BFM_GENERIQUE;
      }
      
      console.log("Loading jingle from:", jingleUrl);
      
      // Load jingle
      const jingleBuffer = await loadAudioBuffer(audioContext, jingleUrl);
      
      if (jingleBuffer) {
        // Create jingle source with gain control
        const jingleSource = audioContext.createBufferSource();
        jingleSource.buffer = jingleBuffer;
        
        const jingleGain = audioContext.createGain();
        jingleGain.gain.value = 0.8; // Jingle volume
        jingleGainRef.current = jingleGain;
        
        jingleSource.connect(jingleGain);
        jingleGain.connect(audioContext.destination);
        
        jingleSourceRef.current = jingleSource;
        jingleSource.start(0);
        console.log("Jingle started, duration:", jingleBuffer.duration);
        
        // After 2 seconds, lower jingle volume and start TTS
        setTimeout(async () => {
          // Check if still playing
          if (!isPlayingRef.current) return;
          
          // Fade down jingle to become background music
          if (jingleGainRef.current && audioContextRef.current) {
            jingleGainRef.current.gain.linearRampToValueAtTime(0.15, audioContextRef.current.currentTime + 0.5);
          }
          
          setPhase('playing');
          
          // Generate and play TTS
          try {
            const timeInWords = timeToFrenchWords(scenarioTime);
            const formattedText = `${station || "France Info"}, il est ${timeInWords}. ${content}. De plus amples informations dans notre prochain flash.`;
            
            console.log("Requesting TTS with time:", timeInWords);
            const res = await axios.post(`${API}/tts/generate`, {
              text: formattedText,
              voice: "nova",
              caller_name: station,
              speed: 1.2 // Urgent news delivery
            });
            
            // Check if still playing after async call
            if (!isPlayingRef.current || !audioContextRef.current) return;
            
            const audioData = res.data.audio || res.data.audio_base64;
            if (audioData && audioContextRef.current) {
              const ttsBuffer = await loadAudioBuffer(audioContextRef.current, audioData, true);
              
              if (ttsBuffer && isPlayingRef.current) {
                const ttsSource = audioContextRef.current.createBufferSource();
                ttsSource.buffer = ttsBuffer;
                
                const ttsGain = audioContextRef.current.createGain();
                ttsGain.gain.value = 1.0; // TTS at full volume
                
                ttsSource.connect(ttsGain);
                ttsGain.connect(audioContextRef.current.destination);
                
                ttsSourceRef.current = ttsSource;
                
                // Track progress
                const startTime = audioContextRef.current.currentTime;
                const duration = ttsBuffer.duration;
                
                const updateProgress = () => {
                  if (audioContextRef.current && phase === 'playing') {
                    const elapsed = audioContextRef.current.currentTime - startTime;
                    setProgress(Math.min((elapsed / duration) * 100, 100));
                    if (elapsed < duration) {
                      requestAnimationFrame(updateProgress);
                    }
                  }
                };
                
                ttsSource.onended = () => {
                  console.log("TTS finished");
                  // Fade out jingle
                  if (jingleGainRef.current && audioContextRef.current) {
                    jingleGainRef.current.gain.linearRampToValueAtTime(0, audioContextRef.current.currentTime + 1);
                  }
                  setPhase('done');
                  setTimeout(() => {
                    stopAllAudio();
                    onComplete?.();
                  }, 1500);
                };
                
                ttsSource.start(0);
                console.log("TTS started, duration:", ttsBuffer.duration);
                requestAnimationFrame(updateProgress);
              }
            }
          } catch (err) {
            console.error("TTS error:", err);
            setPhase('done');
            setTimeout(() => {
              stopAllAudio();
              onComplete?.();
            }, 2000);
          }
        }, 2000); // Start TTS after 2 seconds of jingle
        
      } else {
        // Jingle failed to load, go directly to TTS
        console.log("Jingle failed, playing TTS only");
        setPhase('playing');
        
        const timeStr = scenarioTime || "08:00";
        const formattedText = `${station || "France Info"}, il est ${timeStr}. ${content}`;
        
        const res = await axios.post(`${API}/tts/generate`, {
          text: formattedText,
          voice: "nova",
          speed: 1.2 // Urgent delivery
        });
        
        const audioData = res.data.audio || res.data.audio_base64;
        if (audioData) {
          const ttsBuffer = await loadAudioBuffer(audioContext, audioData, true);
          if (ttsBuffer) {
            const ttsSource = audioContext.createBufferSource();
            ttsSource.buffer = ttsBuffer;
            ttsSource.connect(audioContext.destination);
            ttsSourceRef.current = ttsSource;
            
            ttsSource.onended = () => {
              setPhase('done');
              setTimeout(() => {
                stopAllAudio();
                onComplete?.();
              }, 1000);
            };
            
            ttsSource.start(0);
          }
        }
      }
    } catch (err) {
      console.error("Radio flash error:", err);
      setPhase('done');
      setTimeout(() => {
        stopAllAudio();
        onComplete?.();
      }, 1000);
    }
  }, [phase, station, content, scenarioTime, onComplete, onSaveFlash, stopAllAudio]);

  // Auto-start when visible - WITH STRICT CHECK
  useEffect(() => {
    // Only start if idle AND not already playing (prevents React Strict Mode double trigger)
    if (isVisible && phase === 'idle' && !isPlayingRef.current) {
      // Small delay to ensure component is fully mounted
      const timer = setTimeout(() => {
        if (phase === 'idle' && !isPlayingRef.current) {
          startPlaying();
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isVisible]); // Remove phase and startPlaying from deps to prevent re-triggers

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopAllAudio();
    };
  }, [stopAllAudio]);

  if (!isVisible) return null;

  return (
    <motion.div
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: -100, opacity: 0 }}
      className="fixed top-0 left-0 right-0 z-[150] bg-gradient-to-b from-red-900 to-red-800 shadow-2xl"
    >
      <div className="max-w-4xl mx-auto p-4">
        <div className="flex items-center gap-4">
          {/* Radio icon with animation */}
          <div className="relative">
            <Radio className="w-10 h-10 text-white" />
            {phase === 'jingle' && (
              <motion.div
                className="absolute inset-0 flex items-center justify-center"
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ repeat: Infinity, duration: 0.5 }}
              >
                <div className="w-3 h-3 bg-yellow-400 rounded-full" />
              </motion.div>
            )}
            {phase === 'playing' && (
              <motion.div
                className="absolute inset-0 border-2 border-white rounded-full"
                animate={{ scale: [1, 1.5, 1], opacity: [1, 0, 1] }}
                transition={{ repeat: Infinity, duration: 1 }}
              />
            )}
          </div>
          
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <Badge className="bg-white text-red-900 font-bold">
                📻 {station?.toUpperCase() || "FRANCE INFO"}
              </Badge>
              {phase === 'jingle' && (
                <motion.span
                  animate={{ opacity: [1, 0.5, 1] }}
                  transition={{ repeat: Infinity, duration: 0.3 }}
                  className="text-xs text-yellow-300 font-mono"
                >
                  🎵 JINGLE...
                </motion.span>
              )}
              {phase === 'playing' && (
                <motion.span
                  animate={{ opacity: [1, 0.3, 1] }}
                  transition={{ repeat: Infinity, duration: 0.8 }}
                  className="text-xs text-white font-mono"
                >
                  ● EN DIRECT
                </motion.span>
              )}
              {phase === 'done' && (
                <span className="text-xs text-green-400 font-mono">✓ TERMINÉ</span>
              )}
            </div>
            
            <span className="text-yellow-400 font-mono text-xs mb-1 block">[{scenarioTime}]</span>
            
            <motion.p 
              className="text-white text-sm leading-relaxed"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              {phase === 'jingle' && '🎵 Jingle en cours...'}
              {phase === 'playing' && (
                <span className="flex items-center gap-2">
                  <Volume2 className="w-4 h-4 animate-pulse" />
                  <span>🔊 Montez le son de votre appareil pour écouter le flash !</span>
                </span>
              )}
              {phase === 'done' && '✓ Flash terminé'}
            </motion.p>
            
            {/* Progress bar */}
            <div className="mt-2 h-1.5 bg-red-950 rounded-full overflow-hidden">
              {phase === 'jingle' ? (
                <motion.div
                  className="h-full bg-yellow-400"
                  animate={{ x: ['-100%', '100%'] }}
                  transition={{ repeat: Infinity, duration: 0.8, ease: 'linear' }}
                  style={{ width: '40%' }}
                />
              ) : (
                <motion.div
                  className="h-full bg-green-400"
                  style={{ width: `${progress}%` }}
                />
              )}
            </div>
          </div>
          
          {/* Stop button */}
          <button
            onClick={() => {
              stopAllAudio();
              onComplete?.();
            }}
            className="p-2 rounded-full bg-red-950 hover:bg-red-900 transition-colors shrink-0"
            title="Arrêter la radio"
          >
            <Square className="w-5 h-5 text-white" />
          </button>
        </div>
      </div>
    </motion.div>
  );
};

// Legacy RadioFlash component - replaced by notification + player system
const RadioFlash = ({ station, content, scenarioTime, onComplete, isVisible, onSaveFlash }) => {
  // This is now just a wrapper that uses the new player directly
  return (
    <RadioFlashActivePlayer
      station={station}
      content={content}
      scenarioTime={scenarioTime}
      onComplete={onComplete}
      isVisible={isVisible}
      onSaveFlash={onSaveFlash}
    />
  );
};

// Incoming Phone Call Component with Speech-to-Text Response
const IncomingCall = ({ caller, callerRole, script, onAnswer, onReject, onTranscript, isVisible }) => {
  const [callState, setCallState] = useState('ringing'); // ringing, connected, responding, transcribing, ended
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [transcript, setTranscript] = useState('');
  const audioRef = useRef(null);
  const ringtoneRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);

  // French phone ringtone sound
  useEffect(() => {
    if (isVisible && callState === 'ringing') {
      // Create French-style ringtone (two-tone pattern)
      const playRingtone = () => {
        try {
          const audioContext = new (window.AudioContext || window.webkitAudioContext)();
          
          const playRingTone = () => {
            // French phone ring: two short tones, then pause
            const osc1 = audioContext.createOscillator();
            const osc2 = audioContext.createOscillator();
            const gain = audioContext.createGain();
            
            osc1.connect(gain);
            osc2.connect(gain);
            gain.connect(audioContext.destination);
            
            // French phone frequencies
            osc1.frequency.value = 440;
            osc2.frequency.value = 480;
            osc1.type = 'sine';
            osc2.type = 'sine';
            gain.gain.value = 0.2;
            
            const now = audioContext.currentTime;
            
            // Ring on for 1.5s
            osc1.start(now);
            osc2.start(now);
            osc1.stop(now + 1.5);
            osc2.stop(now + 1.5);
            
            // Fade out
            gain.gain.setValueAtTime(0.2, now);
            gain.gain.linearRampToValueAtTime(0, now + 1.5);
          };
          
          // Play immediately and repeat every 3.5s
          playRingTone();
          const ringInterval = setInterval(() => {
            if (callState === 'ringing') {
              playRingTone();
            }
          }, 3500);
          
          ringtoneRef.current = { interval: ringInterval, context: audioContext };
        } catch (e) {
          console.log('Audio not supported');
        }
      };
      
      playRingtone();
    }
    
    return () => {
      if (ringtoneRef.current) {
        try {
          clearInterval(ringtoneRef.current.interval);
          ringtoneRef.current.context?.close();
        } catch (e) {}
      }
    };
  }, [isVisible, callState]);

  // Cleanup recording timer on unmount
  useEffect(() => {
    return () => {
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
    };
  }, []);

  const handleAnswer = async () => {
    // Stop ringtone
    if (ringtoneRef.current) {
      try {
        ringtoneRef.current.oscillator?.stop();
        clearInterval(ringtoneRef.current.interval);
      } catch (e) {}
    }
    
    setCallState('connected');
    setAudioPlaying(true);
    
    try {
      // Determine voice based on caller name (gender heuristic)
      // Male names/roles: Colonel, Monsieur, avocat, journaliste homme, etc.
      // Female names/roles: Madame, Marie, directrice, etc.
      const isMaleVoice = 
        caller?.toLowerCase()?.includes('colonel') ||
        caller?.toLowerCase()?.includes('monsieur') ||
        caller?.toLowerCase()?.includes('martin') ||
        caller?.toLowerCase()?.includes('avocat') ||
        callerRole?.toLowerCase()?.includes('colonel') ||
        callerRole?.toLowerCase()?.includes('directeur ') || // Note the space to avoid "directrice"
        callerRole?.toLowerCase()?.includes('journaliste') ||
        callerRole?.toLowerCase()?.includes('inspecteur');
        
      // Generate TTS audio with faster speed for stress
      const response = await axios.post(`${API}/tts/generate`, {
        text: script,
        voice: isMaleVoice ? 'onyx' : 'nova',
        caller_name: caller,
        speed: 1.25 // Faster for stress situation
      });
      
      // Play audio
      const audio = new Audio(`data:audio/mp3;base64,${response.data.audio}`);
      audioRef.current = audio;
      
      audio.onended = () => {
        setAudioPlaying(false);
        // After caller speaks, allow player to respond
        setCallState('responding');
      };
      
      audio.play();
    } catch (error) {
      console.error('TTS error:', error);
      // Fallback: just show the script and allow response
      toast.error("Synthèse vocale indisponible - Lisez le message");
      setAudioPlaying(false);
      setCallState('responding');
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          audioChunksRef.current.push(e.data);
        }
      };
      
      mediaRecorder.onstop = async () => {
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
        
        // Create blob and transcribe
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        await transcribeAudio(audioBlob);
      };
      
      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);
      
      // Start recording timer
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
    } catch (error) {
      console.error('Microphone access error:', error);
      toast.error("Impossible d'accéder au microphone");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setCallState('transcribing');
      
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
    }
  };

  const transcribeAudio = async (audioBlob) => {
    try {
      // Convert blob to base64
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64Audio = reader.result.split(',')[1];
        
        try {
          const response = await axios.post(`${API}/stt/transcribe`, {
            audio_base64: base64Audio,
            audio_format: 'webm',
            language: 'fr'
          });
          
          setTranscript(response.data.transcript);
          
          // Notify parent with transcript
          if (onTranscript) {
            onTranscript(response.data.transcript, { caller, callerRole, script });
          }
          
          // End call after short delay
          setTimeout(() => {
            playEndCallTone();
            setCallState('ended');
            setTimeout(() => onAnswer?.(), 1500);
          }, 2000);
          
        } catch (error) {
          console.error('Transcription error:', error);
          toast.error("Erreur de transcription - Votre réponse a été enregistrée");
          setCallState('ended');
          setTimeout(() => onAnswer?.(), 1000);
        }
      };
      reader.readAsDataURL(audioBlob);
    } catch (error) {
      console.error('Audio processing error:', error);
      setCallState('ended');
      setTimeout(() => onAnswer?.(), 1000);
    }
  };

  const skipResponse = () => {
    // End call without recording
    playEndCallTone();
    setCallState('ended');
    setTimeout(() => onAnswer?.(), 1000);
  };

  const playEndCallTone = () => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 480;
      oscillator.type = 'sine';
      gainNode.gain.value = 0.4;
      
      oscillator.start();
      
      // Beep pattern: 3 short beeps
      let beepCount = 0;
      const beepInterval = setInterval(() => {
        beepCount++;
        if (beepCount >= 6) {
          oscillator.stop();
          clearInterval(beepInterval);
          audioContext.close();
          return;
        }
        gainNode.gain.value = gainNode.gain.value > 0 ? 0 : 0.4;
      }, 250);
    } catch (e) {}
  };

  const handleReject = () => {
    if (ringtoneRef.current) {
      try {
        ringtoneRef.current.oscillator?.stop();
        clearInterval(ringtoneRef.current.interval);
      } catch (e) {}
    }
    onReject?.();
  };

  if (!isVisible) return null;

  // Format recording time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${String(secs).padStart(2, '0')}`;
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] bg-black overflow-y-auto"
    >
      {/* Call UI - Scrollable container */}
      <div className="min-h-full flex flex-col items-center justify-start p-4 sm:p-6 sm:justify-center">
        {/* Caller Avatar - Smaller on mobile/iframe */}
        <motion.div
          animate={callState === 'ringing' ? { scale: [1, 1.1, 1] } : isRecording ? { scale: [1, 1.05, 1] } : {}}
          transition={{ repeat: Infinity, duration: 1.5 }}
          className={`w-20 h-20 sm:w-32 sm:h-32 rounded-full flex items-center justify-center mb-4 sm:mb-6 mt-4 ${
            callState === 'ringing' ? 'bg-primary/20 border-4 border-primary' : 
            callState === 'connected' ? 'bg-green-500/20 border-4 border-green-500' :
            callState === 'responding' ? (isRecording ? 'bg-red-500/20 border-4 border-red-500' : 'bg-amber-500/20 border-4 border-amber-500') :
            callState === 'transcribing' ? 'bg-blue-500/20 border-4 border-blue-500' :
            'bg-destructive/20 border-4 border-destructive'
          }`}
        >
          <Phone className={`w-10 h-10 sm:w-16 sm:h-16 ${
            callState === 'ringing' ? 'text-primary' : 
            callState === 'connected' ? 'text-green-500' :
            callState === 'responding' ? (isRecording ? 'text-red-500' : 'text-amber-500') :
            callState === 'transcribing' ? 'text-blue-500' :
            'text-destructive'
          }`} />
        </motion.div>

        {/* Caller Info - Smaller text */}
        <h2 className="text-xl sm:text-3xl font-bold text-white mb-1 sm:mb-2 text-center">{caller}</h2>
        <p className="text-sm sm:text-lg text-muted-foreground mb-1 sm:mb-2 text-center">{callerRole}</p>
        
        {/* Call Status */}
        <p className={`text-sm sm:text-lg font-mono ${
          callState === 'ringing' ? 'text-primary animate-pulse' : 
          callState === 'connected' ? 'text-green-500' :
          callState === 'responding' ? (isRecording ? 'text-red-500' : 'text-amber-500') :
          callState === 'transcribing' ? 'text-blue-500 animate-pulse' :
          'text-destructive'
        }`}>
          {callState === 'ringing' && '📞 Appel entrant...'}
          {callState === 'connected' && '🔊 En ligne...'}
          {callState === 'responding' && !isRecording && '🎙️ À vous de répondre'}
          {callState === 'responding' && isRecording && `🔴 Enregistrement... ${formatTime(recordingTime)}`}
          {callState === 'transcribing' && '⏳ Transcription en cours...'}
          {callState === 'ended' && '📵 Appel terminé'}
        </p>

        {/* Script display when connected */}
        {(callState === 'connected' || callState === 'responding') && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-4 sm:mt-8 max-w-md p-3 sm:p-4 bg-secondary/30 border border-border rounded mx-4"
          >
            <p className="text-xs text-muted-foreground mb-1 uppercase tracking-wider">Message de l'interlocuteur :</p>
            <p className="text-xs sm:text-sm text-white text-center italic">
              "{script}"
            </p>
          </motion.div>
        )}

        {/* Transcript display */}
        {transcript && callState === 'ended' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-4 max-w-md p-3 sm:p-4 bg-green-500/10 border border-green-500/30 rounded mx-4"
          >
            <p className="text-xs text-green-400 mb-1 uppercase tracking-wider">Votre réponse (transcrite) :</p>
            <p className="text-xs sm:text-sm text-white text-center">
              "{transcript}"
            </p>
          </motion.div>
        )}

        {/* Action Buttons - Now inside the scrollable container */}
        {callState === 'ringing' && (
          <div className="py-6 sm:py-8 flex justify-center gap-6 sm:gap-8">
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleReject}
              className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-destructive flex items-center justify-center"
            >
              <PhoneOff className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
            </motion.button>
            
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleAnswer}
              className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-green-500 flex items-center justify-center"
            >
              <Phone className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
            </motion.button>
          </div>
        )}

        {callState === 'connected' && (
          <div className="py-6 sm:py-8 flex justify-center">
            <div className="flex items-center gap-2 text-green-500">
              <Volume2 className="w-5 h-5 sm:w-6 sm:h-6 animate-pulse" />
              <span className="font-mono text-sm sm:text-base">En communication...</span>
            </div>
          </div>
        )}

        {callState === 'responding' && (
          <div className="py-4 sm:py-8 flex flex-col items-center gap-3 sm:gap-4">
            <p className="text-white/70 text-xs sm:text-sm text-center max-w-md px-4">
              {!isRecording 
                ? "Appuyez sur le micro pour enregistrer votre réponse, ou passez si vous ne souhaitez pas répondre."
                : "Parlez maintenant... Appuyez à nouveau pour arrêter l'enregistrement."
              }
            </p>
            <div className="flex gap-4 sm:gap-6">
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                onClick={isRecording ? stopRecording : startRecording}
                className={`w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center ${
                  isRecording ? 'bg-red-600 animate-pulse' : 'bg-amber-500'
                }`}
              >
                {isRecording ? (
                  <div className="w-6 h-6 sm:w-8 sm:h-8 bg-white rounded-sm" />
                ) : (
                  <Volume2 className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
                )}
              </motion.button>
              
              {!isRecording && (
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={skipResponse}
                  className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-muted flex items-center justify-center"
                >
                  <PhoneOff className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
                </motion.button>
              )}
            </div>
          </div>
        )}

        {callState === 'transcribing' && (
          <div className="py-6 sm:py-8 flex justify-center">
            <div className="flex items-center gap-2 text-blue-500">
              <Loader2 className="w-5 h-5 sm:w-6 sm:h-6 animate-spin" />
              <span className="font-mono text-sm sm:text-base">Transcription de votre réponse...</span>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

// Press Release Editor Component - AI-assisted press release creation
const PressReleaseEditor = ({ isOpen, onClose, onPublish, scenarioContext, scenarioTime }) => {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [distribution, setDistribution] = useState([]); // 'afp', 'national', 'local'
  const [isGenerating, setIsGenerating] = useState(false);
  const [step, setStep] = useState(1); // 1: write, 2: review, 3: distribute

  const distributions = [
    { id: 'afp', label: 'AFP', description: 'Agence France Presse - Diffusion mondiale', icon: '🌍' },
    { id: 'national', label: 'Presse Nationale', description: 'Le Monde, Le Figaro, Libération...', icon: '🇫🇷' },
    { id: 'local', label: 'Presse Locale', description: 'Le Progrès, Dauphiné Libéré...', icon: '📍' }
  ];

  const toggleDistribution = (id) => {
    setDistribution(prev => 
      prev.includes(id) ? prev.filter(d => d !== id) : [...prev, id]
    );
  };

  const generateWithAI = async () => {
    setIsGenerating(true);
    try {
      const response = await axios.post(`${API}/ai/generate-press-release`, {
        context: scenarioContext,
        current_situation: "Incident industriel en cours, situation sous contrôle des équipes de sécurité",
        company_name: "ACME Corporation",
        tone: "professional"
      });
      
      if (response.data.title) setTitle(response.data.title);
      if (response.data.content) setContent(response.data.content);
    } catch (err) {
      // Fallback to template if API fails
      setTitle("COMMUNIQUÉ DE PRESSE - ACME Corporation");
      setContent(`${scenarioTime || "08:00"} - ACME Corporation informe que ses équipes sont actuellement mobilisées suite à un incident survenu sur son site de Lyon.

La direction a immédiatement activé son plan de gestion de crise et travaille en étroite collaboration avec les autorités compétentes.

La sécurité de nos collaborateurs et des riverains est notre priorité absolue. Des mesures de précaution ont été mises en place.

Nous communiquerons de nouvelles informations dès que possible.

Contact presse : communication@acme-corp.fr`);
      toast.info("Mode hors-ligne activé", { description: "Template par défaut utilisé" });
    }
    setIsGenerating(false);
  };

  const handlePublish = () => {
    if (!title || !content) {
      toast.error("Veuillez remplir le titre et le contenu");
      return;
    }
    if (distribution.length === 0) {
      toast.error("Veuillez sélectionner au moins un canal de diffusion");
      return;
    }
    
    onPublish({
      title,
      content,
      distribution,
      timestamp: scenarioTime,
      publishedAt: new Date().toISOString()
    });
    
    // Reset
    setTitle("");
    setContent("");
    setDistribution([]);
    setStep(1);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[160] bg-black/90 flex items-center justify-center p-2 sm:p-4 overflow-y-auto"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.95, y: 20 }}
        className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-primary/10 border-b border-border px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
              <PenLine className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="font-bold text-primary">Communiqué de Presse</h2>
              <p className="text-xs text-muted-foreground">
                Étape {step}/3 - {step === 1 ? 'Rédaction' : step === 2 ? 'Relecture' : 'Diffusion'}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Step 1: Write */}
          {step === 1 && (
            <div className="space-y-4">
              <div className="flex justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={generateWithAI}
                  disabled={isGenerating}
                  className="text-primary border-primary/50"
                >
                  {isGenerating ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Génération...</>
                  ) : (
                    <><Zap className="w-4 h-4 mr-2" /> Générer avec IA</>
                  )}
                </Button>
              </div>

              <div>
                <label className="text-sm font-medium text-foreground block mb-1">Titre</label>
                <Input
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                  placeholder="COMMUNIQUÉ DE PRESSE - [Sujet]"
                  className="bg-background"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-foreground block mb-1">Contenu</label>
                <Textarea
                  value={content}
                  onChange={e => setContent(e.target.value)}
                  placeholder="Rédigez votre communiqué ici..."
                  className="bg-background min-h-[200px] font-mono text-sm"
                />
              </div>

              <div className="flex justify-end">
                <Button onClick={() => setStep(2)} disabled={!title || !content}>
                  Suivant →
                </Button>
              </div>
            </div>
          )}

          {/* Step 2: Review */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="bg-white text-black p-6 rounded border shadow-inner font-serif">
                <h3 className="text-xl font-bold mb-4 text-center">{title}</h3>
                <div className="text-sm whitespace-pre-wrap leading-relaxed">
                  {content}
                </div>
                <div className="mt-6 pt-4 border-t text-xs text-gray-500 text-center">
                  {scenarioTime} - ACME Corporation
                </div>
              </div>

              <div className="flex justify-between">
                <Button variant="outline" onClick={() => setStep(1)}>
                  ← Modifier
                </Button>
                <Button onClick={() => setStep(3)}>
                  Valider et Diffuser →
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Distribution */}
          {step === 3 && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground mb-4">
                Sélectionnez les canaux de diffusion pour votre communiqué :
              </p>

              <div className="space-y-3">
                {distributions.map(dist => (
                  <motion.div
                    key={dist.id}
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.99 }}
                    onClick={() => toggleDistribution(dist.id)}
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      distribution.includes(dist.id)
                        ? 'border-primary bg-primary/10'
                        : 'border-border hover:border-primary/50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{dist.icon}</span>
                      <div className="flex-1">
                        <h4 className="font-bold text-foreground">{dist.label}</h4>
                        <p className="text-xs text-muted-foreground">{dist.description}</p>
                      </div>
                      {distribution.includes(dist.id) && (
                        <Check className="w-5 h-5 text-primary" />
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>

              <div className="flex justify-between mt-6">
                <Button variant="outline" onClick={() => setStep(2)}>
                  ← Retour
                </Button>
                <Button 
                  onClick={handlePublish}
                  disabled={distribution.length === 0}
                  className="bg-green-600 hover:bg-green-500"
                >
                  <Send className="w-4 h-4 mr-2" /> Publier le communiqué
                </Button>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

// Social Post Editor Component - AI-assisted social media post creation
const SocialPostEditor = ({ isOpen, onClose, onPublish, scenarioContext, scenarioTime }) => {
  const [platform, setPlatform] = useState('twitter'); // 'twitter', 'linkedin', 'facebook'
  const [content, setContent] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);

  const platforms = [
    { id: 'twitter', label: 'X', icon: '𝕏', maxLength: 280, color: 'bg-black text-white' },
    { id: 'linkedin', label: 'LinkedIn', icon: 'in', maxLength: 3000, color: 'bg-blue-700 text-white' },
    { id: 'facebook', label: 'Facebook', icon: 'f', maxLength: 63206, color: 'bg-blue-600 text-white' }
  ];

  const selectedPlatform = platforms.find(p => p.id === platform);

  const generateWithAI = async () => {
    setIsGenerating(true);
    try {
      const response = await axios.post(`${API}/ai/generate-social-post`, {
        platform: platform,
        context: scenarioContext,
        company_name: "ACME Corporation",
        tone: platform === 'linkedin' ? 'professional' : 'empathetic'
      });
      
      setContent(response.data.content || "");
    } catch (err) {
      // Fallback templates
      const templates = {
        twitter: `⚠️ [INFO] ACME Corporation - Notre équipe est mobilisée suite à l'incident sur notre site. La sécurité de tous reste notre priorité. Plus d'infos à venir. #ACME #Communication`,
        linkedin: `🔔 Communication importante - ACME Corporation\n\nNous informons l'ensemble de nos partenaires et parties prenantes qu'un incident est survenu sur notre site.\n\nNos équipes sont pleinement mobilisées et travaillent en étroite collaboration avec les autorités compétentes.\n\nLa sécurité de nos collaborateurs et des riverains constitue notre priorité absolue.\n\nNous vous tiendrons informés de l'évolution de la situation.\n\n#GestionDeCrise #ACME`,
        facebook: `📢 Information importante\n\nChers abonnés,\n\nNous souhaitons vous informer qu'un incident s'est produit sur notre site. Nos équipes sont sur place et gèrent la situation.\n\nVotre sécurité et celle de nos collaborateurs sont notre priorité.\n\nNous vous communiquerons plus d'informations dès que possible.\n\nMerci de votre compréhension. 🙏`
      };
      setContent(templates[platform]);
    }
    setIsGenerating(false);
  };

  const handlePublish = () => {
    if (!content) {
      toast.error("Veuillez rédiger un message");
      return;
    }
    
    onPublish({
      platform,
      content,
      timestamp: scenarioTime,
      publishedAt: new Date().toISOString()
    });
    
    toast.success(`Post ${selectedPlatform.label} publié !`);
    setContent("");
    onClose();
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[160] bg-black/90 flex items-center justify-center p-2 sm:p-4 overflow-y-auto"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.95, y: 20 }}
        className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-purple-600/10 border-b border-border px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-purple-600/20 flex items-center justify-center">
              <Globe className="w-5 h-5 text-purple-500" />
            </div>
            <div>
              <h2 className="font-bold text-purple-500">Post Réseaux Sociaux</h2>
              <p className="text-xs text-muted-foreground">Communication de crise</p>
            </div>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Platform selector */}
          <div>
            <label className="text-sm font-medium text-foreground block mb-2">Plateforme</label>
            <div className="flex gap-2">
              {platforms.map(p => (
                <button
                  key={p.id}
                  onClick={() => setPlatform(p.id)}
                  className={`flex-1 py-2 px-3 rounded-lg font-medium text-sm transition-all ${
                    platform === p.id 
                      ? p.color + ' ring-2 ring-offset-2 ring-offset-background ring-primary' 
                      : 'bg-muted text-muted-foreground hover:bg-muted/80'
                  }`}
                >
                  <span className="font-bold mr-1">{p.icon}</span> {p.label}
                </button>
              ))}
            </div>
          </div>

          {/* Generate button */}
          <div className="flex justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={generateWithAI}
              disabled={isGenerating}
              className="text-purple-500 border-purple-500/50"
            >
              {isGenerating ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Génération...</>
              ) : (
                <><Zap className="w-4 h-4 mr-2" /> Générer avec IA</>
              )}
            </Button>
          </div>

          {/* Content */}
          <div>
            <div className="flex justify-between items-center mb-1">
              <label className="text-sm font-medium text-foreground">Message</label>
              <span className={`text-xs ${content.length > selectedPlatform.maxLength ? 'text-red-500' : 'text-muted-foreground'}`}>
                {content.length}/{selectedPlatform.maxLength}
              </span>
            </div>
            <Textarea
              value={content}
              onChange={e => setContent(e.target.value)}
              placeholder={`Rédigez votre message ${selectedPlatform.label}...`}
              className="bg-background min-h-[150px]"
              maxLength={selectedPlatform.maxLength + 100}
            />
          </div>

          {/* Preview */}
          {content && (
            <div className={`p-4 rounded-lg ${selectedPlatform.color} text-sm`}>
              <div className="flex items-center gap-2 mb-2 opacity-80">
                <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center font-bold">
                  A
                </div>
                <div>
                  <div className="font-bold">ACME Corporation</div>
                  <div className="text-xs opacity-70">@acme_corp • {scenarioTime}</div>
                </div>
              </div>
              <div className="whitespace-pre-wrap">{content}</div>
            </div>
          )}

          {/* Publish button */}
          <Button 
            onClick={handlePublish}
            disabled={!content || content.length > selectedPlatform.maxLength}
            className="w-full bg-purple-600 hover:bg-purple-500"
          >
            <Send className="w-4 h-4 mr-2" /> Publier sur {selectedPlatform.label}
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ==========================================
// OBSERVER DASHBOARD - Large Screen Optimized
// Resolution: 1920x1080 minimum, designed for TV/Monitor display
// ==========================================

// Animated Gauge Component
const AnimatedGauge = ({ value, max, label, color = "primary", size = "md", showPercentage = true }) => {
  const percentage = Math.min((value / max) * 100, 100);
  const circumference = 2 * Math.PI * 45;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;
  
  const sizeClasses = {
    sm: "w-24 h-24",
    md: "w-32 h-32",
    lg: "w-40 h-40",
    xl: "w-48 h-48"
  };
  
  const colorClasses = {
    primary: "text-primary stroke-primary",
    green: "text-green-500 stroke-green-500",
    yellow: "text-yellow-500 stroke-yellow-500",
    red: "text-red-500 stroke-red-500",
    blue: "text-blue-500 stroke-blue-500",
    purple: "text-purple-500 stroke-purple-500"
  };

  return (
    <div className={`relative ${sizeClasses[size]} flex items-center justify-center`}>
      <svg className="transform -rotate-90 w-full h-full">
        <circle
          cx="50%"
          cy="50%"
          r="45%"
          fill="none"
          stroke="currentColor"
          strokeWidth="8"
          className="text-muted/20"
        />
        <motion.circle
          cx="50%"
          cy="50%"
          r="45%"
          fill="none"
          strokeWidth="8"
          strokeLinecap="round"
          className={colorClasses[color]}
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset }}
          transition={{ duration: 1, ease: "easeOut" }}
          style={{ strokeDasharray: circumference }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <motion.span
          key={value}
          initial={{ scale: 1.2, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className={`text-2xl font-bold ${colorClasses[color].split(' ')[0]}`}
        >
          {showPercentage ? `${Math.round(percentage)}%` : value}
        </motion.span>
        <span className="text-xs text-muted-foreground mt-1">{label}</span>
      </div>
    </div>
  );
};

// Counter Card Component
const CounterCard = ({ icon: Icon, value, label, sublabel, trend, color = "primary" }) => {
  const colorClasses = {
    primary: "border-primary/50 bg-primary/5",
    green: "border-green-500/50 bg-green-500/5",
    yellow: "border-yellow-500/50 bg-yellow-500/5",
    red: "border-red-500/50 bg-red-500/5",
    blue: "border-blue-500/50 bg-blue-500/5",
    purple: "border-purple-500/50 bg-purple-500/5"
  };
  
  const iconColors = {
    primary: "text-primary",
    green: "text-green-500",
    yellow: "text-yellow-500",
    red: "text-red-500",
    blue: "text-blue-500",
    purple: "text-purple-500"
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`p-4 rounded-xl border-2 ${colorClasses[color]}`}
    >
      <div className="flex items-start justify-between">
        <div>
          <motion.p
            key={value}
            initial={{ scale: 1.1 }}
            animate={{ scale: 1 }}
            className="text-4xl font-bold text-foreground"
          >
            {value}
          </motion.p>
          <p className="text-sm font-medium text-foreground mt-1">{label}</p>
          {sublabel && <p className="text-xs text-muted-foreground">{sublabel}</p>}
        </div>
        <div className={`p-3 rounded-lg bg-background/50 ${iconColors[color]}`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
      {trend !== undefined && (
        <div className={`mt-2 text-xs ${trend >= 0 ? 'text-green-500' : 'text-red-500'}`}>
          {trend >= 0 ? '↑' : '↓'} {Math.abs(trend)}% depuis le début
        </div>
      )}
    </motion.div>
  );
};

// Live Activity Feed Item
const ActivityFeedItem = ({ action, isNew }) => {
  const typeIcons = {
    press_release: PenLine,
    social_response: MessageCircle,
    email_response: Mail,
    phone_call: Phone,
    transfer: Share2,
    radio_listened: Radio,
    message_read: Eye
  };
  
  const typeColors = {
    press_release: "text-blue-500 bg-blue-500/10",
    social_response: "text-purple-500 bg-purple-500/10",
    email_response: "text-yellow-500 bg-yellow-500/10",
    phone_call: "text-green-500 bg-green-500/10",
    transfer: "text-orange-500 bg-orange-500/10",
    radio_listened: "text-red-500 bg-red-500/10",
    message_read: "text-gray-500 bg-gray-500/10"
  };
  
  const Icon = typeIcons[action.type] || Activity;
  const colorClass = typeColors[action.type] || "text-primary bg-primary/10";

  return (
    <motion.div
      initial={{ opacity: 0, x: -20, height: 0 }}
      animate={{ opacity: 1, x: 0, height: "auto" }}
      className={`flex items-center gap-3 p-3 rounded-lg border ${isNew ? 'border-primary bg-primary/5' : 'border-border'}`}
    >
      <div className={`p-2 rounded-lg ${colorClass}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground truncate">{action.title || action.type}</p>
        <p className="text-xs text-muted-foreground">{action.actor} • {action.scenarioTime}</p>
      </div>
      {isNew && (
        <Badge className="bg-primary text-primary-foreground text-xs">NEW</Badge>
      )}
    </motion.div>
  );
};

// Reactivity Timeline Chart
const ReactivityChart = ({ data }) => {
  const maxValue = Math.max(...data.map(d => d.value), 1);
  
  return (
    <div className="h-32 flex items-end gap-1">
      {data.map((point, i) => (
        <motion.div
          key={i}
          initial={{ height: 0 }}
          animate={{ height: `${(point.value / maxValue) * 100}%` }}
          transition={{ delay: i * 0.05 }}
          className={`flex-1 rounded-t ${
            point.value > 70 ? 'bg-green-500' :
            point.value > 40 ? 'bg-yellow-500' :
            point.value > 20 ? 'bg-orange-500' : 'bg-red-500'
          }`}
          title={`${point.time}: ${point.value}%`}
        />
      ))}
    </div>
  );
};

// Main Observer Dashboard Component
export const ObserverDashboard = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [scenario, setScenario] = useState(null);
  const [scenarioTime, setScenarioTime] = useState("08:00");
  const [isConnected, setIsConnected] = useState(true);
  
  // Stats
  const [stats, setStats] = useState({
    totalInputs: 0,
    pressArticles: 0,
    socialPosts: 0,
    emails: 0,
    phoneCalls: 0,
    radioFlashes: 0,
    // Responses
    pressReleases: 0,
    socialResponses: 0,
    emailResponses: 0,
    callsAnswered: 0,
    callsRejected: 0,
    messagesRead: 0,
    // Rates
    emailResponseRate: 0,
    socialResponseRate: 0,
    callAnswerRate: 0,
    overallReactivity: 0
  });
  
  // Activity feed
  const [activities, setActivities] = useState([]);
  
  // Reactivity over time (for chart)
  const [reactivityHistory, setReactivityHistory] = useState([]);
  
  // Team members status
  const [teamStatus, setTeamStatus] = useState([
    { role: "DG", name: "Jean Dupont", status: "active", lastAction: "Lecture message", time: "08:05" },
    { role: "DirCom", name: "Marie Martin", status: "active", lastAction: "Communiqué AFP", time: "08:12" },
    { role: "DRH", name: "Pierre Durand", status: "idle", lastAction: "Connexion", time: "08:00" },
    { role: "DirOps", name: "Sophie Bernard", status: "active", lastAction: "Appel répondu", time: "08:08" },
    { role: "DirJuridique", name: "Luc Petit", status: "offline", lastAction: "-", time: "-" }
  ]);

  // Simulate real-time updates (in production, this would be WebSocket)
  useEffect(() => {
    // Initial load
    const loadData = async () => {
      try {
        // Load session info
        const sessRes = await axios.get(`${API}/sessions`);
        const sess = sessRes.data.sessions?.find(s => s.id === sessionId);
        if (sess) setSession(sess);
        
        // Load scenario
        const scenRes = await axios.get(`${API}/scenarios`);
        const scen = scenRes.data.scenarios?.find(s => s.name === "Incendie Industriel");
        if (scen) setScenario(scen);
      } catch (err) {
        console.error("Load error:", err);
      }
    };
    loadData();

    // Simulate real-time updates every 3 seconds
    const interval = setInterval(() => {
      // Update scenario time
      setScenarioTime(prev => {
        const [h, m] = prev.split(':').map(Number);
        const newM = m + 1;
        if (newM >= 60) return `${String(h + 1).padStart(2, '0')}:00`;
        return `${String(h).padStart(2, '0')}:${String(newM).padStart(2, '0')}`;
      });
      
      // Simulate stats updates
      setStats(prev => {
        const newInputs = prev.totalInputs + Math.floor(Math.random() * 3);
        const newSocial = prev.socialPosts + Math.floor(Math.random() * 2);
        const newEmails = prev.emails + (Math.random() > 0.7 ? 1 : 0);
        const newCalls = prev.phoneCalls + (Math.random() > 0.9 ? 1 : 0);
        
        // Calculate rates
        const emailRate = newEmails > 0 ? Math.round((prev.emailResponses / newEmails) * 100) : 0;
        const socialRate = newSocial > 0 ? Math.round((prev.socialResponses / newSocial) * 100) : 0;
        const callRate = newCalls > 0 ? Math.round((prev.callsAnswered / newCalls) * 100) : 0;
        
        return {
          ...prev,
          totalInputs: newInputs,
          socialPosts: newSocial,
          emails: newEmails,
          phoneCalls: newCalls,
          pressArticles: prev.pressArticles + (Math.random() > 0.8 ? 1 : 0),
          radioFlashes: prev.radioFlashes + (Math.random() > 0.95 ? 1 : 0),
          emailResponseRate: emailRate,
          socialResponseRate: socialRate,
          callAnswerRate: callRate,
          overallReactivity: Math.round((emailRate + socialRate + callRate) / 3)
        };
      });
      
      // Update reactivity history
      setReactivityHistory(prev => {
        const newPoint = { 
          time: scenarioTime, 
          value: Math.floor(Math.random() * 40) + 30 + (prev.length * 2) // Gradual increase
        };
        const updated = [...prev, newPoint].slice(-20); // Keep last 20 points
        return updated;
      });
      
      // Occasionally add activity
      if (Math.random() > 0.6) {
        const actionTypes = ['social_response', 'email_response', 'message_read', 'phone_call'];
        const actors = ['DirCom', 'DG', 'DirOps', 'DRH'];
        const newActivity = {
          id: Date.now(),
          type: actionTypes[Math.floor(Math.random() * actionTypes.length)],
          title: "Action effectuée",
          actor: actors[Math.floor(Math.random() * actors.length)],
          scenarioTime,
          timestamp: new Date().toISOString()
        };
        setActivities(prev => [newActivity, ...prev].slice(0, 15));
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [sessionId]);

  // For demo, add initial activities
  useEffect(() => {
    setActivities([
      { id: 1, type: 'press_release', title: 'Communiqué AFP envoyé', actor: 'DirCom', scenarioTime: '08:12' },
      { id: 2, type: 'phone_call', title: 'Appel Colonel SDIS', actor: 'DG', scenarioTime: '08:10' },
      { id: 3, type: 'social_response', title: 'Réponse Twitter', actor: 'DirCom', scenarioTime: '08:08' },
      { id: 4, type: 'email_response', title: 'Email Préfecture', actor: 'DirOps', scenarioTime: '08:06' },
      { id: 5, type: 'radio_listened', title: 'Flash France Info écouté', actor: 'DG', scenarioTime: '08:02' }
    ]);
    
    setReactivityHistory([
      { time: '08:00', value: 10 },
      { time: '08:02', value: 15 },
      { time: '08:04', value: 25 },
      { time: '08:06', value: 35 },
      { time: '08:08', value: 45 },
      { time: '08:10', value: 55 },
      { time: '08:12', value: 60 }
    ]);
    
    setStats({
      totalInputs: 47,
      pressArticles: 8,
      socialPosts: 28,
      emails: 6,
      phoneCalls: 3,
      radioFlashes: 2,
      pressReleases: 1,
      socialResponses: 5,
      emailResponses: 2,
      callsAnswered: 2,
      callsRejected: 1,
      messagesRead: 35,
      emailResponseRate: 33,
      socialResponseRate: 18,
      callAnswerRate: 67,
      overallReactivity: 39
    });
  }, []);

  return (
    <div className="min-h-screen bg-black text-white p-4 overflow-hidden">
      {/* Top Header Bar */}
      <div className="flex items-center justify-between mb-4 border-b border-primary/30 pb-4">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Eye className="w-8 h-8 text-primary" />
            <div>
              <h1 className="text-2xl font-bold text-primary">MODE OBSERVATEUR</h1>
              <p className="text-xs text-muted-foreground">Dashboard temps réel • Lecture seule</p>
            </div>
          </div>
          <Badge className="bg-green-600 text-white px-3 py-1 text-sm">
            ● EN DIRECT
          </Badge>
        </div>
        
        <div className="flex items-center gap-6">
          <div className="text-center">
            <p className="text-xs text-muted-foreground">HEURE SCÉNARIO</p>
            <p className="text-4xl font-mono text-primary font-bold">{scenarioTime}</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-muted-foreground">SESSION</p>
            <p className="text-sm font-mono text-foreground">{session?.company_name || "ACME Corp"}</p>
          </div>
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin')}>
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Main Grid - Optimized for 1920x1080 */}
      <div className="grid grid-cols-12 gap-4 h-[calc(100vh-120px)]">
        
        {/* Left Column - Input Counters */}
        <div className="col-span-2 space-y-3">
          <div className="bg-card/50 rounded-xl p-3 border border-border">
            <h3 className="text-xs font-bold text-primary mb-3 flex items-center gap-2">
              <Target className="w-4 h-4" /> INPUTS REÇUS
            </h3>
            <div className="space-y-2">
              <CounterCard icon={Newspaper} value={stats.pressArticles} label="Articles Presse" color="blue" />
              <CounterCard icon={Globe} value={stats.socialPosts} label="Posts Réseaux" color="purple" />
              <CounterCard icon={Mail} value={stats.emails} label="Emails" color="yellow" />
              <CounterCard icon={Phone} value={stats.phoneCalls} label="Appels" color="green" />
              <CounterCard icon={Radio} value={stats.radioFlashes} label="Flashs Radio" color="red" />
            </div>
          </div>
          
          <div className="bg-card/50 rounded-xl p-3 border border-primary/50">
            <div className="text-center">
              <p className="text-6xl font-bold text-primary">{stats.totalInputs}</p>
              <p className="text-sm text-muted-foreground">TOTAL INPUTS</p>
            </div>
          </div>
        </div>

        {/* Center - Main Dashboard */}
        <div className="col-span-7 space-y-4">
          {/* Gauges Row */}
          <div className="bg-card/50 rounded-xl p-4 border border-border">
            <h3 className="text-sm font-bold text-primary mb-4 flex items-center gap-2">
              <Activity className="w-4 h-4" /> TAUX DE RÉPONSE EN TEMPS RÉEL
            </h3>
            <div className="flex items-center justify-around">
              <div className="text-center">
                <AnimatedGauge 
                  value={stats.emailResponses} 
                  max={Math.max(stats.emails, 1)} 
                  label="Emails" 
                  color={stats.emailResponseRate > 50 ? "green" : stats.emailResponseRate > 25 ? "yellow" : "red"}
                  size="lg"
                />
                <p className="text-xs text-muted-foreground mt-2">
                  {stats.emailResponses}/{stats.emails} traités
                </p>
              </div>
              <div className="text-center">
                <AnimatedGauge 
                  value={stats.socialResponses} 
                  max={Math.max(stats.socialPosts, 1)} 
                  label="Réseaux" 
                  color={stats.socialResponseRate > 30 ? "green" : stats.socialResponseRate > 15 ? "yellow" : "red"}
                  size="lg"
                />
                <p className="text-xs text-muted-foreground mt-2">
                  {stats.socialResponses}/{stats.socialPosts} traités
                </p>
              </div>
              <div className="text-center">
                <AnimatedGauge 
                  value={stats.callsAnswered} 
                  max={Math.max(stats.phoneCalls, 1)} 
                  label="Appels" 
                  color={stats.callAnswerRate > 60 ? "green" : stats.callAnswerRate > 30 ? "yellow" : "red"}
                  size="lg"
                />
                <p className="text-xs text-muted-foreground mt-2">
                  {stats.callsAnswered}/{stats.phoneCalls} répondus
                </p>
              </div>
              <div className="text-center">
                <AnimatedGauge 
                  value={stats.pressReleases} 
                  max={Math.max(stats.pressArticles, 1)} 
                  label="Communiqués" 
                  color={stats.pressReleases > 0 ? "green" : "red"}
                  size="lg"
                />
                <p className="text-xs text-muted-foreground mt-2">
                  {stats.pressReleases} communiqué(s)
                </p>
              </div>
            </div>
          </div>

          {/* Reactivity Index */}
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-2 bg-card/50 rounded-xl p-4 border border-border">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold text-primary flex items-center gap-2">
                  <Zap className="w-4 h-4" /> INDICE DE RÉACTIVITÉ
                </h3>
                <span className="text-xs text-muted-foreground">Évolution temporelle</span>
              </div>
              <ReactivityChart data={reactivityHistory} />
              <div className="flex justify-between mt-2 text-xs text-muted-foreground">
                <span>08:00</span>
                <span>Temps →</span>
                <span>{scenarioTime}</span>
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-primary/20 to-primary/5 rounded-xl p-4 border border-primary/50 flex flex-col items-center justify-center">
              <p className="text-xs text-muted-foreground mb-2">RÉACTIVITÉ GLOBALE</p>
              <motion.p
                key={stats.overallReactivity}
                initial={{ scale: 1.2 }}
                animate={{ scale: 1 }}
                className={`text-6xl font-bold ${
                  stats.overallReactivity > 60 ? 'text-green-500' :
                  stats.overallReactivity > 30 ? 'text-yellow-500' : 'text-red-500'
                }`}
              >
                {stats.overallReactivity}%
              </motion.p>
              <p className={`text-sm mt-2 ${
                stats.overallReactivity > 60 ? 'text-green-400' :
                stats.overallReactivity > 30 ? 'text-yellow-400' : 'text-red-400'
              }`}>
                {stats.overallReactivity > 60 ? '✓ Bonne réactivité' :
                 stats.overallReactivity > 30 ? '⚠ Réactivité moyenne' : '⚠ Réactivité faible'}
              </p>
            </div>
          </div>

          {/* Team Status */}
          <div className="bg-card/50 rounded-xl p-4 border border-border">
            <h3 className="text-sm font-bold text-primary mb-3 flex items-center gap-2">
              <Users className="w-4 h-4" /> STATUT ÉQUIPE COMEX
            </h3>
            <div className="grid grid-cols-5 gap-3">
              {teamStatus.map((member, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: i * 0.1 }}
                  className={`p-3 rounded-lg border ${
                    member.status === 'active' ? 'border-green-500/50 bg-green-500/5' :
                    member.status === 'idle' ? 'border-yellow-500/50 bg-yellow-500/5' :
                    'border-gray-500/50 bg-gray-500/5'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`w-2 h-2 rounded-full ${
                      member.status === 'active' ? 'bg-green-500 animate-pulse' :
                      member.status === 'idle' ? 'bg-yellow-500' : 'bg-gray-500'
                    }`} />
                    <span className="font-bold text-sm">{member.role}</span>
                  </div>
                  <p className="text-xs text-muted-foreground truncate">{member.name}</p>
                  <p className="text-xs text-foreground mt-1 truncate">{member.lastAction}</p>
                  <p className="text-xs text-primary font-mono">{member.time}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        {/* Right Column - Response Counters & Activity Feed */}
        <div className="col-span-3 space-y-3">
          {/* Response Counters */}
          <div className="bg-card/50 rounded-xl p-3 border border-border">
            <h3 className="text-xs font-bold text-primary mb-3 flex items-center gap-2">
              <Send className="w-4 h-4" /> RÉPONSES ÉQUIPE
            </h3>
            <div className="grid grid-cols-2 gap-2">
              <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/30 text-center">
                <p className="text-3xl font-bold text-blue-400">{stats.pressReleases}</p>
                <p className="text-xs text-blue-300">Communiqués</p>
              </div>
              <div className="p-3 rounded-lg bg-purple-500/10 border border-purple-500/30 text-center">
                <p className="text-3xl font-bold text-purple-400">{stats.socialResponses}</p>
                <p className="text-xs text-purple-300">Rép. Réseaux</p>
              </div>
              <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30 text-center">
                <p className="text-3xl font-bold text-yellow-400">{stats.emailResponses}</p>
                <p className="text-xs text-yellow-300">Rép. Emails</p>
              </div>
              <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/30 text-center">
                <p className="text-3xl font-bold text-green-400">{stats.callsAnswered}</p>
                <p className="text-xs text-green-300">Appels OK</p>
              </div>
            </div>
          </div>

          {/* Live Activity Feed */}
          <div className="bg-card/50 rounded-xl p-3 border border-border flex-1 overflow-hidden">
            <h3 className="text-xs font-bold text-primary mb-3 flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Activity className="w-4 h-4" /> ACTIONS EN DIRECT
              </span>
              <span className="text-muted-foreground font-normal">{activities.length} actions</span>
            </h3>
            <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2">
              <AnimatePresence>
                {activities.map((activity, i) => (
                  <ActivityFeedItem 
                    key={activity.id} 
                    action={activity} 
                    isNew={i === 0}
                  />
                ))}
              </AnimatePresence>
            </div>
          </div>

          {/* Key Metrics Summary */}
          <div className="bg-gradient-to-br from-card to-card/50 rounded-xl p-3 border border-primary/30">
            <h3 className="text-xs font-bold text-primary mb-2">📊 RÉSUMÉ</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Messages lus</span>
                <span className="font-bold text-foreground">{stats.messagesRead}/{stats.totalInputs}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Appels refusés</span>
                <span className="font-bold text-red-400">{stats.callsRejected}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Temps de simulation</span>
                <span className="font-bold text-primary">{scenarioTime}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Demo Multi-View Page - Shows all 4 dashboards in a 2x2 grid
export const DemoMultiView = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [sessionId, setSessionId] = useState(searchParams.get('session'));
  const [isCreatingSession, setIsCreatingSession] = useState(false);
  const BASE_URL = window.location.origin;

  // Auto-create session if none provided
  useEffect(() => {
    const createSession = async () => {
      if (!sessionId && !isCreatingSession) {
        setIsCreatingSession(true);
        try {
          const res = await axios.post(`${API}/demo/session/create`, {
            scenario_id: 'default',
            scenario_name: 'Demo Multi-View'
          });
          const newSessionId = res.data.session_id;
          setSessionId(newSessionId);
          // Update URL with session parameter
          setSearchParams({ session: newSessionId });
          toast.success(`Session synchronisée créée: ${newSessionId}`);
        } catch (err) {
          console.error('Failed to create session:', err);
          toast.error("Mode non-synchronisé (impossible de créer la session)");
        }
        setIsCreatingSession(false);
      }
    };
    createSession();
  }, [sessionId, isCreatingSession, setSearchParams]);

  // Build URLs with session parameter
  const buildUrl = (path) => {
    return sessionId ? `${path}?session=${sessionId}` : path;
  };

  if (isCreatingSession) {
    return (
      <div className="h-screen w-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-white">Création de la session synchronisée...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen w-screen bg-black overflow-hidden">
      {/* Control bar */}
      <div className="h-10 bg-card border-b border-border flex items-center justify-between px-4">
        <div className="flex items-center gap-4">
          <span className="text-primary font-bold text-sm">🖥️ MULTI-VIEW</span>
          {sessionId ? (
            <Badge className="bg-green-600 text-white">
              🔗 Synchronisé: {sessionId}
            </Badge>
          ) : (
            <Badge className="bg-amber-600 text-white">
              ⚠️ Non synchronisé
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button 
            size="sm" 
            variant="outline"
            onClick={() => {
              if (document.fullscreenElement) {
                document.exitFullscreen();
              } else {
                document.documentElement.requestFullscreen();
              }
            }}
          >
            Plein écran
          </Button>
          <Button 
            size="sm" 
            variant="ghost" 
            onClick={() => navigate('/demo')}
            className="text-muted-foreground hover:text-destructive"
          >
            <X className="w-4 h-4" />
            <span className="hidden sm:inline ml-1">Quitter</span>
          </Button>
        </div>
      </div>
      
      {/* Info banner for synchronization */}
      {sessionId && (
        <div className="bg-green-900/50 border-b border-green-600 px-4 py-1 text-center">
          <p className="text-xs text-green-300">
            ✅ Cliquez sur <strong>DÉMARRER</strong> dans le panneau <strong>ADMIN</strong> (en bas à gauche) pour lancer la démo synchronisée
          </p>
        </div>
      )}
      
      {/* 2x2 Grid of iframes - Labels removed as each dashboard already shows its type */}
      <div className="grid grid-cols-2 grid-rows-2" style={{ height: sessionId ? 'calc(100vh - 64px)' : 'calc(100vh - 40px)' }}>
        {/* Top Left - Joueur */}
        <div className="border-r border-b border-gray-700 relative">
          <iframe 
            src={buildUrl('/demo/client')}
            className="w-full h-full border-0"
            title="Dashboard Joueur"
          />
        </div>
        
        {/* Top Right - Observateur */}
        <div className="border-b border-gray-700 relative">
          <iframe 
            src={buildUrl('/demo/observer')}
            className="w-full h-full border-0"
            title="Dashboard Observateur"
          />
        </div>
        
        {/* Bottom Left - Admin */}
        <div className="border-r border-gray-700 relative">
          <iframe 
            src={buildUrl('/demo/admin')}
            className="w-full h-full border-0"
            title="Dashboard Admin"
          />
        </div>
        
        {/* Bottom Right - Animateur */}
        <div className="relative">
          <iframe 
            src={buildUrl('/animator/demo')}
            className="w-full h-full border-0"
            title="Dashboard Animateur"
          />
        </div>
      </div>
    </div>
  );
};

// Demo Mode Selection Page
export const DemoSelection = () => {
  const navigate = useNavigate();
  const BASE_URL = window.location.origin;
  const [scenarios, setScenarios] = useState([]);
  const [selectedScenario, setSelectedScenario] = useState(null);
  const [demoSessionId, setDemoSessionId] = useState(null);
  const [joinCode, setJoinCode] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  // Load scenarios
  useEffect(() => {
    const loadScenarios = async () => {
      try {
        const res = await axios.get(`${API}/scenarios`);
        setScenarios(res.data.scenarios || []);
        // Select first scenario by default
        if (res.data.scenarios?.length > 0) {
          setSelectedScenario(res.data.scenarios[0]);
        }
      } catch (err) {
        console.error(err);
      }
    };
    loadScenarios();
  }, []);

  // Create synchronized demo session
  const createSyncSession = async () => {
    if (!selectedScenario) {
      toast.error("Sélectionnez un scénario");
      return;
    }
    setIsCreating(true);
    try {
      const res = await axios.post(`${API}/demo/session/create`, {
        scenario_id: selectedScenario.id,
        scenario_name: selectedScenario.name
      });
      setDemoSessionId(res.data.session_id);
      toast.success(`Session créée : ${res.data.session_id}`);
    } catch (err) {
      toast.error("Erreur lors de la création de la session");
    }
    setIsCreating(false);
  };

  // Join existing demo session
  const joinSession = () => {
    if (!joinCode.trim()) {
      toast.error("Entrez un code de session");
      return;
    }
    setDemoSessionId(joinCode.toUpperCase());
    toast.success(`Rejoindre la session : ${joinCode.toUpperCase()}`);
  };

  // Launch multi-view page (single page with 4 dashboards in grid)
  const launchMultiScreenDemo = () => {
    const sessionParam = demoSessionId ? `?session=${demoSessionId}` : '';
    // Navigate to multi-view page
    navigate(`/demo/multi${sessionParam}`);
  };
  
  // Open 4 separate windows (for multiple physical screens)
  const launch4Windows = () => {
    const sessionParam = demoSessionId ? `?session=${demoSessionId}` : '';
    
    const windows = [
      { url: `/demo/client${sessionParam}`, name: 'Joueur' },
      { url: `/demo/observer${sessionParam}`, name: 'Observateur' },
      { url: `/demo/admin${sessionParam}`, name: 'Admin' },
      { url: `/animator/demo${sessionParam}`, name: 'Animateur' }
    ];
    
    windows.forEach(w => {
      window.open(`${BASE_URL}${w.url}`, `CrisisDemo_${w.name}`);
    });
    
    toast.success("4 fenêtres ouvertes ! Déplacez-les manuellement sur vos écrans.");
  };

  return (
    <div className="min-h-screen matrix-bg flex items-center justify-center p-4">
      <ScanlineOverlay />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-4xl w-full"
      >
        <div className="text-center mb-6">
          <img 
            src={EGERIE_IMAGE} 
            alt="Crisis Simulator AI" 
            className="w-20 h-20 mx-auto object-cover rounded-full border-2 border-primary glow-green mb-3"
            style={{ objectPosition: 'center 20%' }}
          />
          <h1 className="text-2xl font-bold heading text-primary">Mode Démo</h1>
          <p className="font-mono text-xs text-muted-foreground mt-1">
            Découvrez Crisis Simulator AI
          </p>
        </div>

        {/* Scenario Selector + Session Creation */}
        <div className="crisis-panel p-4 mb-4">
          <h2 className="text-lg font-bold heading text-primary mb-3 flex items-center gap-2">
            📋 Configuration de la démo
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Scenario Selection */}
            <div className="space-y-3">
              <label className="text-sm font-bold text-muted-foreground">1. Choisir le scénario</label>
              <select 
                className="w-full bg-secondary border border-border rounded-lg p-3 text-sm"
                value={selectedScenario?.id || ''}
                onChange={(e) => {
                  const s = scenarios.find(sc => sc.id === e.target.value);
                  setSelectedScenario(s);
                }}
              >
                {scenarios.map(s => (
                  <option key={s.id} value={s.id}>
                    {s.name} ({s.events?.length || 0} événements)
                  </option>
                ))}
              </select>
              {selectedScenario && (
                <p className="text-xs text-muted-foreground">
                  {selectedScenario.description?.substring(0, 100) || "Scénario de simulation de crise"}...
                </p>
              )}
            </div>

            {/* Scenario Selection Only - Session is automatic now */}
            <div className="space-y-3">
              <label className="text-sm font-bold text-muted-foreground">Scénario sélectionné</label>
              {scenarios.length === 0 ? (
                <p className="text-xs text-muted-foreground">Chargement des scénarios...</p>
              ) : (
                <p className="text-sm text-primary font-semibold">
                  {selectedScenario?.title || selectedScenario?.name || "Sélectionnez un scénario"}
                </p>
              )}
              <p className="text-xs text-muted-foreground">
                💡 La synchronisation est automatique pour la démo multi-écrans
              </p>
            </div>
          </div>
        </div>

        {/* Multi-screen Demo Banner - SIMPLIFIED */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2 }}
          className="crisis-panel p-4 mb-4 bg-gradient-to-r from-green-900/30 to-blue-900/30 border-green-500/50"
        >
          <div className="flex flex-col md:flex-row items-center gap-4">
            <div className="flex-shrink-0">
              <div className="w-16 h-16 bg-green-500/20 rounded-xl flex items-center justify-center">
                <div className="grid grid-cols-2 gap-1">
                  <div className="w-5 h-5 bg-primary rounded"></div>
                  <div className="w-5 h-5 bg-blue-500 rounded"></div>
                  <div className="w-5 h-5 bg-destructive rounded"></div>
                  <div className="w-5 h-5 bg-amber-500 rounded"></div>
                </div>
              </div>
            </div>
            <div className="flex-1 text-center md:text-left">
              <h2 className="text-lg font-bold heading text-green-400 mb-1">
                🖥️ DÉMO MULTI-ÉCRANS SYNCHRONISÉE
              </h2>
              <p className="text-xs text-muted-foreground mb-2">
                Affiche les 4 dashboards dans une seule fenêtre, tous synchronisés.
                <br />
                <span className="text-green-400">Cliquez sur DÉMARRER dans le panneau Animateur pour lancer.</span>
              </p>
              <div className="flex flex-wrap gap-1 justify-center md:justify-start text-xs">
                <Badge className="bg-primary/20 text-primary">Joueur</Badge>
                <Badge className="bg-blue-500/20 text-blue-400">Observateur</Badge>
                <Badge className="bg-destructive/20 text-destructive">Admin</Badge>
                <Badge className="bg-amber-500/20 text-amber-400">Animateur</Badge>
              </div>
            </div>
            <Button 
              onClick={() => navigate(`/demo/multi?scenarioId=${selectedScenario?.id || 'default'}`)}
              className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 text-lg"
            >
              <Play className="w-5 h-5 mr-2" /> Lancer la Démo
            </Button>
          </div>
        </motion.div>

        {/* Individual Demo Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {/* Demo Client (Joueur) */}
          <motion.div
            whileHover={{ scale: 1.02 }}
            className="crisis-panel p-4 cursor-pointer hover:border-primary/50 transition-all"
            onClick={() => navigate("/demo/client")}
            data-testid="demo-client-btn"
          >
            <div className="text-center">
              <div className="w-12 h-12 mx-auto bg-primary/20 rounded-full flex items-center justify-center mb-3">
                <Eye className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-lg font-bold heading text-primary mb-2">Démo Joueur</h3>
              <p className="text-xs text-muted-foreground mb-3">
                Participant COMEX face à une crise
              </p>
              <Button size="sm" className="w-full bg-primary text-primary-foreground">
                <Play className="w-3 h-3 mr-1" /> Lancer
              </Button>
            </div>
          </motion.div>

          {/* Demo Observer */}
          <motion.div
            whileHover={{ scale: 1.02 }}
            className="crisis-panel p-4 cursor-pointer hover:border-blue-500/50 transition-all"
            onClick={() => navigate("/demo/observer")}
            data-testid="demo-observer-btn"
          >
            <div className="text-center">
              <div className="w-12 h-12 mx-auto bg-blue-500/20 rounded-full flex items-center justify-center mb-3">
                <Activity className="w-6 h-6 text-blue-400" />
              </div>
              <h3 className="text-lg font-bold heading text-blue-400 mb-2">Démo Observateur</h3>
              <p className="text-xs text-muted-foreground mb-3">
                Dashboard DRH/CEO lecture seule
              </p>
              <Button size="sm" className="w-full bg-blue-600 hover:bg-blue-700 text-white">
                <Play className="w-3 h-3 mr-1" /> Lancer
              </Button>
            </div>
          </motion.div>

          {/* Demo Admin */}
          <motion.div
            whileHover={{ scale: 1.02 }}
            className="crisis-panel p-4 cursor-pointer hover:border-destructive/50 transition-all"
            onClick={() => navigate("/demo/admin")}
            data-testid="demo-admin-btn"
          >
            <div className="text-center">
              <div className="w-12 h-12 mx-auto bg-destructive/20 rounded-full flex items-center justify-center mb-3">
                <Shield className="w-6 h-6 text-destructive" />
              </div>
              <h3 className="text-lg font-bold heading text-destructive mb-2">Démo Admin</h3>
              <p className="text-xs text-muted-foreground mb-3">
                Gérer sessions et scénarios
              </p>
              <Button size="sm" className="w-full bg-destructive/20 text-destructive border border-destructive/50 hover:bg-destructive/30">
                <Play className="w-3 h-3 mr-1" /> Lancer
              </Button>
            </div>
          </motion.div>

          {/* Demo Animator */}
          <motion.div
            whileHover={{ scale: 1.02 }}
            className="crisis-panel p-4 cursor-pointer hover:border-amber-500/50 transition-all"
            onClick={() => window.open(`${BASE_URL}/animator/demo`, '_blank')}
            data-testid="demo-animator-btn"
          >
            <div className="text-center">
              <div className="w-12 h-12 mx-auto bg-amber-500/20 rounded-full flex items-center justify-center mb-3">
                <Radio className="w-6 h-6 text-amber-400" />
              </div>
              <h3 className="text-lg font-bold heading text-amber-400 mb-2">Démo Animateur</h3>
              <p className="text-xs text-muted-foreground mb-3">
                Piloter la simulation en direct
              </p>
              <Button size="sm" className="w-full bg-amber-500/20 text-amber-400 border border-amber-500/50 hover:bg-amber-500/30">
                <Play className="w-3 h-3 mr-1" /> Lancer
              </Button>
            </div>
          </motion.div>
        </div>

        {/* Links section */}
        <div className="crisis-panel p-4 mt-6">
          <h3 className="text-sm font-bold text-primary mb-3 flex items-center gap-2">
            <FileText className="w-4 h-4" /> Liens rapides
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
            <button 
              onClick={() => { navigator.clipboard.writeText(`${BASE_URL}/demo/client`); toast.success("Lien copié !"); }}
              className="p-2 bg-secondary/30 rounded hover:bg-secondary/50 transition-colors text-left"
            >
              <span className="text-primary font-bold">Joueur</span>
              <span className="block text-muted-foreground truncate">/demo/client</span>
            </button>
            <button 
              onClick={() => { navigator.clipboard.writeText(`${BASE_URL}/demo/observer`); toast.success("Lien copié !"); }}
              className="p-2 bg-secondary/30 rounded hover:bg-secondary/50 transition-colors text-left"
            >
              <span className="text-blue-400 font-bold">Observateur</span>
              <span className="block text-muted-foreground truncate">/demo/observer</span>
            </button>
            <button 
              onClick={() => { navigator.clipboard.writeText(`${BASE_URL}/demo/admin`); toast.success("Lien copié !"); }}
              className="p-2 bg-secondary/30 rounded hover:bg-secondary/50 transition-colors text-left"
            >
              <span className="text-destructive font-bold">Admin</span>
              <span className="block text-muted-foreground truncate">/demo/admin</span>
            </button>
            <button 
              onClick={() => { navigator.clipboard.writeText(`${BASE_URL}/animator/demo`); toast.success("Lien copié !"); }}
              className="p-2 bg-secondary/30 rounded hover:bg-secondary/50 transition-colors text-left"
            >
              <span className="text-amber-400 font-bold">Animateur</span>
              <span className="block text-muted-foreground truncate">/animator/demo</span>
            </button>
          </div>
        </div>

        <div className="text-center mt-6">
          <Button variant="ghost" onClick={() => navigate("/")} className="text-muted-foreground">
            <ArrowLeft className="w-4 h-4 mr-2" /> Retour à l'accueil
          </Button>
        </div>

        <div className="text-center mt-4">
          <p className="font-mono text-[10px] text-muted-foreground">
            © {APP_DATE} {APP_EDITOR} - {APP_VERSION}
          </p>
        </div>
      </motion.div>
    </div>
  );
};

// Demo Admin Request Form
export const DemoAdminRequest = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    role: "",
    motivation: ""
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.company || !formData.motivation) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/demo/request-admin-access`, formData);
      setSubmitted(true);
      toast.success("Demande envoyée avec succès");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'envoi");
    }
    setLoading(false);
  };

  if (submitted) {
    return (
      <div className="min-h-screen matrix-bg flex items-center justify-center p-4">
        <ScanlineOverlay />
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="crisis-panel p-8 max-w-md text-center"
        >
          <div className="w-20 h-20 mx-auto bg-primary/20 rounded-full flex items-center justify-center mb-6">
            <Mail className="w-10 h-10 text-primary" />
          </div>
          <h2 className="text-2xl font-bold heading text-primary mb-4">Demande Envoyée</h2>
          <p className="text-muted-foreground mb-6">
            Votre demande d'accès à la démo administrateur a été transmise. 
            Vous recevrez un email avec votre code d'accès une fois votre demande approuvée.
          </p>
          <Button onClick={() => navigate("/")} className="w-full">
            <ArrowLeft className="w-4 h-4 mr-2" /> Retour à l'accueil
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen matrix-bg flex items-center justify-center p-4">
      <ScanlineOverlay />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-lg w-full"
      >
        <div className="text-center mb-6">
          <Shield className="w-12 h-12 text-destructive mx-auto mb-4" />
          <h1 className="text-2xl font-bold heading text-destructive">Demande d'accès Démo Admin</h1>
          <p className="font-mono text-sm text-muted-foreground mt-2">
            Remplissez ce formulaire pour demander l'accès
          </p>
        </div>

        <form onSubmit={handleSubmit} className="crisis-panel p-6 space-y-4">
          <div>
            <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground block mb-2">
              <User className="w-3 h-3 inline mr-1" /> Nom complet *
            </label>
            <Input
              className="input-matrix"
              placeholder="Jean Dupont"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>

          <div>
            <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground block mb-2">
              <Mail className="w-3 h-3 inline mr-1" /> Email professionnel *
            </label>
            <Input
              type="email"
              className="input-matrix"
              placeholder="jean.dupont@entreprise.com"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
            />
          </div>

          <div>
            <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground block mb-2">
              <Building className="w-3 h-3 inline mr-1" /> Entreprise / Organisation *
            </label>
            <Input
              className="input-matrix"
              placeholder="ACME Corporation"
              value={formData.company}
              onChange={(e) => setFormData({ ...formData, company: e.target.value })}
              required
            />
          </div>

          <div>
            <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground block mb-2">
              <Users className="w-3 h-3 inline mr-1" /> Fonction
            </label>
            <Input
              className="input-matrix"
              placeholder="Directeur de la Communication"
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
            />
          </div>

          <div>
            <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground block mb-2">
              <FileText className="w-3 h-3 inline mr-1" /> Motivation de votre demande *
            </label>
            <Textarea
              className="input-matrix min-h-[100px]"
              placeholder="Expliquez pourquoi vous souhaitez tester la plateforme en tant qu'administrateur..."
              value={formData.motivation}
              onChange={(e) => setFormData({ ...formData, motivation: e.target.value })}
              required
            />
          </div>

          <div className="pt-4 space-y-3">
            <Button
              type="submit"
              className="w-full bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={loading}
            >
              {loading ? (
                <>
                  <div className="loading-dot" />
                  <div className="loading-dot" />
                  <div className="loading-dot" />
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" /> Envoyer ma demande
                </>
              )}
            </Button>
            
            <Button
              type="button"
              variant="ghost"
              className="w-full text-muted-foreground"
              onClick={() => navigate("/demo")}
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Retour
            </Button>
          </div>
        </form>

        <p className="font-mono text-[10px] text-muted-foreground text-center mt-6">
          © {APP_DATE} {APP_EDITOR} - {APP_VERSION}
        </p>
      </motion.div>
    </div>
  );
};

// Demo Admin Login (with code)
export const DemoAdminLogin = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !code) {
      toast.error("Email et code requis");
      return;
    }
    
    setLoading(true);
    try {
      const res = await axios.post(`${API}/demo/verify-admin-code`, { email, code });
      localStorage.setItem('demoAdminToken', res.data.token);
      toast.success("Accès autorisé");
      navigate("/demo/admin");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Code invalide ou expiré");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen matrix-bg flex items-center justify-center p-4">
      <ScanlineOverlay />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full"
      >
        <div className="text-center mb-6">
          <Shield className="w-12 h-12 text-destructive mx-auto mb-4" />
          <h1 className="text-2xl font-bold heading text-destructive">Accès Démo Admin</h1>
          <p className="font-mono text-sm text-muted-foreground mt-2">
            Entrez votre code d'accès reçu par email
          </p>
        </div>

        <form onSubmit={handleLogin} className="crisis-panel p-6 space-y-4">
          <div>
            <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground block mb-2">
              Email
            </label>
            <Input
              type="email"
              className="input-matrix"
              placeholder="votre@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div>
            <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground block mb-2">
              Code d'accès
            </label>
            <Input
              className="input-matrix text-center text-2xl tracking-widest"
              placeholder="XXXXXX"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              maxLength={6}
            />
          </div>

          <Button
            type="submit"
            className="w-full bg-destructive text-destructive-foreground"
            disabled={loading}
          >
            {loading ? "Vérification..." : "Accéder à la démo"}
          </Button>

          <div className="text-center pt-2">
            <Button
              type="button"
              variant="link"
              className="text-muted-foreground text-sm"
              onClick={() => navigate("/demo/admin-request")}
            >
              Pas encore de code ? Demander l'accès
            </Button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

// Cinematic Intro Component
const CinematicIntro = ({ scenario, companyName, onComplete, playerRole }) => {
  const [phase, setPhase] = useState(0);
  const [textVisible, setTextVisible] = useState(false);
  
  // Get current real date and time for intro
  const now = new Date();
  const dateStr = now.toLocaleDateString('fr-FR', { 
    day: 'numeric', 
    month: 'long', 
    year: 'numeric' 
  }).toUpperCase();
  // Use scenario start time (08:00) instead of real time for immersion
  const timeStr = "08:00";
  
  // Dynamic role info
  const roleTitle = playerRole?.title || "Directeur de la Communication";
  const roleName = playerRole?.name || "DirCom";
  
  // Intro phases configuration - using scenario time (08:00)
  const introPhases = [
    {
      type: "date",
      text: dateStr,
      subtext: timeStr,
      duration: 3000
    },
    {
      type: "location",
      text: companyName || "ACME CORPORATION",
      subtext: "Zone industrielle de Lyon",
      duration: 3500
    },
    {
      type: "context",
      title: "SITUATION INITIALE",
      lines: [
        "Un incendie vient de se déclarer dans l'entrepôt de LogiStock,",
        "entreprise voisine de votre site industriel.",
        "",
        "Les flammes sont visibles depuis l'autoroute.",
        "Les pompiers sont en route."
      ],
      duration: 7000
    },
    {
      type: "alert",
      title: "⚠️ ALERTE DÉCLENCHÉE",
      text: "La cellule de crise est activée.",
      duration: 3000
    },
    {
      type: "role",
      title: "VOTRE RÔLE",
      text: roleTitle,
      subtext: "Vous rejoignez votre poste en salle de crise.",
      duration: 4000
    },
    {
      type: "start",
      title: "SIMULATION",
      text: "EN COURS",
      duration: 2000
    }
  ];

  useEffect(() => {
    if (phase >= introPhases.length) {
      onComplete();
      return;
    }

    // Fade in text
    setTextVisible(false);
    const fadeInTimer = setTimeout(() => setTextVisible(true), 300);
    
    // Move to next phase
    const phaseTimer = setTimeout(() => {
      setPhase(prev => prev + 1);
    }, introPhases[phase].duration);

    return () => {
      clearTimeout(fadeInTimer);
      clearTimeout(phaseTimer);
    };
  }, [phase, onComplete]);

  if (phase >= introPhases.length) return null;

  const currentPhase = introPhases[phase];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[200] bg-black flex items-center justify-center"
    >
      <div className="max-w-3xl w-full px-8 text-center">
        <AnimatePresence mode="wait">
          <motion.div
            key={phase}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: textVisible ? 1 : 0, y: textVisible ? 0 : 20 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
          >
            {currentPhase.type === "date" && (
              <div className="space-y-4">
                <p className="font-mono text-primary/60 text-sm tracking-[0.5em]">
                  {currentPhase.text}
                </p>
                <p className="font-mono text-6xl text-primary font-bold">
                  {currentPhase.subtext}
                </p>
              </div>
            )}

            {currentPhase.type === "location" && (
              <div className="space-y-4">
                <h1 className="text-5xl font-bold heading text-white tracking-wider">
                  {currentPhase.text}
                </h1>
                <p className="font-mono text-primary/80 text-lg">
                  {currentPhase.subtext}
                </p>
              </div>
            )}

            {currentPhase.type === "context" && (
              <div className="space-y-6">
                <h2 className="font-mono text-primary text-sm tracking-[0.3em]">
                  {currentPhase.title}
                </h2>
                <div className="space-y-2">
                  {currentPhase.lines.map((line, i) => (
                    <motion.p
                      key={i}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: i * 0.3 }}
                      className={`text-xl ${line === "" ? "h-4" : "text-white/90"}`}
                    >
                      {line}
                    </motion.p>
                  ))}
                </div>
              </div>
            )}

            {currentPhase.type === "alert" && (
              <div className="space-y-6">
                <motion.div
                  animate={{ scale: [1, 1.05, 1] }}
                  transition={{ repeat: Infinity, duration: 1 }}
                  className="text-4xl font-bold text-destructive"
                >
                  {currentPhase.title}
                </motion.div>
                <p className="text-2xl text-white">
                  {currentPhase.text}
                </p>
              </div>
            )}

            {currentPhase.type === "role" && (
              <div className="space-y-6">
                <p className="font-mono text-primary/60 text-sm tracking-[0.3em]">
                  {currentPhase.title}
                </p>
                <h2 className="text-4xl font-bold text-white">
                  {currentPhase.text}
                </h2>
                <p className="text-lg text-white/70">
                  {currentPhase.subtext}
                </p>
              </div>
            )}

            {currentPhase.type === "start" && (
              <div className="space-y-4">
                <p className="font-mono text-primary/60 text-sm tracking-[0.5em]">
                  {currentPhase.title}
                </p>
                <motion.h2
                  animate={{ opacity: [0.5, 1, 0.5] }}
                  transition={{ repeat: Infinity, duration: 1.5 }}
                  className="text-5xl font-bold text-primary"
                >
                  {currentPhase.text}
                </motion.h2>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Skip button */}
      <Button
        variant="ghost"
        className="absolute bottom-8 right-8 text-white/50 hover:text-white"
        onClick={onComplete}
      >
        Passer l'intro →
      </Button>

      {/* Progress indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2">
        {introPhases.map((_, i) => (
          <div
            key={i}
            className={`w-2 h-2 rounded-full transition-colors ${
              i === phase ? 'bg-primary' : i < phase ? 'bg-primary/50' : 'bg-white/20'
            }`}
          />
        ))}
      </div>
    </motion.div>
  );
};

// MessageDetailModal - Extracted as a separate component to fix re-render issues
const MessageDetailModal = ({ 
  message, 
  onClose, 
  textSize, 
  textSizeClasses, 
  cycleTextSize, 
  onSendResponse,
  onShareToComex,
  scenarioTime,
  platformIcons
}) => {
  // Local state for response text to prevent parent re-renders
  const [localResponseText, setLocalResponseText] = useState('');
  
  if (!message) return null;
  const sizes = textSizeClasses[textSize];
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-2 sm:p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="crisis-panel p-4 sm:p-6 max-w-2xl w-full max-h-[95vh] overflow-y-auto relative"
        onClick={e => e.stopPropagation()}
      >
        {/* Large back button at top */}
        <button
          onClick={onClose}
          className="absolute top-2 right-2 sm:top-4 sm:right-4 flex items-center gap-2 px-3 py-2 bg-primary/10 hover:bg-primary/20 border border-primary/30 rounded-lg text-primary transition-colors"
        >
          <X className="w-5 h-5" />
          <span className="text-sm font-medium hidden sm:inline">Retour Timeline</span>
        </button>
        
        <div className="flex items-center justify-between mb-4 pr-32">
          <Badge className={`badge-matrix ${message.message_type === 'flash_info' ? 'alert' : ''} ${textSize !== 'normal' ? 'text-base px-3 py-1' : ''}`}>
            {message.message_type}
          </Badge>
          <div className="flex items-center gap-2">
            {/* Text size button in modal */}
            <Button 
              variant="outline" 
              size="sm" 
              onClick={cycleTextSize}
              className="font-bold"
            >
              Aa{textSize === 'large' ? '+' : textSize === 'xlarge' ? '++' : ''}
            </Button>
          </div>
        </div>
        
        <div className="space-y-4">
          <div className="flex items-center gap-3 flex-wrap">
            {/* Show logo based on message type */}
            {message.message_type === 'social_post' ? (
              <SocialLogo platform={message.sender} size="md" />
            ) : (message.message_type === 'flash_info' || message.message_type === 'radio_flash' || message.message_type === 'media_arrival' || (message.message_type === 'email' && (message.sender?.includes('AFP') || message.sender?.includes('BFM') || message.sender?.includes('France') || message.sender?.includes('Monde')))) ? (
              <MediaLogo source={message.sender} size="md" />
            ) : (
              platformIcons[message.message_type]
            )}
            <div className="flex-1">
              <span className={`font-mono ${sizes.label} text-primary block`}>{message.sender}</span>
              {message.metadata?.author && (
                <span className="font-mono text-xs text-muted-foreground">{message.metadata.author}</span>
              )}
            </div>
            <span className={`font-mono text-xs text-muted-foreground`}>{message.scenario_time}</span>
          </div>
          
          {message.subject && (
            <h3 className={`${sizes.title} font-bold heading text-primary leading-tight`}>{message.subject}</h3>
          )}
          
          <div className="bg-secondary/30 p-4 sm:p-6 border border-border rounded">
            <p className={`${sizes.content} leading-relaxed whitespace-pre-wrap`}>{message.content}</p>
          </div>
          
          {/* Social engagement for social posts */}
          {message.message_type === 'social_post' && message.metadata && (
            <div className="flex items-center gap-6 text-sm p-3 bg-secondary/20 rounded border border-border">
              {message.metadata.likes && (
                <span className="flex items-center gap-2">
                  <Heart className="w-4 h-4 text-red-400" /> 
                  <span className="font-semibold">{message.metadata.likes > 1000 ? `${(message.metadata.likes/1000).toFixed(1)}K` : message.metadata.likes}</span>
                  <span className="text-muted-foreground text-xs">likes</span>
                </span>
              )}
              {message.metadata.retweets && (
                <span className="flex items-center gap-2">
                  <Share2 className="w-4 h-4 text-green-400" /> 
                  <span className="font-semibold">{message.metadata.retweets}</span>
                  <span className="text-muted-foreground text-xs">RT</span>
                </span>
              )}
              {message.metadata.comments && (
                <span className="flex items-center gap-2">
                  <MessageCircle className="w-4 h-4 text-blue-400" /> 
                  <span className="font-semibold">{message.metadata.comments}</span>
                  <span className="text-muted-foreground text-xs">com.</span>
                </span>
              )}
              {message.metadata.shares && (
                <span className="flex items-center gap-2">
                  <Share2 className="w-4 h-4 text-primary" /> 
                  <span className="font-semibold">{message.metadata.shares}</span>
                  <span className="text-muted-foreground text-xs">partages</span>
                </span>
              )}
              {message.metadata.angry_reactions && (
                <span className="flex items-center gap-2">
                  😡 <span className="font-semibold">{message.metadata.angry_reactions}</span>
                </span>
              )}
              {message.metadata.views && (
                <span className="flex items-center gap-2">
                  <Eye className="w-4 h-4 text-muted-foreground" /> 
                  <span className="font-semibold">{message.metadata.views > 1000 ? `${(message.metadata.views/1000).toFixed(1)}K` : message.metadata.views}</span>
                  <span className="text-muted-foreground text-xs">vues</span>
                </span>
              )}
            </div>
          )}
          
          <div className="pt-4 border-t border-border">
            <label className={`font-mono ${sizes.label} text-muted-foreground block mb-2`}>
              VOTRE RÉPONSE
            </label>
            <Textarea
              className={`input-matrix ${sizes.input}`}
              placeholder="Rédigez votre réponse..."
              value={localResponseText}
              onChange={(e) => setLocalResponseText(e.target.value)}
            />
            <Button
              className={`w-full mt-3 bg-primary text-primary-foreground ${textSize !== 'normal' ? 'py-4 text-lg' : ''}`}
              onClick={() => {
                onSendResponse(localResponseText);
                setLocalResponseText('');
                onClose();
              }}
              disabled={!localResponseText.trim()}
            >
              <Send className="w-4 h-4 mr-2" /> Envoyer la réponse
            </Button>
            
            {/* Share to COMEX button */}
            <Button
              variant="outline"
              className={`w-full mt-2 ${textSize !== 'normal' ? 'py-3 text-base' : ''}`}
              onClick={() => {
                onShareToComex(message, scenarioTime);
                onClose();
              }}
            >
              <Share2 className="w-4 h-4 mr-2" /> Partager avec l'équipe COMEX
            </Button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Demo Client Dashboard - Auto-running scenario with real-time x2 acceleration
export const DemoClientDashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [scenario, setScenario] = useState(null);
  const [messages, setMessages] = useState([]);
  const [startTime, setStartTime] = useState(null); // Set when user clicks "Commencer"
  const [scenarioTime, setScenarioTime] = useState("08:00");
  const [currentEventIndex, setCurrentEventIndex] = useState(-1);
  const [activeTab, setActiveTab] = useState("all");
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [responseText, setResponseText] = useState("");
  
  // Detect if we're in an iframe (multi-view mode) for compact display
  const isInIframe = window.self !== window.top;
  
  const [comexMessage, setComexMessage] = useState(""); // COMEX team chat message
  const [comexChat, setComexChat] = useState([ // COMEX team chat history
    { id: 1, sender: "DG", text: "On se retrouve en salle de crise dans 5 min", time: "08:02" },
    { id: 2, sender: "DRH", text: "J'ai prévenu les syndicats", time: "08:05" },
    { id: 3, sender: "DirOps", text: "Évacuation en cours du bâtiment B", time: "08:08" }
  ]);
  const [isRunning, setIsRunning] = useState(false); // Start paused until intro completes
  const [showIntro, setShowIntro] = useState(true);
  const [radioFlash, setRadioFlash] = useState(null); // Currently playing radio flash
  const [pendingRadioFlash, setPendingRadioFlash] = useState(null); // Notification waiting for user click
  const [isListeningRadio, setIsListeningRadio] = useState(false); // Is user currently listening to radio?
  const [incomingCall, setIncomingCall] = useState(null);
  const [pendingCall, setPendingCall] = useState(null); // Call waiting because radio is playing
  const [textSize, setTextSize] = useState('normal'); // 'normal', 'large', 'xlarge'
  const [savedFlashes, setSavedFlashes] = useState([]); // Store all radio flashes for replay
  const [flashPlayerOpen, setFlashPlayerOpen] = useState(null); // Flash to replay
  const [internalMessages, setInternalMessages] = useState([]); // Internal COMEX messages
  const [pressReleaseOpen, setPressReleaseOpen] = useState(false); // Press release editor
  const [socialPostOpen, setSocialPostOpen] = useState(false); // Social post creator
  const [toolboxOpen, setToolboxOpen] = useState(false); // Toolbox modal
  const [microTrottoirOpen, setMicroTrottoirOpen] = useState(false); // BFM TV micro-trottoir
  const [publishedReleases, setPublishedReleases] = useState([]); // Published press releases
  const [publishedSocialPosts, setPublishedSocialPosts] = useState([]); // Published social posts
  const [actionLogs, setActionLogs] = useState([]); // All user actions for RETEX
  const [callTranscripts, setCallTranscripts] = useState([]); // Transcribed call responses
  const [outgoingCallContact, setOutgoingCallContact] = useState(null); // For outgoing calls
  const [outgoingMessage, setOutgoingMessage] = useState(""); // Message to leave
  const intervalRef = useRef(null);
  const processedEventsRef = useRef(new Set());
  const isInitializedRef = useRef(false);
  
  // ========== ROLE MANAGEMENT ==========
  const [selectedRole, setSelectedRole] = useState(null); // Selected player role
  const [availableRoles, setAvailableRoles] = useState([]); // Roles from API
  const [showRoleSelector, setShowRoleSelector] = useState(true); // Show role selector modal
  const [playerInfo, setPlayerInfo] = useState({ name: '', email: '', phone: '' }); // Player info
  
  // Load available roles
  useEffect(() => {
    const loadRoles = async () => {
      try {
        const res = await axios.get(`${API}/roles/standard`);
        setAvailableRoles(res.data.roles || []);
        
        // Check URL params for pre-selected role
        const roleParam = searchParams.get('role');
        const sessionParam = searchParams.get('session');
        
        if (roleParam) {
          const preselectedRole = res.data.roles.find(r => r.id === roleParam);
          if (preselectedRole) {
            setSelectedRole(preselectedRole);
            setShowRoleSelector(false);
          }
        } else if (sessionParam) {
          // In synchronized mode, skip role selector automatically
          // User can still change role via the button in header
          setShowRoleSelector(false);
        }
      } catch (err) {
        console.error('Error loading roles:', err);
      }
    };
    loadRoles();
  }, [searchParams]);
  
  // Filter messages based on selected role
  const filteredMessages = useCallback(() => {
    if (!selectedRole) return messages;
    
    return messages.filter(msg => {
      // Show all messages if no target roles specified (broadcast)
      if (!msg.target_roles || msg.target_roles.length === 0) return true;
      // Show if "all" is in target roles
      if (msg.target_roles.includes('all')) return true;
      // Show if current role matches target roles (check id, name, or name variations)
      const roleMatchers = [
        selectedRole.id?.toLowerCase(),
        selectedRole.name?.toLowerCase(),
        // Common variations
        selectedRole.id,
        selectedRole.name
      ].filter(Boolean);
      
      return msg.target_roles.some(targetRole => {
        const targetLower = targetRole.toLowerCase();
        return roleMatchers.some(matcher => 
          matcher === targetLower || 
          matcher === targetRole ||
          // Handle mappings like "DirCom" -> "dir_com"
          targetLower.replace(/[^a-z]/g, '') === matcher.replace(/[^a-z]/g, '')
        );
      });
    });
  }, [messages, selectedRole]);
  
  // Demo sync hook - client/player listens to animator
  const { sessionId, isConnected, syncState, isSynced } = useDemoSync('client');
  
  // Sync state from animator (when in synchronized mode)
  // This should work even if showIntro is true - we want to skip intro when animator starts
  useEffect(() => {
    if (syncState) {
      // Update time from animator
      if (syncState.time) {
        setScenarioTime(syncState.time);
      }
      // Update running state from animator - CRITICAL: skip intro when animator starts
      if (syncState.is_running !== undefined) {
        setIsRunning(syncState.is_running);
        // If animator started the simulation, skip intro and start immediately
        if (syncState.is_running && showIntro) {
          console.log('[DemoClient] Admin started - skipping intro');
          setShowIntro(false);
          setStartTime(new Date());
        }
      }
      // Process triggered events from animator - no isSynced check needed
      if (syncState.triggered_events?.length > 0) {
        syncState.triggered_events.forEach(event => {
          const eventKey = `synced_${event.id || event.title || Date.now()}`;
          if (!processedEventsRef.current.has(eventKey)) {
            processedEventsRef.current.add(eventKey);
            // Add event to messages
            const newMessage = {
              id: `event-${Date.now()}-${Math.random()}`,
              type: event.event_type,
              message_type: event.event_type,
              sender: event.source,
              subject: event.title,
              content: event.content,
              scenario_time: syncState.time || scenarioTime,
              ...event,
              isNew: true
            };
            setMessages(prev => [newMessage, ...prev]);
            
            // Handle special event types
            if (event.event_type === 'phone_call' && !incomingCall) {
              setIncomingCall({
                caller: event.source || event.sender || event.caller,
                callerRole: event.title || event.actor || event.callerRole,
                script: event.content || event.script
              });
            }
          }
        });
      }
    }
  }, [syncState, isSynced, showIntro, scenarioTime, incomingCall]);

  // Calculate scenario time: real time accelerated x2
  const updateScenarioTime = useCallback(() => {
    // If startTime not set yet, just return current time
    if (!startTime) {
      const now = new Date();
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      return `${hours}:${minutes}`;
    }
    
    const now = new Date();
    const elapsedMs = now.getTime() - startTime.getTime();
    const acceleratedMs = elapsedMs * 2; // x2 acceleration
    const scenarioDate = new Date(startTime.getTime() + acceleratedMs);
    const hours = String(scenarioDate.getHours()).padStart(2, '0');
    const minutes = String(scenarioDate.getMinutes()).padStart(2, '0');
    setScenarioTime(`${hours}:${minutes}`);
    return `${hours}:${minutes}`;
  }, [startTime]);

  // Log user action for RETEX
  const logAction = useCallback((action) => {
    const logEntry = {
      id: `action-${Date.now()}`,
      timestamp: new Date().toISOString(),
      scenarioTime,
      ...action
    };
    setActionLogs(prev => [...prev, logEntry]);
  }, [scenarioTime]);

  // Handle press release publication
  const handlePublishRelease = useCallback((release) => {
    setPublishedReleases(prev => [...prev, { ...release, id: Date.now() }]);
    
    // Log the action
    logAction({
      type: 'press_release',
      title: release.title,
      distribution: release.distribution,
      content: release.content.substring(0, 100) + '...'
    });
    
    // Show toast for each distribution channel
    release.distribution.forEach(channel => {
      const channelNames = { afp: 'AFP', national: 'Presse Nationale', local: 'Presse Locale' };
      toast.success(`Communiqué envoyé à ${channelNames[channel]}`, {
        duration: 5000
      });
    });
    
    // Add to messages as internal confirmation
    const confirmMsg = {
      id: `release-${Date.now()}`,
      message_type: 'press_release_sent',
      sender: 'DirCom',
      subject: `📰 Communiqué publié : ${release.title}`,
      content: `Diffusé à : ${release.distribution.map(d => ({ afp: 'AFP', national: 'Presse Nationale', local: 'Presse Locale' }[d])).join(', ')}`,
      scenario_time: scenarioTime,
      is_read: true,
      metadata: { release }
    };
    setMessages(prev => [confirmMsg, ...prev]);
  }, [scenarioTime, logAction]);

  // Handle social post publication
  const handlePublishSocialPost = useCallback((post) => {
    setPublishedSocialPosts(prev => [...prev, { ...post, id: Date.now() }]);
    
    // Log the action
    logAction({
      type: 'social_post',
      platform: post.platform,
      content: post.content.substring(0, 100) + '...'
    });
    
    // Add to messages as internal confirmation
    const platformLabels = { twitter: 'X', linkedin: 'LinkedIn', facebook: 'Facebook' };
    const confirmMsg = {
      id: `social-${Date.now()}`,
      message_type: 'social_post_sent',
      sender: 'DirCom',
      subject: `📱 Post ${platformLabels[post.platform]} publié`,
      content: post.content.substring(0, 150) + (post.content.length > 150 ? '...' : ''),
      scenario_time: scenarioTime,
      is_read: true,
      metadata: { post }
    };
    setMessages(prev => [confirmMsg, ...prev]);
  }, [scenarioTime, logAction]);

  // Handle call transcript from STT
  const handleCallTranscript = useCallback((transcript, callData) => {
    const transcriptEntry = {
      id: `transcript-${Date.now()}`,
      caller: callData.caller,
      callerRole: callData.callerRole,
      question: callData.script,
      response: transcript,
      scenarioTime,
      timestamp: new Date().toISOString()
    };
    setCallTranscripts(prev => [...prev, transcriptEntry]);
    
    // Log the action
    logAction({
      type: 'phone_response',
      caller: callData.caller,
      transcript: transcript.substring(0, 100) + '...'
    });
    
    toast.success('Votre réponse a été transcrite et enregistrée');
  }, [scenarioTime, logAction]);

  // Save flash for replay
  const saveFlashForReplay = useCallback((flash) => {
    setSavedFlashes(prev => {
      // Avoid duplicates
      if (prev.some(f => f.content === flash.content)) return prev;
      return [...prev, { ...flash, id: Date.now() }];
    });
  }, []);

  // Handle user clicking "Listen" on radio notification
  const handleListenRadio = useCallback(() => {
    if (pendingRadioFlash) {
      setIsListeningRadio(true);
      setRadioFlash(pendingRadioFlash);
      setPendingRadioFlash(null);
    }
  }, [pendingRadioFlash]);

  // Handle radio flash completion
  const handleRadioComplete = useCallback(() => {
    setRadioFlash(null);
    setIsListeningRadio(false);
    
    // If there's a pending call, show it now
    if (pendingCall) {
      setTimeout(() => {
        setIncomingCall(pendingCall);
        setPendingCall(null);
      }, 500);
    }
  }, [pendingCall]);

  // Handle dismissing radio notification
  const handleDismissRadio = useCallback(() => {
    if (pendingRadioFlash) {
      // Just close the notification silently
      setPendingRadioFlash(null);
    }
  }, [pendingRadioFlash]);

  // Share flash internally to COMEX members
  const shareFlashInternal = useCallback(({ flash, recipients, message, sender }) => {
    const internalMsg = {
      id: `internal-${Date.now()}`,
      message_type: 'internal_flash',
      sender: sender || 'DirCom',
      subject: `📻 Flash Radio à écouter - ${flash.station}`,
      content: message || `Le DirCom vous partage ce flash radio important.\n\n📻 ${flash.station} [${flash.scenarioTime}]\n\n"${flash.content.substring(0, 200)}..."`,
      scenario_time: flash.scenarioTime,
      is_read: false,
      recipients: recipients,
      flash_data: flash, // Attach flash for replay
      metadata: { type: 'flash_share', from: 'DirCom' }
    };
    
    setInternalMessages(prev => [internalMsg, ...prev]);
    setMessages(prev => [internalMsg, ...prev]);
  }, []);

  const handleIntroComplete = useCallback(() => {
    // Set start time to NOW when user clicks "Commencer"
    const now = new Date();
    setStartTime(now);
    // Also update scenarioTime to match the real current time
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    setScenarioTime(`${hours}:${minutes}`);
    setShowIntro(false);
    setIsRunning(true);
  }, []);

  useEffect(() => {
    // Load Incendie Industriel scenario only once
    if (isInitializedRef.current) return;
    isInitializedRef.current = true;
    
    const loadScenario = async () => {
      try {
        const res = await axios.get(`${API}/scenarios`);
        if (res.data.scenarios?.length > 0) {
          // Find "Incendie Industriel" scenario specifically for demo
          const incendieScenario = res.data.scenarios.find(s => s.name === "Incendie Industriel");
          setScenario(incendieScenario || res.data.scenarios[0]);
        }
      } catch (err) {
        console.error(err);
      }
    };
    loadScenario();
  }, []);

  useEffect(() => {
    // Skip local timer if in synchronized mode - animator controls everything
    if (!scenario || !isRunning || isSynced) return;
    
    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    // Auto-advance scenario every 3 seconds in demo (fast for submersion effect)
    // But skip first few radio flashes to give user time to discover the interface
    let eventSkipCount = 0;
    const DELAY_FIRST_RADIO = 5; // Skip first N events before showing radio flash
    
    intervalRef.current = setInterval(() => {
      // Update scenario time (real time x2)
      updateScenarioTime();
      
      setCurrentEventIndex(prev => {
        const nextIndex = prev + 1;
        if (nextIndex >= scenario.events.length) return prev;
        
        const messageId = `demo-${nextIndex}`;
        
        // Skip if already processed (prevents duplicates)
        if (processedEventsRef.current.has(messageId)) {
          return nextIndex;
        }
        
        processedEventsRef.current.add(messageId);
        const event = scenario.events[nextIndex];
        eventSkipCount++;
        
        // Get current scenario time
        const currentTime = updateScenarioTime();
        
        const newMessage = {
          id: messageId,
          message_type: event.event_type,
          sender: event.source,
          subject: event.title,
          content: event.content
            .replace(/{company_name}/g, scenario?.company_name || "ACME Corporation")
            .replace(/{city}/g, "Lyon")
            .replace(/{neighboring_company}/g, "LogiStock")
            .replace(/{department}/g, "Rhône")
            .replace(/{erp_name}/g, "SAP")
            .replace(/\(company_name\)/g, scenario?.company_name || "ACME Corporation")
            .replace(/\(erp_name\)/g, "SAP"),
          scenario_time: currentTime,
          platform: event.source,
          is_read: false,
          metadata: event.metadata || {}
        };
        
        // MUTUAL EXCLUSION: Radio and Calls cannot happen simultaneously
        const isRadioActive = isListeningRadio || radioFlash;
        const isCallActive = incomingCall !== null;
        
        // Handle phone_call events - Only one call at a time, but allow multiple calls during the simulation
        if (event.event_type === 'phone_call' && !processedEventsRef.current.has(`call_${nextIndex}`)) {
          processedEventsRef.current.add(`call_${nextIndex}`);
          const callData = {
            caller: event.source,
            callerRole: event.title,
            script: newMessage.content
          };
          
          // Check if radio is playing or another call is active - if so, queue the call
          if (isRadioActive || pendingRadioFlash || isCallActive || pendingCall) {
            // Only queue if no call is already pending
            if (!pendingCall) {
              setPendingCall(callData);
            }
          } else {
            setIncomingCall(callData);
          }
        }
        
        // Handle radio_flash events - DELAY first ones to let user discover interface
        if ((event.event_type === 'radio_flash' || event.event_type === 'flash_info') && 
            !processedEventsRef.current.has(`radio_${nextIndex}`)) {
          processedEventsRef.current.add(`radio_${nextIndex}`);
          
          const flashData = {
            station: event.source || "France Info",
            content: newMessage.content,
            scenarioTime: currentTime
          };
          
          // Only show notification if no call is active AND we've shown enough events first
          if (!isCallActive && !pendingCall && eventSkipCount > DELAY_FIRST_RADIO) {
            setPendingRadioFlash(flashData);
          }
          // If too early or call is active, flash just appears in messages list (no notification)
        }
        
        // Handle tv_report events (BFM Micro-Trottoir) - Show notification banner
        if (event.event_type === 'tv_report' && 
            !processedEventsRef.current.has(`tv_${nextIndex}`)) {
          processedEventsRef.current.add(`tv_${nextIndex}`);
          
          // Show toast notification for BFM TV
          toast("🎥 BFM TV - ÉDITION SPÉCIALE", { 
            description: "Micro-trottoir disponible - Cliquez dans l'onglet Radio/TV pour regarder",
            duration: 10000,
            action: {
              label: "Regarder",
              onClick: () => setMicroTrottoirOpen(true)
            }
          });
        }
        
        // Show toast for media arrival
        if (event.event_type === 'media_arrival') {
          toast.error("ARRIVÉE MÉDIA", { 
            description: `${event.source} est sur place`,
            duration: 8000
          });
        }
        
        // Add message to the feed EXCEPT for phone_call events
        // Phone calls should only appear in the feed AFTER being answered
        if (event.event_type !== 'phone_call' && event.event_type !== 'tv_report') {
          setMessages(prevMessages => [newMessage, ...prevMessages]);
        }
        
        
        return nextIndex;
      });
    }, 3000); // New event every 3 seconds for submersion effect

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [scenario, isRunning, isSynced, isListeningRadio, radioFlash, pendingRadioFlash, incomingCall, pendingCall, saveFlashForReplay]);

  const handleCallAnswer = () => {
    // Add call summary to messages AFTER the call was answered
    if (incomingCall) {
      const callSummary = {
        id: `call-summary-${Date.now()}`,
        message_type: 'call_log',
        sender: incomingCall.caller,
        subject: `📞 Appel reçu - ${incomingCall.callerRole}`,
        content: `Vous avez répondu à l'appel de ${incomingCall.caller}. Résumé : "${incomingCall.script.substring(0, 100)}..."`,
        scenario_time: scenarioTime,
        is_read: true,
        metadata: { call_type: 'answered' }
      };
      setMessages(prev => [callSummary, ...prev]);
    }
    setIncomingCall(null);
    toast.success("Appel terminé", { description: "Vous avez raccroché" });
  };

  const handleCallReject = () => {
    // Add missed call to messages
    if (incomingCall) {
      const missedCall = {
        id: `call-missed-${Date.now()}`,
        message_type: 'call_log',
        sender: incomingCall.caller,
        subject: `📵 Appel manqué - ${incomingCall.callerRole}`,
        content: `Vous avez refusé l'appel de ${incomingCall.caller}. L'appelant réessaiera peut-être...`,
        scenario_time: scenarioTime,
        is_read: false,
        metadata: { call_type: 'missed' }
      };
      setMessages(prev => [missedCall, ...prev]);
    }
    setIncomingCall(null);
    toast.warning("Appel refusé", { description: "L'appelant réessaiera peut-être..." });
  };

  const sendResponse = () => {
    if (!responseText.trim() || !selectedMessage) return;
    
    toast.success("Réponse envoyée", {
      description: `Votre réponse à ${selectedMessage.sender} a été transmise`
    });
    setResponseText("");
    setSelectedMessage(null);
  };

  // Send COMEX team message
  const sendComexMessage = () => {
    if (!comexMessage.trim()) return;
    
    const newMsg = {
      id: Date.now(),
      sender: "Vous (DirCom)",
      text: comexMessage,
      time: scenarioTime
    };
    
    setComexChat(prev => [...prev, newMsg]);
    setComexMessage("");
    toast.success("Message envoyé à l'équipe COMEX");
  };

  // First filter by role, then by type
  const filterMessages = (type) => {
    // Apply role filter first
    let roleFiltered = messages;
    if (selectedRole) {
      roleFiltered = messages.filter(msg => {
        // Show all messages if no target roles specified (broadcast)
        if (!msg.target_roles || msg.target_roles.length === 0) return true;
        // Show if "all" is in target roles
        if (msg.target_roles.includes('all')) return true;
        // Show if current role matches target roles (check id, name, or name variations)
        const roleMatchers = [
          selectedRole.id?.toLowerCase(),
          selectedRole.name?.toLowerCase(),
          selectedRole.id,
          selectedRole.name
        ].filter(Boolean);
        
        return msg.target_roles.some(targetRole => {
          const targetLower = targetRole.toLowerCase();
          return roleMatchers.some(matcher => 
            matcher === targetLower || 
            matcher === targetRole ||
            // Handle mappings like "DirCom" -> "dir_com"
            targetLower.replace(/[^a-z]/g, '') === matcher.replace(/[^a-z]/g, '')
          );
        });
      });
    }
    
    // Then apply type filter
    if (type === "all") return roleFiltered;
    if (type === "social") return roleFiltered.filter(m => 
      m.message_type === "social_post" || 
      m.sender?.toLowerCase()?.includes("twitter") ||
      m.sender?.toLowerCase()?.includes("x ") ||
      m.sender?.toLowerCase()?.includes("facebook") ||
      m.sender?.toLowerCase()?.includes("instagram") ||
      m.sender?.toLowerCase()?.includes("tiktok") ||
      m.sender?.toLowerCase()?.includes("linkedin") ||
      m.sender?.toLowerCase()?.includes("youtube")
    );
    if (type === "press") return roleFiltered.filter(m => 
      m.message_type === "media_arrival" ||
      m.sender?.toLowerCase()?.includes("afp") ||
      m.sender?.toLowerCase()?.includes("le monde") ||
      m.sender?.toLowerCase()?.includes("figaro") ||
      m.sender?.toLowerCase()?.includes("reuters") ||
      m.sender?.toLowerCase()?.includes("libération") ||
      m.sender?.toLowerCase()?.includes("échos") ||
      m.sender?.toLowerCase()?.includes("ouest-france") ||
      m.sender?.toLowerCase()?.includes("dépêche")
    );
    if (type === "radio") return roleFiltered.filter(m => 
      m.message_type === "flash_info" || 
      m.message_type === "radio_flash" ||
      m.message_type === "tv_report" || // Include BFM TV reports
      m.sender?.toLowerCase()?.includes("france info") ||
      m.sender?.toLowerCase()?.includes("europe 1") ||
      m.sender?.toLowerCase()?.includes("rtl") ||
      m.sender?.toLowerCase()?.includes("bfm")
    );
    if (type === "sms") return roleFiltered.filter(m => 
      m.message_type === "sms" || 
      m.message_type === "whatsapp"
      // Removed sender-based filtering - only use message_type
    );
    if (type === "calls") return []; // Handled separately with callTranscripts
    if (type === "internal") return roleFiltered.filter(m => 
      (m.message_type === "email" || m.message_type === "internal_flash") &&
      !m.sender?.toLowerCase()?.includes("afp") &&
      !m.sender?.toLowerCase()?.includes("le monde") &&
      !m.sender?.toLowerCase()?.includes("figaro") &&
      !m.sender?.toLowerCase()?.includes("reuters")
    );
    return roleFiltered;
  };

  const platformIcons = {
    flash_info: <Radio className="w-4 h-4 text-destructive" />,
    social_post: <Globe className="w-4 h-4 text-blue-400" />,
    media_arrival: <Newspaper className="w-4 h-4 text-yellow-500" />,
    email: <Mail className="w-4 h-4" />,
    sms: <MessageSquare className="w-4 h-4" />,
    phone_call: <Activity className="w-4 h-4 text-primary" />,
  };

  // Text size classes
  const textSizeClasses = {
    normal: {
      content: 'text-base',
      title: 'text-xl',
      label: 'text-sm',
      input: 'text-base min-h-[100px]'
    },
    large: {
      content: 'text-lg',
      title: 'text-2xl',
      label: 'text-base',
      input: 'text-lg min-h-[120px]'
    },
    xlarge: {
      content: 'text-xl',
      title: 'text-3xl',
      label: 'text-lg',
      input: 'text-xl min-h-[140px]'
    }
  };

  const cycleTextSize = () => {
    setTextSize(prev => {
      if (prev === 'normal') return 'large';
      if (prev === 'large') return 'xlarge';
      return 'normal';
    });
  };

  return (
    <div className={`${isInIframe ? 'h-full overflow-hidden' : 'min-h-screen'} matrix-bg flex flex-col`}>
      <ScanlineOverlay />
      
      {/* Role Selector Modal - COMPACT VERSION for iframe */}
      <AnimatePresence>
        {showRoleSelector && availableRoles.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/95 flex items-start justify-center p-2 overflow-y-auto"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-background border-2 border-destructive rounded-lg p-3 w-full max-w-2xl my-2"
            >
              <h2 className="text-lg font-bold text-destructive mb-2 flex items-center gap-2">
                <Users className="w-5 h-5" /> Choisissez votre rôle
              </h2>
              
              {/* Compact role grid */}
              <div className="grid grid-cols-4 gap-2 mb-3">
                {availableRoles.filter(r => r.default_active).map(role => (
                  <button
                    key={role.id}
                    onClick={() => {
                      setSelectedRole(role);
                    }}
                    className={`p-2 rounded border transition-all text-center ${
                      selectedRole?.id === role.id
                        ? 'border-green-500 bg-green-900/40'
                        : 'border-border hover:border-destructive/50 bg-secondary/30'
                    }`}
                  >
                    <div className="text-lg">{role.icon}</div>
                    <div className="font-bold text-xs truncate">{role.name}</div>
                  </button>
                ))}
              </div>
              
              {/* Action buttons - always visible */}
              <div className="flex gap-2 justify-between">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowRoleSelector(false)}
                >
                  Passer
                </Button>
                <Button
                  size="sm"
                  className="bg-green-600 hover:bg-green-700"
                  onClick={() => setShowRoleSelector(false)}
                >
                  <Check className="w-3 h-3 mr-1" />
                  {selectedRole ? `Valider: ${selectedRole.name}` : 'Continuer'}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Cinematic Intro */}
      <AnimatePresence>
        {showIntro && scenario && !showRoleSelector && (
          <CinematicIntro
            scenario={scenario}
            companyName="ACME CORPORATION"
            onComplete={handleIntroComplete}
            playerRole={selectedRole}
          />
        )}
      </AnimatePresence>
      
      {/* Radio Flash Notification - Clickable banner, but NOT shown during active call */}
      <AnimatePresence>
        {pendingRadioFlash && !radioFlash && !incomingCall && (
          <RadioFlashNotification
            station={pendingRadioFlash.station}
            content={pendingRadioFlash.content}
            scenarioTime={pendingRadioFlash.scenarioTime}
            onListen={handleListenRadio}
            onDismiss={handleDismissRadio}
            isVisible={true}
          />
        )}
      </AnimatePresence>
      
      {/* Radio Flash Active Player - Only plays when user clicks */}
      <AnimatePresence>
        {radioFlash && (
          <RadioFlashActivePlayer
            station={radioFlash.station}
            content={radioFlash.content}
            scenarioTime={radioFlash.scenarioTime}
            onComplete={handleRadioComplete}
            onSaveFlash={saveFlashForReplay}
            isVisible={true}
          />
        )}
      </AnimatePresence>
      
      {/* Incoming Phone Call Overlay - Only shows when radio is NOT playing */}
      <AnimatePresence>
        {incomingCall && !isListeningRadio && !radioFlash && !pendingRadioFlash && (
          <IncomingCall
            caller={incomingCall.caller}
            callerRole={incomingCall.callerRole}
            script={incomingCall.script}
            onAnswer={handleCallAnswer}
            onReject={handleCallReject}
            onTranscript={handleCallTranscript}
            isVisible={true}
          />
        )}
      </AnimatePresence>
      
      {/* Radio Flash Player for Replay (from Flashes tab) - uses original RadioFlashPlayer with flash object */}
      <AnimatePresence>
        {flashPlayerOpen && (
          <RadioFlashPlayer
            flash={flashPlayerOpen}
            onClose={() => setFlashPlayerOpen(null)}
            onShareInternal={shareFlashInternal}
          />
        )}
      </AnimatePresence>
      
      {/* Message Detail Modal */}
      <AnimatePresence>
        {selectedMessage && (
          selectedMessage.message_type === 'internal_flash' && selectedMessage.flash_data ? (
            <RadioFlashPlayer
              flash={selectedMessage.flash_data}
              onClose={() => setSelectedMessage(null)}
              onShareInternal={shareFlashInternal}
            />
          ) : (
            <MessageDetailModal 
              message={selectedMessage} 
              onClose={() => setSelectedMessage(null)}
              textSize={textSize}
              textSizeClasses={textSizeClasses}
              cycleTextSize={cycleTextSize}
              onSendResponse={(text) => {
                setResponseText(text);
                sendResponse();
              }}
              onShareToComex={(message, time) => {
                const shareText = `📋 ${message.message_type === 'social_post' ? 'Post' : message.message_type === 'radio_flash' ? 'Flash radio' : 'Info'} de ${message.sender}: "${message.content.substring(0, 80)}..."`;
                const newMsg = {
                  id: Date.now(),
                  sender: "Vous (DirCom)",
                  text: shareText,
                  time: time
                };
                setComexChat(prev => [...prev, newMsg]);
                toast.success("Partagé avec l'équipe COMEX");
              }}
              scenarioTime={scenarioTime}
              platformIcons={platformIcons}
            />
          )
        )}
      </AnimatePresence>
      
      {/* Press Release Editor Modal */}
      <AnimatePresence>
        {pressReleaseOpen && (
          <PressReleaseEditor
            isOpen={pressReleaseOpen}
            onClose={() => setPressReleaseOpen(false)}
            onPublish={handlePublishRelease}
            scenarioContext={scenario?.description || "Incident industriel en cours"}
            scenarioTime={scenarioTime}
          />
        )}
      </AnimatePresence>
      
      {/* Social Post Editor Modal */}
      <AnimatePresence>
        {socialPostOpen && (
          <SocialPostEditor
            isOpen={socialPostOpen}
            onClose={() => setSocialPostOpen(false)}
            onPublish={handlePublishSocialPost}
            scenarioContext={scenario?.description || "Incident industriel en cours"}
            scenarioTime={scenarioTime}
          />
        )}
      </AnimatePresence>
      
      {/* BFM TV Micro-Trottoir */}
      <AnimatePresence>
        {microTrottoirOpen && (
          <BFMTVMicroTrottoir
            isOpen={microTrottoirOpen}
            onClose={() => setMicroTrottoirOpen(false)}
            scenarioContext={scenario?.description || "Incident industriel en cours"}
            scenarioTime={scenarioTime}
          />
        )}
      </AnimatePresence>
      
      {/* Outgoing Call Modal */}
      <AnimatePresence>
        {outgoingCallContact && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="bg-card border border-blue-500/50 rounded-xl p-6 max-w-md w-full"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 rounded-full bg-blue-600/30 flex items-center justify-center">
                  <PhoneOutgoing className="w-8 h-8 text-blue-400" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Appel sortant</h3>
                  <p className="text-blue-400 font-semibold">{outgoingCallContact.name}</p>
                  <p className="text-sm text-muted-foreground">{outgoingCallContact.role}</p>
                </div>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-muted-foreground mb-2">
                  Votre message (sera enregistré pour le RETEX)
                </label>
                <Textarea
                  value={outgoingMessage}
                  onChange={(e) => setOutgoingMessage(e.target.value)}
                  placeholder="Ex: Bonjour, je souhaite vous informer de la situation actuelle..."
                  className="min-h-[120px] bg-secondary/50"
                />
                <p className="text-xs text-muted-foreground mt-2">
                  💡 Dans une vraie simulation avec acteurs, cet appel serait passé à un animateur.
                </p>
              </div>
              
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setOutgoingCallContact(null);
                    setOutgoingMessage("");
                  }}
                >
                  Annuler
                </Button>
                <Button
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                  onClick={() => {
                    // Log the outgoing call
                    const callLog = {
                      id: `outgoing-${Date.now()}`,
                      caller: outgoingCallContact.name,
                      callerRole: outgoingCallContact.role,
                      direction: 'outgoing',
                      status: 'completed',
                      transcript: outgoingMessage || "(Pas de message)",
                      time: scenarioTime
                    };
                    setCallTranscripts(prev => [callLog, ...prev]);
                    
                    // Log action for RETEX
                    logAction({
                      type: 'outgoing_call',
                      contact: outgoingCallContact.name,
                      role: outgoingCallContact.role,
                      message: outgoingMessage
                    });
                    
                    toast.success(`Appel vers ${outgoingCallContact.name} enregistré`);
                    setOutgoingCallContact(null);
                    setOutgoingMessage("");
                  }}
                  disabled={!outgoingMessage.trim()}
                >
                  <Phone className="w-4 h-4 mr-2" /> Envoyer
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Floating Toolbox Button - "Vos outils" */}
      {!showIntro && (
        <motion.div
          initial={{ opacity: 0, scale: 0, x: 100 }}
          animate={{ opacity: 1, scale: 1, x: 0 }}
          transition={{ delay: 1.5 }}
          className="fixed bottom-20 right-4 z-[100]"
        >
          {/* Toolbox expanded menu */}
          <AnimatePresence>
            {toolboxOpen && (
              <motion.div
                initial={{ opacity: 0, y: 20, scale: 0.9 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 20, scale: 0.9 }}
                className="absolute bottom-16 right-0 bg-card border border-border rounded-xl shadow-2xl p-3 w-64 mb-2"
              >
                <div className="text-xs text-muted-foreground mb-2 font-mono">VOS OUTILS</div>
                
                <button
                  onClick={() => { setPressReleaseOpen(true); setToolboxOpen(false); }}
                  className="w-full p-3 rounded-lg bg-blue-600/10 hover:bg-blue-600/20 border border-blue-500/30 text-left mb-2 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <PenLine className="w-5 h-5 text-blue-500" />
                    <div>
                      <div className="font-medium text-foreground">Communiqué de Presse</div>
                      <div className="text-xs text-muted-foreground">Rédaction assistée par IA</div>
                    </div>
                  </div>
                </button>
                
                <button
                  onClick={() => { setSocialPostOpen(true); setToolboxOpen(false); }}
                  className="w-full p-3 rounded-lg bg-purple-600/10 hover:bg-purple-600/20 border border-purple-500/30 text-left transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <Globe className="w-5 h-5 text-purple-500" />
                    <div>
                      <div className="font-medium text-foreground">Post Réseaux Sociaux</div>
                      <div className="text-xs text-muted-foreground">Twitter, LinkedIn, Facebook</div>
                    </div>
                  </div>
                </button>
              </motion.div>
            )}
          </AnimatePresence>
          
          {/* Main toolbox button */}
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setToolboxOpen(!toolboxOpen)}
            className={`px-4 py-3 rounded-lg shadow-2xl flex items-center gap-2 transition-all border ${
              toolboxOpen 
                ? 'bg-primary text-primary-foreground border-primary' 
                : 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white border-indigo-400 hover:from-indigo-500 hover:to-purple-500'
            }`}
            title="Ouvrir la boîte à outils"
          >
            <Wrench className="w-5 h-5" />
            <span className="font-medium hidden sm:inline">Vos outils</span>
          </motion.button>
        </motion.div>
      )}
      
      {/* Floating Accessibility Button (Mobile) */}
      {!showIntro && (
        <motion.button
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 1 }}
          onClick={cycleTextSize}
          className="fixed bottom-4 left-4 z-40 w-10 h-10 rounded-full bg-secondary text-foreground shadow-lg flex items-center justify-center font-bold text-sm sm:hidden border border-border"
          title="Changer la taille du texte"
        >
          {textSize === 'normal' ? 'Aa' : textSize === 'large' ? 'A+' : 'A++'}
        </motion.button>
      )}
      
      {/* FIXED Header - Compact */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-card border-b border-border">
        <div className="flex items-center justify-between px-2 py-1">
          <div className="flex items-center gap-2 min-w-0">
            <Badge className="badge-matrix alert text-[10px] shrink-0">JOUEUR</Badge>
            {selectedRole && (
              <Badge className="text-[10px] shrink-0" style={{backgroundColor: selectedRole.color === 'red' ? '#dc2626' : selectedRole.color === 'blue' ? '#2563eb' : selectedRole.color === 'green' ? '#16a34a' : selectedRole.color === 'purple' ? '#9333ea' : selectedRole.color === 'amber' ? '#d97706' : '#6b7280'}}>
                {selectedRole.icon} {selectedRole.name}
              </Badge>
            )}
            <h1 className="heading text-xs text-primary truncate hidden sm:block">{scenario?.company_name || "ACME Corporation"}</h1>
          </div>
          <div className="flex items-center gap-1">
            {isSynced && (
              <Badge className={`text-[9px] px-1 ${isConnected ? 'bg-green-600' : 'bg-amber-600'}`}>
                {isConnected ? '🔗' : '⏳'}
              </Badge>
            )}
            <span className="digital-clock text-base">{scenarioTime}</span>
            {!isSynced && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsRunning(!isRunning)}
                className="h-8 w-8 p-0"
              >
                {isRunning ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                if (window.confirm("Êtes-vous sûr de vouloir quitter la simulation ?")) {
                  navigate("/demo");
                }
              }}
              className={`text-destructive hover:text-destructive hover:bg-destructive/10 ${isInIframe ? 'h-6 w-6 p-0' : 'h-8 px-2'}`}
            >
              <X className={`${isInIframe ? 'w-3 h-3' : 'w-4 h-4'}`} />
            </Button>
          </div>
        </div>
      </div>

      {/* FIXED Tabs - Always visible below header */}
      <div className="fixed top-[52px] left-0 right-0 z-40 bg-card/95 backdrop-blur border-b border-border">
        <div className="overflow-x-auto scrollbar-hide">
          <div className="flex gap-1 p-1 min-w-max">
            <button
              onClick={() => setActiveTab("all")}
              className={`flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium whitespace-nowrap transition-colors ${activeTab === 'all' ? 'bg-primary text-primary-foreground' : 'bg-secondary/50 hover:bg-secondary'}`}
            >
              <Activity className="w-3 h-3" /> Tous
              <span className="bg-primary/20 text-primary px-1 rounded text-[9px]">{messages.length}</span>
            </button>
            <button
              onClick={() => setActiveTab("press")}
              className={`flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium whitespace-nowrap transition-colors ${activeTab === 'press' ? 'bg-primary text-primary-foreground' : 'bg-secondary/50 hover:bg-secondary'}`}
            >
              <Newspaper className="w-3 h-3" /> Presse
            </button>
            <button
              onClick={() => setActiveTab("radio")}
              className={`flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium whitespace-nowrap transition-colors ${activeTab === 'radio' ? 'bg-red-600 text-white' : 'bg-secondary/50 hover:bg-secondary'}`}
            >
              <Radio className="w-3 h-3" /> Radio/TV
              {savedFlashes.length > 0 && <span className="bg-red-600 text-white px-1 rounded text-[9px]">{savedFlashes.length}</span>}
            </button>
            <button
              onClick={() => setActiveTab("social")}
              className={`flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium whitespace-nowrap transition-colors ${activeTab === 'social' ? 'bg-primary text-primary-foreground' : 'bg-secondary/50 hover:bg-secondary'}`}
            >
              <Globe className="w-3 h-3" /> Réseaux
            </button>
            <button
              onClick={() => setActiveTab("sms")}
              className={`flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium whitespace-nowrap transition-colors ${activeTab === 'sms' ? 'bg-green-600 text-white' : 'bg-secondary/50 hover:bg-secondary'}`}
            >
              <Smartphone className="w-3 h-3" /> SMS
            </button>
            <button
              onClick={() => setActiveTab("calls")}
              className={`flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium whitespace-nowrap transition-colors ${activeTab === 'calls' ? 'bg-blue-600 text-white' : 'bg-secondary/50 hover:bg-secondary'}`}
            >
              <PhoneCall className="w-3 h-3" /> Appels
              {callTranscripts.length > 0 && <span className="bg-blue-600 text-white px-1 rounded text-[9px]">{callTranscripts.length}</span>}
            </button>
            <button
              onClick={() => setActiveTab("internal")}
              className={`flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium whitespace-nowrap transition-colors ${activeTab === 'internal' ? 'bg-primary text-primary-foreground' : 'bg-secondary/50 hover:bg-secondary'}`}
            >
              <Mail className="w-3 h-3" /> Emails
            </button>
          </div>
        </div>
      </div>

      {/* Spacer for fixed header + tabs */}
      <div className={isInIframe ? 'h-[72px] shrink-0' : 'h-[88px]'}></div>
      
      {/* Main Content Area - fills remaining space */}
      <div className={`${isInIframe ? 'flex-1 overflow-y-auto' : ''}`}>

      {/* Main Content - No more Tabs wrapper */}
      <div className={`${isInIframe ? 'flex-1 overflow-hidden' : ''} p-2 max-w-6xl mx-auto`}>
        <div className={`crisis-panel p-2 ${isInIframe ? 'h-full' : ''}`}>
          <ScrollArea className={isInIframe ? 'h-full' : 'h-[calc(100vh-130px)]'}>
            <AnimatePresence>
              {activeTab === 'radio' ? (
                    // Radio/TV Tab
                    <div className="space-y-4">
                      {/* BFM TV Micro-Trottoir Button */}
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="bg-gradient-to-r from-red-900/50 to-red-800/50 rounded-xl p-4 border border-red-500/30 cursor-pointer hover:border-red-500/50 transition-colors"
                        onClick={() => setMicroTrottoirOpen(true)}
                      >
                        <div className="flex items-center gap-4">
                          <img src={BFM_MICRO_CAMERA} alt="BFM TV" className="w-16 h-16 rounded-lg object-cover" />
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="bg-white text-red-600 font-black text-xs px-2 py-0.5 rounded">BFM</span>
                              <span className="text-white font-bold text-sm">MICRO-TROTTOIR</span>
                              <motion.span
                                animate={{ opacity: [1, 0.5, 1] }}
                                transition={{ repeat: Infinity, duration: 1 }}
                                className="text-red-500 text-xs"
                              >🔴 DIRECT</motion.span>
                            </div>
                            <p className="text-white/70 text-xs">Écoutez les témoignages des riverains et salariés</p>
                          </div>
                          <Play className="w-8 h-8 text-red-400" />
                        </div>
                      </motion.div>
                      
                      {/* Radio Flashes */}
                      <h3 className="text-sm font-semibold text-primary flex items-center gap-2">
                        📻 Flashs Radio ({filterMessages('radio').length + savedFlashes.length})
                      </h3>
                      
                      {/* Pending radio flashes from messages */}
                      {filterMessages('radio').length > 0 && (
                        <div className="space-y-2 mb-4">
                          <p className="text-xs text-muted-foreground">Nouveaux flashs :</p>
                          {filterMessages('radio').map((msg, i) => (
                            <motion.div
                              key={msg.id}
                              initial={{ opacity: 0, x: -20 }}
                              animate={{ opacity: 1, x: 0 }}
                              className="message-card alert hover:border-red-500/50 transition-colors"
                            >
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <MediaLogo source={msg.sender || msg.source} size="sm" />
                                  <span className="font-mono text-xs text-muted-foreground">{msg.sender || msg.source}</span>
                                </div>
                                <span className="font-mono text-[10px] text-muted-foreground">{msg.scenario_time}</span>
                              </div>
                              <p className="text-sm text-foreground/80 line-clamp-2">{msg.content}</p>
                              <div className="flex items-center justify-between mt-2">
                                <Badge className="badge-matrix bg-red-900/50 text-red-300">
                                  📻 FLASH RADIO
                                </Badge>
                                {/* Button to listen to the flash */}
                                {(msg.message_type === 'flash_info' || msg.message_type === 'radio_flash') && (
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    className="text-xs bg-red-600/20 hover:bg-red-600/40 border-red-500/50"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setPendingRadioFlash({
                                        station: msg.sender || msg.source || "France Info",
                                        content: msg.content,
                                        scenarioTime: msg.scenario_time
                                      });
                                    }}
                                  >
                                    <Play className="w-3 h-3 mr-1" /> Écouter
                                  </Button>
                                )}
                              </div>
                            </motion.div>
                          ))}
                        </div>
                      )}
                      
                      {/* Saved/played flashes */}
                      {savedFlashes.length > 0 && (
                        <div className="space-y-2">
                          <p className="text-xs text-muted-foreground">Flashs diffusés (rejouables) :</p>
                          {savedFlashes.map((flash) => (
                            <motion.div
                              key={flash.id}
                              initial={{ opacity: 0, x: -20 }}
                              animate={{ opacity: 1, x: 0 }}
                              className="message-card alert cursor-pointer hover:border-red-500/50 transition-colors"
                              onClick={() => setFlashPlayerOpen(flash)}
                            >
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <MediaLogo source={flash.station} size="sm" />
                                  <span className="font-mono text-xs text-muted-foreground">{flash.station}</span>
                                </div>
                                <span className="font-mono text-[10px] text-muted-foreground">{flash.scenarioTime}</span>
                              </div>
                              <p className="text-sm text-foreground/80 line-clamp-2">{flash.content}</p>
                              <div className="flex items-center justify-between mt-2">
                                <Badge className="badge-matrix bg-red-900/50 text-red-300">
                                  FLASH RADIO
                                </Badge>
                                <Button size="sm" variant="outline" className="text-xs border-red-500/50 text-red-400 hover:bg-red-950">
                                  <Play className="w-3 h-3 mr-1" /> Réécouter
                                </Button>
                              </div>
                            </motion.div>
                          ))}
                        </div>
                      )}
                      
                      {/* Empty state */}
                      {filterMessages('radio').length === 0 && savedFlashes.length === 0 && (
                        <div className="text-center py-8">
                          <Radio className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                          <p className="text-muted-foreground">Aucun flash radio pour l'instant</p>
                          <p className="text-xs text-muted-foreground mt-2">Les flashs apparaîtront ici</p>
                        </div>
                      )}
                    </div>
                  ) : activeTab === 'calls' ? (
                    // Calls Tab - Call history AND Outgoing calls
                    <div className="space-y-4">
                      {/* Outgoing Calls Section */}
                      <div className="bg-gradient-to-r from-blue-900/30 to-blue-800/30 rounded-xl p-4 border border-blue-500/30">
                        <div className="flex items-center gap-2 mb-3">
                          <PhoneOutgoing className="w-5 h-5 text-blue-400" />
                          <h3 className="font-bold text-blue-400">Passer un appel</h3>
                        </div>
                        <p className="text-xs text-muted-foreground mb-3">
                          Sélectionnez un contact pour laisser un message (sera enregistré pour le RETEX)
                        </p>
                        
                        {/* Contacts Grid - extracted from scenario */}
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                          {scenario?.events
                            ?.filter(e => e.event_type === 'phone_call')
                            ?.map((event, idx) => {
                              // Extract caller info
                              const callerName = event.source || event.title?.replace('Appel ', '').split(' - ')[0];
                              const callerRole = event.title?.includes('-') ? event.title.split(' - ')[1] : event.source;
                              return (
                                <motion.button
                                  key={idx}
                                  whileHover={{ scale: 1.02 }}
                                  whileTap={{ scale: 0.98 }}
                                  onClick={() => {
                                    setOutgoingCallContact({
                                      name: callerName,
                                      role: callerRole,
                                      event: event
                                    });
                                  }}
                                  className="p-2 bg-secondary/50 hover:bg-secondary rounded-lg border border-border hover:border-blue-500/50 transition-colors text-left"
                                >
                                  <div className="flex items-center gap-2">
                                    <div className="w-8 h-8 rounded-full bg-blue-600/30 flex items-center justify-center">
                                      <Phone className="w-4 h-4 text-blue-400" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-xs font-semibold truncate">{callerName}</p>
                                      <p className="text-[10px] text-muted-foreground truncate">{callerRole}</p>
                                    </div>
                                  </div>
                                </motion.button>
                              );
                            })
                            ?.filter((v, i, a) => a.findIndex(t => t.key === v.key) === i) // Remove duplicates
                            ?.slice(0, 9) // Limit to 9 contacts
                          }
                        </div>
                      </div>

                      {/* Call History */}
                      <div className="flex items-center gap-2 mb-4">
                        <PhoneCall className="w-5 h-5 text-blue-400" />
                        <h3 className="font-bold text-blue-400">Historique des appels</h3>
                      </div>
                      {callTranscripts.length === 0 ? (
                        <div className="text-center py-8">
                          <Phone className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                          <p className="text-muted-foreground">Aucun appel pour l'instant</p>
                          <p className="text-xs text-muted-foreground mt-2">Les appels entrants et sortants apparaîtront ici</p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {callTranscripts.map((call, idx) => (
                            <motion.div
                              key={idx}
                              initial={{ opacity: 0, x: -20 }}
                              animate={{ opacity: 1, x: 0 }}
                              className={`p-4 rounded-lg border ${
                                call.direction === 'outgoing' 
                                  ? 'bg-blue-950/20 border-blue-500/50' 
                                  : call.status === 'answered' 
                                    ? 'bg-green-950/20 border-green-500/50' 
                                    : call.status === 'missed' 
                                      ? 'bg-red-950/20 border-red-500/50' 
                                      : 'bg-secondary/50 border-border'
                              }`}
                            >
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  {call.direction === 'outgoing' ? (
                                    <PhoneOutgoing className="w-5 h-5 text-blue-400" />
                                  ) : call.status === 'answered' ? (
                                    <PhoneIncoming className="w-5 h-5 text-green-400" />
                                  ) : call.status === 'missed' ? (
                                    <PhoneMissed className="w-5 h-5 text-red-400" />
                                  ) : (
                                    <PhoneOff className="w-5 h-5 text-muted-foreground" />
                                  )}
                                  <span className="font-bold">{call.caller}</span>
                                  <Badge className={
                                    call.direction === 'outgoing' 
                                      ? 'bg-blue-600' 
                                      : call.status === 'answered' 
                                        ? 'bg-green-600' 
                                        : call.status === 'missed' 
                                          ? 'bg-red-600' 
                                          : 'bg-muted'
                                  }>
                                    {call.direction === 'outgoing' ? 'Sortant' : call.status === 'answered' ? 'Répondu' : call.status === 'missed' ? 'Manqué' : 'Refusé'}
                                  </Badge>
                                </div>
                                <span className="text-xs text-muted-foreground">{call.time}</span>
                              </div>
                              <p className="text-sm text-muted-foreground mb-2">{call.callerRole}</p>
                              {call.transcript && (
                                <div className="mt-3 p-3 bg-secondary/30 rounded border border-border">
                                  <p className="text-xs text-muted-foreground mb-1">
                                    {call.direction === 'outgoing' ? 'Votre message :' : 'Votre réponse :'}
                                  </p>
                                  <p className="text-sm italic">"{call.transcript}"</p>
                                </div>
                              )}
                            </motion.div>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : activeTab === 'sms' ? (
                    // SMS/WhatsApp Tab
                    <div className="space-y-4">
                      <div className="flex items-center gap-2 mb-4">
                        <Smartphone className="w-5 h-5 text-green-400" />
                        <h3 className="font-bold text-green-400">SMS & WhatsApp</h3>
                      </div>
                      {filterMessages('sms').length === 0 ? (
                        <div className="text-center py-8">
                          <Smartphone className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                          <p className="text-muted-foreground">Aucun SMS/WhatsApp reçu</p>
                          <p className="text-xs text-muted-foreground mt-2">Les messages courts apparaîtront ici</p>
                        </div>
                      ) : (
                        filterMessages('sms').map((msg, i) => (
                          <motion.div
                            key={msg.id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className={`p-3 rounded-lg cursor-pointer ${msg.sender?.toLowerCase()?.includes('whatsapp') ? 'bg-green-950/30 border border-green-500/50' : 'bg-blue-950/30 border border-blue-500/50'}`}
                            onClick={() => setSelectedMessage(msg)}
                          >
                            <div className="flex items-center gap-2 mb-2">
                              {msg.sender?.toLowerCase()?.includes('whatsapp') ? (
                                <span className="text-lg">💬</span>
                              ) : (
                                <MessageSquare className="w-4 h-4 text-blue-400" />
                              )}
                              <span className="font-bold text-sm">{msg.sender}</span>
                              <span className="text-xs text-muted-foreground ml-auto">{msg.scenario_time}</span>
                            </div>
                            <p className="text-sm">{msg.content}</p>
                          </motion.div>
                        ))
                      )}
                    </div>
                  ) : filterMessages(activeTab).length === 0 ? (
                    <div className="text-center py-12">
                      <Clock className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground">En attente de messages...</p>
                    </div>
                  ) : (
                    filterMessages(activeTab).map((msg, i) => (
                      <motion.div
                        key={msg.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className={`message-card ${!msg.is_read ? 'unread' : ''} ${msg.message_type === 'flash_info' || msg.message_type === 'radio_flash' ? 'alert' : ''} ${msg.message_type === 'internal_flash' ? 'border-blue-500/50 bg-blue-950/20' : ''} cursor-pointer mb-2 hover:border-primary/50 transition-colors`}
                        onClick={() => setSelectedMessage(msg)}
                      >
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div className="flex items-center gap-2">
                            {/* Internal flash shared by DirCom */}
                            {msg.message_type === 'internal_flash' ? (
                              <>
                                <Mail className="w-4 h-4 text-blue-400" />
                                <Radio className="w-3 h-3 text-red-400" />
                              </>
                            ) : msg.message_type === 'social_post' ? (
                              <SocialLogo platform={msg.sender} size="sm" />
                            ) : (msg.message_type === 'flash_info' || msg.message_type === 'radio_flash' || msg.message_type === 'media_arrival' || msg.message_type === 'email' && (msg.sender?.includes('AFP') || msg.sender?.includes('BFM') || msg.sender?.includes('France'))) ? (
                              <MediaLogo source={msg.sender} size="sm" />
                            ) : (
                              platformIcons[msg.message_type] || <Terminal className="w-4 h-4" />
                            )}
                            <span className={`font-mono ${textSize === 'xlarge' ? 'text-sm' : 'text-xs'} ${msg.message_type === 'internal_flash' ? 'text-blue-400' : 'text-muted-foreground'}`}>{msg.sender}</span>
                          </div>
                          <span className="font-mono text-[10px] text-muted-foreground">{msg.scenario_time}</span>
                        </div>
                        {msg.subject && (
                          <h4 className={`${textSize === 'normal' ? 'text-sm' : textSize === 'large' ? 'text-base' : 'text-lg'} font-semibold mb-1 text-primary`}>{msg.subject}</h4>
                        )}
                        <p className={`${textSize === 'normal' ? 'text-sm' : textSize === 'large' ? 'text-base' : 'text-lg'} text-foreground/80 line-clamp-3`}>{msg.content}</p>
                        
                        {/* Social engagement metrics */}
                        {msg.message_type === 'social_post' && msg.metadata && (
                          <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                            {msg.metadata.likes && (
                              <span className="flex items-center gap-1">
                                <Heart className="w-3 h-3 text-red-400" /> {msg.metadata.likes > 1000 ? `${(msg.metadata.likes/1000).toFixed(1)}K` : msg.metadata.likes}
                              </span>
                            )}
                            {msg.metadata.retweets && (
                              <span className="flex items-center gap-1">
                                <Share2 className="w-3 h-3 text-green-400" /> {msg.metadata.retweets}
                              </span>
                            )}
                            {msg.metadata.comments && (
                              <span className="flex items-center gap-1">
                                <MessageCircle className="w-3 h-3 text-blue-400" /> {msg.metadata.comments}
                              </span>
                            )}
                            {msg.metadata.shares && (
                              <span className="flex items-center gap-1">
                                <Share2 className="w-3 h-3 text-primary" /> {msg.metadata.shares}
                              </span>
                            )}
                            {msg.metadata.angry_reactions && (
                              <span className="flex items-center gap-1">
                                😡 {msg.metadata.angry_reactions}
                              </span>
                            )}
                          </div>
                        )}
                        
                        {/* Action button for radio flash - LISTEN button */}
                        {(msg.message_type === 'flash_info' || msg.message_type === 'radio_flash') && (
                          <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={(e) => {
                              e.stopPropagation();
                              // Play this flash
                              setRadioFlash({
                                station: msg.sender || "France Info",
                                content: msg.content,
                                scenarioTime: msg.scenario_time
                              });
                              setIsListeningRadio(true);
                            }}
                            className="w-full mt-3 py-2 bg-gradient-to-r from-red-700 to-red-600 hover:from-red-600 hover:to-red-500 text-white rounded-lg font-bold text-sm flex items-center justify-center gap-2"
                          >
                            <Volume2 className="w-4 h-4" />
                            ÉCOUTER LA RADIO
                          </motion.button>
                        )}
                        
                        <div className="flex items-center justify-between mt-2">
                          <Badge className={`badge-matrix ${textSize !== 'normal' ? 'text-xs px-2' : 'text-[10px]'} ${msg.message_type === 'internal_flash' ? 'bg-blue-600 text-white' : ''} ${msg.message_type === 'flash_info' || msg.message_type === 'radio_flash' ? 'bg-red-600 text-white' : ''}`}>
                            {msg.message_type === 'internal_flash' ? '📻 FLASH PARTAGÉ' : 
                             msg.message_type === 'flash_info' || msg.message_type === 'radio_flash' ? '📻 FLASH RADIO' : 
                             msg.message_type}
                          </Badge>
                          {msg.message_type !== 'flash_info' && msg.message_type !== 'radio_flash' && (
                            <span className={`${textSize === 'xlarge' ? 'text-xs' : 'text-[10px]'} ${msg.message_type === 'internal_flash' ? 'text-blue-400' : 'text-primary'} font-mono flex items-center gap-1`}>
                              {msg.message_type === 'internal_flash' ? (
                                <><Play className="w-3 h-3" /> Écouter</>
                              ) : (
                                <><Eye className="w-3 h-3" /> Tap pour lire</>
                              )}
                            </span>
                          )}
                        </div>
                      </motion.div>
                    ))
                  )}
              </AnimatePresence>
              </ScrollArea>
            </div>

            {/* Team Chat - Always visible */}
            <div className="crisis-panel p-4">
              <h3 className="heading text-sm text-primary mb-3 flex items-center gap-2">
                <Users className="w-4 h-4" /> Messagerie équipe COMEX
              </h3>
              <div className="p-3 bg-secondary/30 border border-border text-sm space-y-2 max-h-40 overflow-y-auto">
                {comexChat.map(msg => (
                  <p key={msg.id}>
                    <span className="text-primary font-semibold">{msg.sender}:</span>{' '}
                    <span className={msg.sender.includes('Vous') ? 'text-green-400' : ''}>{msg.text}</span>
                    <span className="text-muted-foreground text-xs ml-2">[{msg.time}]</span>
                  </p>
                ))}
                {/* Show internal flash shares */}
                {internalMessages.map(msg => (
                  <p key={msg.id} className="text-blue-400 cursor-pointer hover:text-blue-300" onClick={() => setSelectedMessage(msg)}>
                    <span className="font-semibold">{msg.sender}:</span> 📻 <span className="underline">Flash radio partagé - cliquez pour écouter</span>
                  </p>
                ))}
              </div>
              <div className="flex gap-2 mt-3">
                <Input 
                  className="input-matrix flex-1" 
                  placeholder="Écrire à l'équipe..."
                  value={comexMessage}
                  onChange={(e) => setComexMessage(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      sendComexMessage();
                    }
                  }}
                />
                <Button 
                  size="sm" 
                  className="bg-primary text-primary-foreground"
                  onClick={sendComexMessage}
                  disabled={!comexMessage.trim()}
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
      </div>
      {/* Close Main Content Area div */}
      </div>
    </div>
  );
};

// Demo Admin Dashboard - MASTER CONTROLLER
// Admin controls: start/stop, time, trigger events, supervise simulation
export const DemoAdminDashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [scenario, setScenario] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const [scenarioTime, setScenarioTime] = useState("08:00");
  const [triggeredEvents, setTriggeredEvents] = useState([]);
  const [customEvent, setCustomEvent] = useState({ type: 'flash_info', title: '', content: '' });
  const [activeTab, setActiveTab] = useState("control"); // control, bank, players, generator
  
  // Detect if we're in an iframe (multi-view mode) for compact display
  const isInIframe = window.self !== window.top;
  
  // Demo sync hook - admin is the MASTER controller
  const { sessionId, isConnected, syncState, sendTimeUpdate, sendRunningUpdate, sendTriggerEvent, isSynced } = useDemoSync('admin');
  
  // Sync initial state from session when connecting
  useEffect(() => {
    if (syncState && isSynced) {
      // Sync time from session if available
      if (syncState.time && syncState.time !== scenarioTime) {
        console.log(`[Admin] Syncing time from session: ${syncState.time}`);
        setScenarioTime(syncState.time);
      }
      // Sync running state
      if (syncState.is_running !== undefined && syncState.is_running !== isRunning) {
        console.log(`[Admin] Syncing running state: ${syncState.is_running}`);
        setIsRunning(syncState.is_running);
      }
      // Sync triggered events
      if (syncState.triggered_events?.length > 0 && triggeredEvents.length === 0) {
        console.log(`[Admin] Syncing ${syncState.triggered_events.length} triggered events`);
        setTriggeredEvents(syncState.triggered_events);
      }
    }
  }, [syncState, isSynced]);
  
  // Event categories for the control panel
  const eventCategories = [
    { id: 'flash_info', label: '📻 Flash Radio', color: 'bg-red-600' },
    { id: 'social_post', label: '📱 Post Réseaux', color: 'bg-blue-600' },
    { id: 'email', label: '📧 Email', color: 'bg-green-600' },
    { id: 'phone_call', label: '📞 Appel', color: 'bg-amber-600' },
    { id: 'sms', label: '💬 SMS', color: 'bg-purple-600' },
    { id: 'tv_report', label: '📺 Reportage TV', color: 'bg-pink-600' }
  ];

  // Handle inject input from bank
  const handleInjectInput = (input) => {
    const eventWithTime = { 
      ...input, 
      triggeredAt: scenarioTime, 
      id: Date.now(),
      event_type: input.input_type || input.type,
      title: input.title || input.caller_role || input.source || "Input injecté",
      content: input.content || input.script || input.body || ""
    };
    setTriggeredEvents(prev => [...prev, eventWithTime]);
    
    // Broadcast to synced clients
    if (isSynced) {
      sendTriggerEvent(eventWithTime);
    }
  };

  // Load scenario
  useEffect(() => {
    const loadScenario = async () => {
      try {
        const res = await axios.get(`${API}/scenarios`);
        const scenarioId = searchParams.get('scenarioId');
        let selected;
        if (scenarioId) {
          selected = res.data.scenarios?.find(s => s.id === scenarioId);
        }
        if (!selected && res.data.scenarios?.length > 0) {
          selected = res.data.scenarios[0];
        }
        if (selected) {
          setScenario(selected);
        }
      } catch (err) {
        console.error(err);
      }
    };
    loadScenario();
  }, [searchParams]);

  // Timer - when running, update time and broadcast to sync
  useEffect(() => {
    if (!isRunning) return;
    const interval = setInterval(() => {
      setScenarioTime(prev => {
        const [h, m] = prev.split(':').map(Number);
        const totalMins = h * 60 + m + 1;
        const newTime = `${String(Math.floor(totalMins / 60)).padStart(2, '0')}:${String(totalMins % 60).padStart(2, '0')}`;
        // Broadcast to synced clients
        if (isSynced) {
          sendTimeUpdate(newTime);
        }
        return newTime;
      });
    }, 2000); // 2 seconds = 1 minute in scenario time (x30 acceleration)
    return () => clearInterval(interval);
  }, [isRunning, isSynced, sendTimeUpdate]);

  // AUTO-TRIGGER EVENTS based on time_offset_minutes
  // This ref tracks which events have been auto-triggered to avoid duplicates
  const autoTriggeredEventsRef = useRef(new Set());
  
  useEffect(() => {
    if (!isRunning || !scenario?.events) return;
    
    // Calculate current elapsed minutes since 08:00
    const [h, m] = scenarioTime.split(':').map(Number);
    const currentMinutes = (h * 60 + m) - 480; // 480 = 08:00
    
    // Find events that should trigger now
    scenario.events.forEach(event => {
      const eventKey = `auto_${event.id || event.title}_${event.time_offset_minutes}`;
      
      // Skip if already triggered
      if (autoTriggeredEventsRef.current.has(eventKey)) return;
      if (triggeredEvents.some(te => te.id === event.id || (te.title === event.title && te.time_offset_minutes === event.time_offset_minutes))) return;
      
      // Trigger if we've reached or passed the event time
      if (event.time_offset_minutes <= currentMinutes) {
        autoTriggeredEventsRef.current.add(eventKey);
        
        // Trigger the event
        const eventWithTime = { 
          ...event, 
          triggeredAt: scenarioTime, 
          id: event.id || Date.now(),
          autoTriggered: true 
        };
        setTriggeredEvents(prev => [...prev, eventWithTime]);
        
        // Broadcast to synced clients
        if (isSynced) {
          sendTriggerEvent(eventWithTime);
        }
        
        console.log(`[Admin] Auto-triggered event at +${event.time_offset_minutes}min: ${event.title}`);
      }
    });
  }, [scenarioTime, isRunning, scenario, isSynced, sendTriggerEvent, triggeredEvents]);

  // Handle start/stop with sync
  const handleToggleRunning = () => {
    const newRunning = !isRunning;
    setIsRunning(newRunning);
    if (isSynced) {
      sendRunningUpdate(newRunning);
    }
  };

  const triggerEvent = (event) => {
    const eventWithTime = { ...event, triggeredAt: scenarioTime, id: Date.now() };
    setTriggeredEvents(prev => [...prev, eventWithTime]);
    
    // Broadcast to synced clients
    if (isSynced) {
      sendTriggerEvent(eventWithTime);
    }
    
    toast.success(`Événement déclenché : ${event.title || event.event_type}`);
  };

  const injectCustomEvent = () => {
    if (!customEvent.title || !customEvent.content) {
      toast.error("Remplissez le titre et le contenu");
      return;
    }
    triggerEvent({
      event_type: customEvent.type,
      title: customEvent.title,
      content: customEvent.content,
      source: "Injection manuelle"
    });
    setCustomEvent({ type: 'flash_info', title: '', content: '' });
  };

  const upcomingEvents = scenario?.events?.filter(e => {
    const currentMinutes = parseInt(scenarioTime.split(':')[0]) * 60 + parseInt(scenarioTime.split(':')[1]) - 480;
    return e.time_offset_minutes > currentMinutes && e.time_offset_minutes <= currentMinutes + 15;
  }).slice(0, 8) || [];

  return (
    <div className={`${isInIframe ? 'h-full overflow-hidden flex flex-col' : 'min-h-screen'} matrix-bg`}>
      <ScanlineOverlay />
      
      {/* Header - Compact in iframe mode */}
      <div className={`crisis-header shrink-0 ${isInIframe ? 'py-1 px-2' : ''}`}>
        <div className="flex items-center gap-2">
          <Badge className={`badge-matrix bg-destructive text-white ${isInIframe ? 'text-xs px-1 py-0.5' : ''}`}>ADMIN</Badge>
          {isSynced && (
            <Badge className={`bg-green-600 text-white animate-pulse ${isInIframe ? 'text-xs px-1 py-0.5' : ''}`}>
              🔗 SYNC: {sessionId}
            </Badge>
          )}
          <div>
            <h1 className={`heading text-destructive ${isInIframe ? 'text-sm' : 'text-lg'}`}>Panneau Admin - Maître du Jeu</h1>
            <p className={`font-mono text-muted-foreground ${isInIframe ? 'text-[10px]' : 'text-xs'}`}>
              {isSynced ? `Session synchronisée - ${syncState?.connected_dashboards?.length || 1} dashboard(s) connecté(s)` : "Mode Démo - Contrôle simulation"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-center">
            <span className={`font-mono text-muted-foreground block ${isInIframe ? 'text-[10px]' : 'text-xs'}`}>HEURE SCÉNARIO</span>
            <span className={`digital-clock ${isInIframe ? 'text-lg' : 'text-2xl'}`}>{scenarioTime}</span>
          </div>
          <Button
            variant={isRunning ? "destructive" : "default"}
            onClick={handleToggleRunning}
            className={`${isRunning ? "" : "bg-green-600 hover:bg-green-700"} ${isInIframe ? 'text-xs px-2 py-1 h-7' : ''}`}
            size={isInIframe ? "sm" : "default"}
          >
            {isRunning ? <Pause className={`${isInIframe ? 'w-3 h-3' : 'w-4 h-4'} mr-1`} /> : <Play className={`${isInIframe ? 'w-3 h-3' : 'w-4 h-4'} mr-1`} />}
            {isRunning ? 'PAUSE' : 'DÉMARRER'}
          </Button>
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => navigate("/demo")}
            className={`text-muted-foreground hover:text-destructive ${isInIframe ? 'h-7 w-7 p-0' : ''}`}
          >
            <X className={`${isInIframe ? 'w-3 h-3' : 'w-4 h-4'}`} />
            {!isInIframe && <span className="hidden sm:inline ml-1">Quitter</span>}
          </Button>
        </div>
      </div>

      {/* BIG START BUTTON - Shows when not running */}
      {!isRunning && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center">
          <div className={`text-center ${isInIframe ? 'p-4' : 'p-8'}`}>
            <div className={isInIframe ? 'mb-3' : 'mb-6'}>
              <Badge className={`badge-matrix bg-destructive text-white ${isInIframe ? 'text-sm px-2 py-1 mb-2' : 'text-xl px-4 py-2 mb-4'}`}>
                PANNEAU ADMIN
              </Badge>
              <h1 className={`font-bold text-white ${isInIframe ? 'text-xl mb-2' : 'text-4xl md:text-6xl mb-4'}`}>
                {scenario?.name || "Crisis Simulator"}
              </h1>
              <p className={`text-gray-400 ${isInIframe ? 'text-sm mb-1' : 'text-xl mb-2'}`}>
                {isSynced ? `${syncState?.connected_dashboards?.length || 1} écran(s) connecté(s)` : "Mode démonstration"}
              </p>
              <p className={`text-gray-500 ${isInIframe ? 'text-xs' : 'text-lg'}`}>
                Heure de début : {scenarioTime}
              </p>
            </div>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleToggleRunning}
              className={`bg-green-600 hover:bg-green-500 text-white font-bold rounded-xl shadow-2xl shadow-green-600/50 flex items-center gap-2 mx-auto ${isInIframe ? 'text-lg px-6 py-3' : 'text-3xl md:text-4xl px-12 py-6 gap-4'}`}
            >
              <Play className={isInIframe ? 'w-5 h-5' : 'w-10 h-10 md:w-12 md:h-12'} />
              LANCER LA DÉMO
            </motion.button>
            
            <p className={`text-gray-500 ${isInIframe ? 'text-xs mt-3' : 'text-sm mt-6'}`}>
              Cliquez pour démarrer la simulation sur tous les écrans
            </p>
          </div>
        </div>
      )}

      {/* Main Content - Only visible when running */}
      <div className={`${isInIframe ? 'flex-1 overflow-hidden p-2' : 'p-4'} ${!isRunning ? 'opacity-20' : ''}`}>
        {/* Tab Navigation */}
        <div className={`flex items-center gap-1 overflow-x-auto shrink-0 ${isInIframe ? 'mb-2 pb-1' : 'mb-4 pb-2'}`}>
          <Button
            size="sm"
            variant={activeTab === "control" ? "default" : "outline"}
            onClick={() => setActiveTab("control")}
            className={`${activeTab === "control" ? "bg-destructive" : ""} ${isInIframe ? 'h-7 text-xs px-2' : ''}`}
          >
            <LayoutGrid className={`${isInIframe ? 'w-3 h-3 mr-1' : 'w-4 h-4 mr-2'}`} /> Contrôle
          </Button>
          <Button
            size="sm"
            variant={activeTab === "bank" ? "default" : "outline"}
            onClick={() => setActiveTab("bank")}
            className={`${activeTab === "bank" ? "bg-amber-600" : ""} ${isInIframe ? 'h-7 text-xs px-2' : ''}`}
          >
            <Database className={`${isInIframe ? 'w-3 h-3 mr-1' : 'w-4 h-4 mr-2'}`} /> Banque d'Inputs
          </Button>
          <Button
            size="sm"
            variant={activeTab === "players" ? "default" : "outline"}
            onClick={() => setActiveTab("players")}
            className={`${activeTab === "players" ? "bg-green-600" : ""} ${isInIframe ? 'h-7 text-xs px-2' : ''}`}
          >
            <Users className={`${isInIframe ? 'w-3 h-3 mr-1' : 'w-4 h-4 mr-2'}`} /> Joueurs
          </Button>
          <Button
            size="sm"
            variant={activeTab === "generator" ? "default" : "outline"}
            onClick={() => setActiveTab("generator")}
            className={`${activeTab === "generator" ? "bg-purple-600" : ""} ${isInIframe ? 'h-7 text-xs px-2' : ''}`}
          >
            <Zap className={`${isInIframe ? 'w-3 h-3 mr-1' : 'w-4 h-4 mr-2'}`} /> Générateur
          </Button>
        </div>

        {/* Tab Content */}
        {activeTab === "control" && (
          <div className={`grid grid-cols-1 lg:grid-cols-3 ${isInIframe ? 'gap-2' : 'gap-4'}`}>
            {/* Left Column - Quick Actions */}
            <div className={isInIframe ? 'space-y-2' : 'space-y-4'}>
              <div className={`crisis-panel ${isInIframe ? 'p-2' : 'p-4'}`}>
                <h3 className={`heading text-destructive flex items-center gap-1 ${isInIframe ? 'text-xs mb-2' : 'text-sm mb-4'}`}>
                  <Zap className={`${isInIframe ? 'w-3 h-3' : 'w-4 h-4'}`} /> ACTIONS RAPIDES
                </h3>
                <div className="grid grid-cols-2 gap-2">
                  {eventCategories.map(cat => (
                    <Button
                      key={cat.id}
                      size="sm"
                      className={`${cat.color} text-white hover:opacity-80`}
                      onClick={() => {
                        const randomEvent = scenario?.events?.find(e => e.event_type === cat.id);
                        if (randomEvent) triggerEvent(randomEvent);
                        else toast.info(`Pas d'événement ${cat.label} disponible`);
                      }}
                    >
                      {cat.label}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Custom Event Injection - Hide in iframe mode to save space */}
              {!isInIframe && (
                <div className="crisis-panel p-4">
                  <h3 className="heading text-sm text-destructive mb-4 flex items-center gap-2">
                    <PenLine className="w-4 h-4" /> INJECTION MANUELLE
                  </h3>
                  <div className="space-y-3">
                    <select 
                      className="w-full bg-secondary border border-border rounded p-2 text-sm"
                      value={customEvent.type}
                      onChange={(e) => setCustomEvent({...customEvent, type: e.target.value})}
                    >
                      {eventCategories.map(cat => (
                        <option key={cat.id} value={cat.id}>{cat.label}</option>
                      ))}
                    </select>
                    <Input
                      placeholder="Titre de l'événement"
                      value={customEvent.title}
                      onChange={(e) => setCustomEvent({...customEvent, title: e.target.value})}
                      className="input-matrix"
                    />
                    <Textarea
                      placeholder="Contenu de l'événement..."
                      value={customEvent.content}
                      onChange={(e) => setCustomEvent({...customEvent, content: e.target.value})}
                      className="input-matrix"
                      rows={3}
                    />
                    <Button onClick={injectCustomEvent} className="w-full bg-destructive hover:bg-destructive/80">
                      <Send className="w-4 h-4 mr-2" /> Injecter
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Middle Column - Timeline */}
            <div className={`crisis-panel ${isInIframe ? 'p-2' : 'p-4'}`}>
              <h3 className={`heading text-destructive flex items-center gap-1 ${isInIframe ? 'text-xs mb-2' : 'text-sm mb-4'}`}>
                <Clock className={`${isInIframe ? 'w-3 h-3' : 'w-4 h-4'}`} /> ÉVÉNEMENTS À VENIR
              </h3>
              <ScrollArea className={isInIframe ? 'h-[200px]' : 'h-[calc(100vh-350px)]'}>
                {upcomingEvents.length === 0 ? (
                  <div className={`text-center text-muted-foreground ${isInIframe ? 'py-4' : 'py-8'}`}>
                    <Clock className={`mx-auto opacity-50 ${isInIframe ? 'w-5 h-5 mb-1' : 'w-8 h-8 mb-2'}`} />
                    <p className={isInIframe ? 'text-[10px]' : 'text-sm'}>Aucun événement à venir</p>
                  </div>
                ) : (
                  <div className="space-y-1">
                    {upcomingEvents.map((event, i) => (
                      <div 
                        key={i}
                        className={`${isInIframe ? 'p-1.5' : 'p-3'} bg-secondary/30 rounded border border-border hover:border-destructive/50 cursor-pointer transition-colors`}
                        onClick={() => triggerEvent(event)}
                      >
                        <div className="flex items-center justify-between mb-0.5">
                          <Badge className={`badge-matrix ${isInIframe ? 'text-[8px] px-1 py-0' : 'text-xs'}`}>{event.event_type}</Badge>
                          <span className={`text-muted-foreground ${isInIframe ? 'text-[9px]' : 'text-xs'}`}>+{event.time_offset_minutes}m</span>
                        </div>
                        <p className={`font-bold truncate ${isInIframe ? 'text-[10px]' : 'text-sm'}`}>{event.title}</p>
                        {!isInIframe && <p className="text-xs text-muted-foreground truncate">{event.content?.substring(0, 60)}...</p>}
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </div>

            {/* Right Column - Triggered Events Log */}
            <div className="crisis-panel p-4">
              <h3 className="heading text-sm text-destructive mb-4 flex items-center gap-2">
                <Activity className="w-4 h-4" /> ÉVÉNEMENTS DÉCLENCHÉS ({triggeredEvents.length})
              </h3>
              <ScrollArea className="h-[calc(100vh-350px)]">
                {triggeredEvents.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Activity className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Aucun événement déclenché</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {triggeredEvents.slice().reverse().map((event) => (
                      <div key={event.id} className="p-2 bg-green-900/20 rounded border border-green-500/30">
                        <div className="flex items-center justify-between">
                          <Badge className="bg-green-600 text-white text-xs">{event.event_type}</Badge>
                          <span className="text-xs text-green-400">{event.triggeredAt}</span>
                        </div>
                        <p className="text-xs mt-1 truncate">{event.title}</p>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </div>
          </div>
        )}

        {/* Inputs Bank Tab */}
        {activeTab === "bank" && (
          <div className="crisis-panel p-4">
            <InputsBankBrowser 
              onInjectInput={handleInjectInput}
              sessionId={sessionId}
            />
          </div>
        )}

        {/* Players Configuration Tab */}
        {activeTab === "players" && (
          <div className="crisis-panel p-4">
            <PlayerConfiguration 
              sessionId={sessionId}
              onConfigSaved={(players, media) => {
                toast.success(`Configuration sauvegardée: ${players.length} joueurs, ${media.length} médias locaux`);
              }}
            />
          </div>
        )}

        {/* Generator Tab */}
        {activeTab === "generator" && (
          <div className="crisis-panel p-4">
            <InputsGenerator 
              onInputsGenerated={(inputs) => {
                toast.success("Inputs générés et prêts à être utilisés dans la banque");
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

// Demo Observer Dashboard - Read-only dashboard for DRH/CEO
// Demo Observer Dashboard - READ-ONLY dashboard for DRH/CEO
export const DemoObserverDashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [scenarioTime, setScenarioTime] = useState("08:00");
  const [isRunning, setIsRunning] = useState(false);
  const [scenario, setScenario] = useState(null);
  const [messages, setMessages] = useState([]);
  
  // Detect if we're in an iframe (multi-view mode) for compact display
  const isInIframe = window.self !== window.top;
  
  // Demo sync hook - observer LISTENS to admin
  const { sessionId, isConnected, syncState, isSynced } = useDemoSync('observer');

  // Sync state from admin
  useEffect(() => {
    if (syncState) {
      if (syncState.time) setScenarioTime(syncState.time);
      if (syncState.is_running !== undefined) setIsRunning(syncState.is_running);
      // Process triggered events
      if (syncState.triggered_events?.length > 0) {
        setMessages(syncState.triggered_events);
      }
    }
  }, [syncState]);

  // Load scenario
  useEffect(() => {
    const loadScenario = async () => {
      try {
        const res = await axios.get(`${API}/scenarios`);
        const scenarioId = searchParams.get('scenarioId');
        let selected;
        if (scenarioId) {
          selected = res.data.scenarios?.find(s => s.id === scenarioId);
        }
        if (!selected && res.data.scenarios?.length > 0) {
          selected = res.data.scenarios[0];
        }
        if (selected) {
          setScenario(selected);
        }
      } catch (err) {
        console.error(err);
      }
    };
    loadScenario();
  }, [searchParams]);

  // Stats (simulated for demo)
  const stats = {
    totalEvents: messages.length,
    radioFlashes: messages.filter(m => m.event_type === 'flash_info').length,
    socialPosts: messages.filter(m => m.event_type === 'social_post').length,
    phoneCalls: messages.filter(m => m.event_type === 'phone_call').length,
    emails: messages.filter(m => m.event_type === 'email').length
  };

  // Calculate elapsed time
  const getElapsedMinutes = () => {
    const [h, m] = scenarioTime.split(':').map(Number);
    return (h * 60 + m) - 480; // 480 = 08:00
  };

  return (
    <div className={`${isInIframe ? 'h-full overflow-hidden flex flex-col' : 'min-h-screen'} matrix-bg`}>
      <ScanlineOverlay />
      
      {/* Header - Compact in iframe mode */}
      <div className={`crisis-header shrink-0 ${isInIframe ? 'py-1 px-2' : ''}`}>
        <div className="flex items-center gap-2">
          <Badge className={`badge-matrix bg-blue-600 text-white ${isInIframe ? 'text-xs px-1 py-0.5' : ''}`}>OBSERVATEUR</Badge>
          {isSynced && (
            <Badge className={`bg-green-600 text-white ${isInIframe ? 'text-xs px-1 py-0.5' : ''}`}>
              🔗 {isInIframe ? 'Synchronisé' : 'Connecté'}
            </Badge>
          )}
          <div>
            <h1 className={`heading text-blue-400 ${isInIframe ? 'text-sm' : 'text-lg'}`}>Dashboard Observateur</h1>
            <p className={`font-mono text-muted-foreground ${isInIframe ? 'text-[10px]' : 'text-xs'}`}>Lecture seule - {scenario?.name || "Chargement..."}</p>
          </div>
        </div>
        <div className={`flex items-center ${isInIframe ? 'gap-1' : 'gap-4'}`}>
          <div className="text-center">
            <span className={`font-mono text-muted-foreground block ${isInIframe ? 'text-[9px]' : 'text-xs'}`}>HEURE SCÉNARIO</span>
            <span className={`digital-clock ${isInIframe ? 'text-lg' : 'text-2xl'}`}>{scenarioTime}</span>
          </div>
          {isSynced ? (
            <Badge className={`${isRunning ? "bg-green-600" : "bg-amber-600"} ${isInIframe ? 'text-[9px] px-1' : ''}`}>
              {isRunning ? (isInIframe ? "🔄" : "🔄 Synchronisé") : (isInIframe ? "⏸️" : "⏸️ En attente Admin")}
            </Badge>
          ) : (
            <Badge className={`bg-gray-600 ${isInIframe ? 'text-[9px] px-1' : ''}`}>Local</Badge>
          )}
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => navigate("/demo")}
            className={`text-muted-foreground hover:text-destructive ${isInIframe ? 'h-6 w-6 p-0' : ''}`}
          >
            <X className={`${isInIframe ? 'w-3 h-3' : 'w-4 h-4'}`} />
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className={`${isInIframe ? 'flex-1 overflow-hidden p-2' : 'p-4'} grid grid-cols-1 lg:grid-cols-3 gap-2`}>
        {/* KPIs */}
        <div className={`crisis-panel ${isInIframe ? 'p-2' : 'p-4'}`}>
          <h3 className={`heading text-blue-400 flex items-center gap-1 ${isInIframe ? 'text-[10px] mb-2' : 'text-sm mb-4'}`}>
            <Activity className={`${isInIframe ? 'w-3 h-3' : 'w-4 h-4'}`} /> MÉTRIQUES EN TEMPS RÉEL
          </h3>
          <div className="grid grid-cols-2 gap-2">
            <div className={`${isInIframe ? 'p-1.5' : 'p-3'} bg-blue-900/30 rounded text-center border border-blue-500/30`}>
              <p className={`font-bold text-blue-400 ${isInIframe ? 'text-lg' : 'text-3xl'}`}>{getElapsedMinutes()}</p>
              <p className={`text-muted-foreground ${isInIframe ? 'text-[9px]' : 'text-xs'}`}>Minutes écoulées</p>
            </div>
            <div className={`${isInIframe ? 'p-1.5' : 'p-3'} bg-green-900/30 rounded text-center border border-green-500/30`}>
              <p className={`font-bold text-green-400 ${isInIframe ? 'text-lg' : 'text-3xl'}`}>{stats.totalEvents}</p>
              <p className={`text-muted-foreground ${isInIframe ? 'text-[9px]' : 'text-xs'}`}>Événements</p>
            </div>
            <div className={`${isInIframe ? 'p-1.5' : 'p-3'} bg-red-900/30 rounded text-center border border-red-500/30`}>
              <p className={`font-bold text-red-400 ${isInIframe ? 'text-lg' : 'text-2xl'}`}>{stats.radioFlashes}</p>
              <p className={`text-muted-foreground ${isInIframe ? 'text-[9px]' : 'text-xs'}`}>Flashes Radio</p>
            </div>
            <div className={`${isInIframe ? 'p-1.5' : 'p-3'} bg-amber-900/30 rounded text-center border border-amber-500/30`}>
              <p className={`font-bold text-amber-400 ${isInIframe ? 'text-lg' : 'text-2xl'}`}>{stats.phoneCalls}</p>
              <p className={`text-muted-foreground ${isInIframe ? 'text-[9px]' : 'text-xs'}`}>Appels</p>
            </div>
          </div>
          
          {/* Status */}
          <div className={`${isInIframe ? 'mt-2 p-1.5' : 'mt-4 p-3'} bg-secondary/30 rounded border border-border`}>
            <p className={`text-muted-foreground ${isInIframe ? 'text-[9px] mb-0.5' : 'text-xs mb-1'}`}>STATUT SIMULATION</p>
            <div className="flex items-center gap-1">
              <div className={`${isInIframe ? 'w-2 h-2' : 'w-3 h-3'} rounded-full ${isRunning ? 'bg-green-500 animate-pulse' : 'bg-amber-500'}`}></div>
              <span className={`font-semibold ${isInIframe ? 'text-xs' : 'text-sm'}`}>{isRunning ? 'EN COURS' : 'EN PAUSE'}</span>
            </div>
          </div>
        </div>

        {/* Event Timeline */}
        <div className={`lg:col-span-2 crisis-panel ${isInIframe ? 'p-2' : 'p-4'}`}>
          <h3 className={`heading text-blue-400 flex items-center gap-1 ${isInIframe ? 'text-[10px] mb-2' : 'text-sm mb-4'}`}>
            <Clock className={`${isInIframe ? 'w-3 h-3' : 'w-4 h-4'}`} /> FIL D'ÉVÉNEMENTS
          </h3>
          <ScrollArea className={isInIframe ? 'h-[180px]' : 'h-[calc(100vh-280px)]'}>
            {messages.length === 0 ? (
              <div className={`text-center ${isInIframe ? 'py-4' : 'py-12'}`}>
                <Eye className={`${isInIframe ? 'w-6 h-6 mb-2' : 'w-12 h-12 mb-4'} text-muted-foreground mx-auto opacity-50`} />
                <p className={`text-muted-foreground ${isInIframe ? 'text-xs' : ''}`}>En attente d'événements...</p>
              </div>
            ) : (
              <div className="space-y-1">
                {messages.slice().reverse().map((event, i) => (
                  <div key={event.id || i} className={`${isInIframe ? 'p-1.5' : 'p-3'} bg-secondary/30 rounded border border-border`}>
                    <div className="flex items-center justify-between mb-0.5">
                      <Badge className={`badge-matrix ${isInIframe ? 'text-[9px] px-1 py-0' : 'text-xs'}`}>{event.event_type}</Badge>
                      <span className={`text-muted-foreground ${isInIframe ? 'text-[9px]' : 'text-xs'}`}>{event.triggeredAt}</span>
                    </div>
                    <p className={`font-bold ${isInIframe ? 'text-[10px]' : 'text-sm'}`}>{event.title}</p>
                    {!isInIframe && <p className="text-xs text-muted-foreground line-clamp-2 mt-1">{event.content?.substring(0, 100)}...</p>}
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </div>
      </div>
    </div>
  );
};

// Demo Animator Dashboard - TERRAIN TASK DISPLAY
// Simple, large-text display for animators executing field tasks
// Shows: WHO (role), WHEN (time), WHAT (action), WHERE/HOW (location/means), SCRIPT (text to say)
export const DemoAnimatorDashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [scenarioTime, setScenarioTime] = useState("08:00");
  const [isRunning, setIsRunning] = useState(false);
  const [scenario, setScenario] = useState(null);
  const [currentTaskIndex, setCurrentTaskIndex] = useState(0);
  const [taskCompleted, setTaskCompleted] = useState(false);
  
  // Detect if we're in an iframe (multi-view mode) for compact display
  const isInIframe = window.self !== window.top;
  
  // Demo sync hook - animator LISTENS to admin
  const { sessionId, isConnected, syncState, isSynced } = useDemoSync('animator');
  
  // Sync state from admin
  useEffect(() => {
    if (syncState) {
      if (syncState.time) setScenarioTime(syncState.time);
      if (syncState.is_running !== undefined) setIsRunning(syncState.is_running);
    }
  }, [syncState]);

  // Load scenario
  useEffect(() => {
    const loadScenario = async () => {
      try {
        const res = await axios.get(`${API}/scenarios`);
        const scenarioId = searchParams.get('scenarioId');
        let selected;
        if (scenarioId) {
          selected = res.data.scenarios?.find(s => s.id === scenarioId);
        }
        if (!selected) {
          selected = res.data.scenarios?.find(s => s.name === "Incendie Industriel");
        }
        if (selected) setScenario(selected);
      } catch (err) {
        console.error(err);
      }
    };
    loadScenario();
  }, [searchParams]);

  // Sample animator tasks (in real app, these would come from scenario)
  const animatorTasks = [
    {
      role: "Journaliste BFM TV",
      time: "08:15",
      action: "Demander une interview au responsable de crise",
      location: "Accueil de l'entreprise",
      script: "Bonjour, je suis Marie Dupont de BFM TV. Nous avons été alertés d'un incident sur votre site. Pouvez-vous nous confirmer la nature de l'incident et nous accorder une interview ?",
      equipment: ["Badge presse", "Caméra (simulée)", "Micro"]
    },
    {
      role: "Colonel SDIS",
      time: "08:25",
      action: "Appeler le directeur de crise pour informer de l'arrivée des secours",
      location: "Appel téléphonique",
      script: "Bonjour, Colonel Martin du SDIS. Nos équipes sont sur place. Nous avons besoin d'un point de rassemblement et d'un accès au plan des installations. Pouvez-vous nous envoyer votre responsable sécurité ?",
      equipment: ["Téléphone"]
    },
    {
      role: "Manifestant écologiste",
      time: "08:45",
      action: "Simuler une manifestation devant l'entrée principale",
      location: "Parking visiteurs",
      script: "STOP À LA POLLUTION ! ACME ASSASSIN ! Nous exigeons des explications sur cet incident !",
      equipment: ["Mégaphone", "Banderole"]
    },
    {
      role: "Inspecteur DREAL",
      time: "09:00",
      action: "Arriver pour une inspection surprise",
      location: "Entrée principale",
      script: "Bonjour, Inspecteur Moreau de la DREAL. Suite à l'incident signalé, je suis mandaté pour effectuer une inspection de vos installations. J'aurai besoin d'accéder au site et de consulter vos registres de sécurité.",
      equipment: ["Badge officiel", "Bloc-notes"]
    }
  ];

  // Get current task based on scenario time
  const getCurrentTask = () => {
    const currentMinutes = parseInt(scenarioTime.split(':')[0]) * 60 + parseInt(scenarioTime.split(':')[1]);
    
    // Find the next task that should be executed
    for (let i = 0; i < animatorTasks.length; i++) {
      const taskMinutes = parseInt(animatorTasks[i].time.split(':')[0]) * 60 + parseInt(animatorTasks[i].time.split(':')[1]);
      if (taskMinutes >= currentMinutes - 5 && taskMinutes <= currentMinutes + 10) {
        return { task: animatorTasks[i], index: i };
      }
    }
    // If no task in range, show the next upcoming one
    for (let i = 0; i < animatorTasks.length; i++) {
      const taskMinutes = parseInt(animatorTasks[i].time.split(':')[0]) * 60 + parseInt(animatorTasks[i].time.split(':')[1]);
      if (taskMinutes > currentMinutes) {
        return { task: animatorTasks[i], index: i, upcoming: true };
      }
    }
    return { task: animatorTasks[0], index: 0, upcoming: true };
  };

  const { task: currentTask, upcoming: isUpcoming } = getCurrentTask();

  const markTaskDone = () => {
    setTaskCompleted(true);
    toast.success("Tâche marquée comme terminée !");
    setTimeout(() => setTaskCompleted(false), 3000);
  };

  return (
    <div className={`${isInIframe ? 'h-full overflow-hidden' : 'min-h-screen'} bg-black text-white flex flex-col`}>
      {/* Compact Header */}
      <div className={`bg-amber-600 flex items-center justify-between shrink-0 ${isInIframe ? 'px-2 py-1' : 'px-4 py-2'}`}>
        <div className="flex items-center gap-2">
          <Badge className={`bg-black text-amber-400 ${isInIframe ? 'text-xs px-2 py-0.5' : 'text-lg px-3 py-1'}`}>ANIMATEUR</Badge>
          {isSynced && (
            <Badge className={`bg-green-600 text-white ${isInIframe ? 'text-xs px-1 py-0.5' : ''}`}>
              🔗 Connecté
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className="text-center">
            <span className={`text-black/70 block ${isInIframe ? 'text-[10px]' : 'text-xs'}`}>HEURE</span>
            <span className={`text-black font-bold font-mono ${isInIframe ? 'text-lg' : 'text-2xl'}`}>{scenarioTime}</span>
          </div>
          <Button 
            variant="ghost" 
            size="sm"
            className={`text-black hover:bg-black/20 ${isInIframe ? 'h-6 w-6 p-0' : ''}`}
            onClick={() => navigate("/demo")}
          >
            <X className={`${isInIframe ? 'w-3 h-3' : 'w-5 h-5'}`} />
          </Button>
        </div>
      </div>

      {/* Waiting State */}
      {!isRunning && (
        <div className="flex-1 flex items-center justify-center">
          <div className={`text-center ${isInIframe ? 'p-4' : 'p-8'}`}>
            <Clock className={`mx-auto text-amber-500 animate-pulse ${isInIframe ? 'w-12 h-12 mb-3' : 'w-24 h-24 mb-6'}`} />
            <h1 className={`font-bold text-amber-400 ${isInIframe ? 'text-xl mb-2' : 'text-4xl mb-4'}`}>EN ATTENTE</h1>
            <p className={`text-gray-400 ${isInIframe ? 'text-sm' : 'text-xl'}`}>
              La simulation n'a pas encore démarré
            </p>
            <p className={`text-gray-500 ${isInIframe ? 'text-xs mt-1' : 'text-lg mt-2'}`}>
              L'admin doit lancer la simulation
            </p>
          </div>
        </div>
      )}

      {/* Active Task Display */}
      {isRunning && currentTask && (
        <div className={`flex-1 flex flex-col ${isInIframe ? 'p-2' : 'p-4 md:p-8'}`}>
          {/* Task Status Banner */}
          {isUpcoming && (
            <div className={`bg-blue-900/50 border border-blue-500 rounded-lg text-center ${isInIframe ? 'p-1 mb-2' : 'p-3 mb-4'}`}>
              <p className={`text-blue-300 ${isInIframe ? 'text-sm' : 'text-lg'}`}>⏳ PROCHAINE TÂCHE À {currentTask.time}</p>
            </div>
          )}
          
          {taskCompleted && (
            <div className={`bg-green-900/50 border border-green-500 rounded-lg text-center ${isInIframe ? 'p-1 mb-2' : 'p-3 mb-4'}`}>
              <p className={`text-green-300 ${isInIframe ? 'text-sm' : 'text-lg'}`}>✅ TÂCHE TERMINÉE</p>
            </div>
          )}

          {/* WHO - Role */}
          <div className={`bg-amber-900/30 border-2 border-amber-500 rounded-xl ${isInIframe ? 'p-2 mb-2' : 'p-4 md:p-6 mb-4'}`}>
            <p className={`text-amber-400 uppercase tracking-wider ${isInIframe ? 'text-[10px] mb-0.5' : 'text-sm mb-1'}`}>VOTRE RÔLE</p>
            <h1 className={`font-bold text-white ${isInIframe ? 'text-lg' : 'text-3xl md:text-5xl'}`}>{currentTask.role}</h1>
          </div>

          {/* WHEN & WHERE */}
          <div className={`grid grid-cols-2 ${isInIframe ? 'gap-2 mb-2' : 'gap-4 mb-4'}`}>
            <div className={`bg-gray-900 border border-gray-700 rounded-xl ${isInIframe ? 'p-2' : 'p-4'}`}>
              <p className={`text-gray-400 uppercase tracking-wider ${isInIframe ? 'text-[10px] mb-0.5' : 'text-sm mb-1'}`}>🕐 QUAND</p>
              <p className={`font-bold text-white ${isInIframe ? 'text-lg' : 'text-2xl md:text-3xl'}`}>{currentTask.time}</p>
            </div>
            <div className={`bg-gray-900 border border-gray-700 rounded-xl ${isInIframe ? 'p-2' : 'p-4'}`}>
              <p className={`text-gray-400 uppercase tracking-wider ${isInIframe ? 'text-[10px] mb-0.5' : 'text-sm mb-1'}`}>📍 OÙ</p>
              <p className={`font-bold text-white ${isInIframe ? 'text-sm' : 'text-xl md:text-2xl'}`}>{currentTask.location}</p>
            </div>
          </div>

          {/* WHAT - Action */}
          <div className={`bg-red-900/30 border-2 border-red-500 rounded-xl ${isInIframe ? 'p-2 mb-2' : 'p-4 md:p-6 mb-4'}`}>
            <p className={`text-red-400 uppercase tracking-wider ${isInIframe ? 'text-[10px] mb-0.5' : 'text-sm mb-2'}`}>🎬 ACTION À ACCOMPLIR</p>
            <p className={`text-white font-semibold ${isInIframe ? 'text-sm' : 'text-xl md:text-2xl'}`}>{currentTask.action}</p>
          </div>

          {/* SCRIPT - What to say */}
          <div className={`bg-blue-900/30 border-2 border-blue-500 rounded-xl flex-1 ${isInIframe ? 'p-2 mb-2' : 'p-4 md:p-6 mb-4'}`}>
            <p className={`text-blue-400 uppercase tracking-wider ${isInIframe ? 'text-[10px] mb-0.5' : 'text-sm mb-2'}`}>💬 TEXTE À DIRE</p>
            <p className={`text-white leading-relaxed italic ${isInIframe ? 'text-xs' : 'text-lg md:text-xl'}`}>
              "{currentTask.script}"
            </p>
          </div>

          {/* EQUIPMENT - Hidden in iframe mode for space */}
          {!isInIframe && (
            <div className="bg-gray-800 border border-gray-600 rounded-xl p-4 mb-4">
              <p className="text-gray-400 text-sm uppercase tracking-wider mb-2">🎒 MATÉRIEL NÉCESSAIRE</p>
              <div className="flex flex-wrap gap-2">
                {currentTask.equipment.map((item, i) => (
                  <Badge key={i} className="bg-gray-700 text-white text-base px-3 py-1">
                    {item}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Action Button */}
          <Button 
            onClick={markTaskDone}
            className={`w-full bg-green-600 hover:bg-green-700 text-white rounded-xl ${isInIframe ? 'py-2 text-sm' : 'py-6 text-xl'}`}
            disabled={taskCompleted}
          >
            <Check className={`${isInIframe ? 'w-4 h-4' : 'w-6 h-6'} mr-2`} />
            {taskCompleted ? "TÂCHE TERMINÉE ✓" : "MARQUER COMME FAIT"}
          </Button>
        </div>
      )}

      {/* Bottom Nav for task switching - Hidden in iframe mode */}
      {isRunning && !isInIframe && (
        <div className="bg-gray-900 border-t border-gray-700 p-2">
          <div className="flex items-center justify-center gap-2 overflow-x-auto">
            {animatorTasks.map((task, i) => (
              <button
                key={i}
                onClick={() => setCurrentTaskIndex(i)}
                className={`px-3 py-1 rounded text-xs whitespace-nowrap ${
                  i === getCurrentTask().index 
                    ? 'bg-amber-600 text-black' 
                    : 'bg-gray-800 text-gray-400'
                }`}
              >
                {task.time} - {task.role.substring(0, 15)}...
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Quote Request Page - Sales Funnel
export const QuoteRequestPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    participants: 5,
    hours: 3,
    scenario_type: "",
    message: ""
  });
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [calculating, setCalculating] = useState(false);

  // Calculate quote when participants or hours change
  useEffect(() => {
    const calculateQuote = async () => {
      if (formData.participants < 1 || formData.hours < 2) return;
      setCalculating(true);
      try {
        const res = await axios.post(`${API}/quote/calculate`, {
          participants: formData.participants,
          hours: formData.hours
        });
        setQuote(res.data);
      } catch (err) {
        console.error(err);
      }
      setCalculating(false);
    };
    calculateQuote();
  }, [formData.participants, formData.hours]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.company) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }
    
    setLoading(true);
    try {
      const res = await axios.post(`${API}/quote/request`, formData);
      setSubmitted(true);
      toast.success("Demande de devis envoyée !");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'envoi");
    }
    setLoading(false);
  };

  const scenarioTypes = [
    { value: "incendie", label: "🔥 Incendie industriel" },
    { value: "cyberattaque", label: "💻 Cyberattaque" },
    { value: "produit", label: "📦 Crise produit" },
    { value: "maritime", label: "⚓ Crise maritime" },
    { value: "sociale", label: "👥 Crise sociale" },
    { value: "custom", label: "✨ Scénario sur mesure" }
  ];

  if (submitted) {
    return (
      <div className="min-h-screen matrix-bg flex items-center justify-center p-4">
        <ScanlineOverlay />
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="crisis-panel p-8 max-w-md text-center"
        >
          <div className="w-20 h-20 mx-auto bg-primary/20 rounded-full flex items-center justify-center mb-6">
            <Check className="w-10 h-10 text-primary" />
          </div>
          <h2 className="text-2xl font-bold heading text-primary mb-4">Demande Envoyée !</h2>
          <p className="text-muted-foreground mb-4">
            Merci pour votre intérêt. Vous recevrez votre devis proforma par email sous peu.
          </p>
          {quote && (
            <div className="bg-secondary/30 p-4 border border-border mb-6">
              <p className="text-sm text-muted-foreground">Estimation :</p>
              <p className="text-2xl font-bold text-primary">{quote.total_ht.toFixed(2)}€ HT</p>
              <p className="text-xs text-muted-foreground">({quote.total_ttc.toFixed(2)}€ TTC)</p>
            </div>
          )}
          <div className="space-y-2">
            <Button onClick={() => navigate("/demo")} className="w-full bg-primary text-primary-foreground">
              <Eye className="w-4 h-4 mr-2" /> Tester la démo
            </Button>
            <Button variant="outline" onClick={() => navigate("/")} className="w-full">
              <ArrowLeft className="w-4 h-4 mr-2" /> Retour à l'accueil
            </Button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen matrix-bg py-8 px-4">
      <ScanlineOverlay />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-4xl mx-auto"
      >
        <div className="text-center mb-8">
          <img 
            src={EGERIE_IMAGE} 
            alt="Crisis Simulator AI" 
            className="w-20 h-20 mx-auto object-cover rounded-full border-2 border-primary glow-green mb-4"
            style={{ objectPosition: 'center 20%' }}
          />
          <h1 className="text-3xl font-bold heading text-primary">Demander un Devis</h1>
          <p className="font-mono text-sm text-muted-foreground mt-2">
            Simulation de crise sur mesure pour votre entreprise
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Form */}
          <form onSubmit={handleSubmit} className="crisis-panel p-6 space-y-4">
            <h2 className="heading text-lg text-primary mb-4">Vos informations</h2>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground block mb-2">
                  <User className="w-3 h-3 inline mr-1" /> Nom complet *
                </label>
                <Input
                  className="input-matrix"
                  placeholder="Jean Dupont"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground block mb-2">
                  <Phone className="w-3 h-3 inline mr-1" /> Téléphone
                </label>
                <Input
                  className="input-matrix"
                  placeholder="06 12 34 56 78"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </div>
            </div>

            <div>
              <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground block mb-2">
                <Mail className="w-3 h-3 inline mr-1" /> Email professionnel *
              </label>
              <Input
                type="email"
                className="input-matrix"
                placeholder="jean.dupont@entreprise.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </div>

            <div>
              <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground block mb-2">
                <Building className="w-3 h-3 inline mr-1" /> Entreprise *
              </label>
              <Input
                className="input-matrix"
                placeholder="ACME Corporation"
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground block mb-2">
                  <Users className="w-3 h-3 inline mr-1" /> Participants *
                </label>
                <Input
                  type="number"
                  min="1"
                  className="input-matrix"
                  value={formData.participants}
                  onChange={(e) => setFormData({ ...formData, participants: parseInt(e.target.value) || 1 })}
                />
              </div>
              <div>
                <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground block mb-2">
                  <Clock className="w-3 h-3 inline mr-1" /> Durée (heures) *
                </label>
                <Input
                  type="number"
                  min="2"
                  className="input-matrix"
                  value={formData.hours}
                  onChange={(e) => setFormData({ ...formData, hours: parseInt(e.target.value) || 2 })}
                />
                <p className="text-[10px] text-muted-foreground mt-1">Minimum 2 heures</p>
              </div>
            </div>

            <div>
              <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground block mb-2">
                <AlertTriangle className="w-3 h-3 inline mr-1" /> Type de scénario
              </label>
              <select
                className="input-matrix w-full h-10 px-3"
                value={formData.scenario_type}
                onChange={(e) => setFormData({ ...formData, scenario_type: e.target.value })}
              >
                <option value="">Sélectionner un scénario...</option>
                {scenarioTypes.map(s => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground block mb-2">
                <FileText className="w-3 h-3 inline mr-1" /> Message (optionnel)
              </label>
              <Textarea
                className="input-matrix min-h-[80px]"
                placeholder="Précisions sur vos besoins, contexte particulier..."
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-bold uppercase tracking-wider mt-4"
              disabled={loading}
            >
              {loading ? (
                <>
                  <div className="loading-dot" />
                  <div className="loading-dot" />
                  <div className="loading-dot" />
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" /> Demander le devis
                </>
              )}
            </Button>
          </form>

          {/* Quote Preview */}
          <div className="space-y-4">
            <div className="crisis-panel p-6">
              <h2 className="heading text-lg text-primary mb-4">Estimation tarifaire</h2>
              
              {calculating ? (
                <div className="flex justify-center py-8">
                  <div className="loading-dot" />
                  <div className="loading-dot" />
                  <div className="loading-dot" />
                </div>
              ) : quote ? (
                <div className="space-y-4">
                  <div className="flex justify-between items-center py-2 border-b border-border">
                    <span className="text-muted-foreground">Tarif horaire / personne</span>
                    <span className="font-mono font-bold">{quote.hourly_rate}€ HT</span>
                  </div>
                  
                  <div className="flex justify-between items-center py-2 border-b border-border">
                    <span className="text-muted-foreground">{quote.participants} participants × {quote.hours}h</span>
                    <span className="font-mono">{quote.subtotal.toFixed(2)}€</span>
                  </div>
                  
                  {quote.discount_percent > 0 && (
                    <div className="flex justify-between items-center py-2 border-b border-border text-green-500">
                      <span>Remise volume (-{quote.discount_percent}%)</span>
                      <span className="font-mono">-{quote.discount_amount.toFixed(2)}€</span>
                    </div>
                  )}
                  
                  <div className="flex justify-between items-center py-3 border-b-2 border-primary">
                    <span className="font-bold">Total HT</span>
                    <span className="font-mono font-bold text-xl">{quote.total_ht.toFixed(2)}€</span>
                  </div>
                  
                  <div className="flex justify-between items-center py-2 text-muted-foreground">
                    <span>TVA (20%)</span>
                    <span className="font-mono">{quote.tva.toFixed(2)}€</span>
                  </div>
                  
                  <div className="flex justify-between items-center py-4 bg-primary/20 px-4 -mx-6 mb-0">
                    <span className="font-bold text-lg">Total TTC</span>
                    <span className="font-mono font-bold text-2xl text-primary">{quote.total_ttc.toFixed(2)}€</span>
                  </div>
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-8">
                  Renseignez le nombre de participants et la durée pour voir l'estimation
                </p>
              )}
            </div>

            {/* Pricing Info */}
            <div className="crisis-panel p-6">
              <h3 className="heading text-sm text-primary mb-3">💰 Tarification</h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-primary" />
                  <span>250€ HT / heure / participant</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-primary" />
                  <span>Durée minimum : 2 heures</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500" />
                  <span><strong>-20%</strong> à partir de 5 participants</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500" />
                  <span><strong>-30%</strong> à partir de 10 participants</span>
                </li>
              </ul>
            </div>

            {/* Contact */}
            <div className="crisis-panel p-6">
              <h3 className="heading text-sm text-primary mb-3">📞 Contact direct</h3>
              <p className="text-muted-foreground text-sm mb-2">
                Pour toute question ou demande spécifique :
              </p>
              <p className="font-mono text-primary">06 63 21 12 34</p>
              <p className="font-mono text-sm text-muted-foreground">jbgiraud@simulationdecrise.fr</p>
            </div>
          </div>
        </div>

        <div className="text-center mt-8">
          <Button variant="ghost" onClick={() => navigate("/")} className="text-muted-foreground">
            <ArrowLeft className="w-4 h-4 mr-2" /> Retour à l'accueil
          </Button>
        </div>

        <div className="text-center mt-4">
          <p className="font-mono text-[10px] text-muted-foreground">
            © {APP_DATE} {APP_EDITOR} - {APP_VERSION}
          </p>
        </div>
      </motion.div>
    </div>
  );
};

// User Guides Page - Downloadable PDF-ready guides
export const UserGuidesPage = () => {
  const navigate = useNavigate();
  const printRef = useRef(null);
  const [activeGuide, setActiveGuide] = useState('admin'); // 'admin' or 'client'

  const handlePrint = () => {
    window.print();
  };

  const BASE_URL = window.location.origin;

  return (
    <div className="min-h-screen matrix-bg print:bg-white print:min-h-0">
      <ScanlineOverlay />
      
      {/* Screen Header - Hidden in print */}
      <div className="crisis-header print:hidden">
        <div className="flex items-center gap-4">
          <FileText className="w-8 h-8 text-primary" />
          <div>
            <h1 className="heading text-lg text-primary">Modes d'Emploi</h1>
            <p className="font-mono text-xs text-muted-foreground">Guides téléchargeables et imprimables</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant={activeGuide === 'admin' ? 'default' : 'outline'}
            onClick={() => setActiveGuide('admin')}
            className={activeGuide === 'admin' ? 'bg-primary' : ''}
          >
            <Shield className="w-4 h-4 mr-2" /> Guide Admin
          </Button>
          <Button
            variant={activeGuide === 'client' ? 'default' : 'outline'}
            onClick={() => setActiveGuide('client')}
            className={activeGuide === 'client' ? 'bg-primary' : ''}
          >
            <User className="w-4 h-4 mr-2" /> Guide Client
          </Button>
          <Button onClick={handlePrint} className="bg-green-600 hover:bg-green-700">
            <FileText className="w-4 h-4 mr-2" /> Imprimer / PDF
          </Button>
          <Button variant="ghost" onClick={() => navigate("/")}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Retour
          </Button>
        </div>
      </div>

      {/* Printable Content */}
      <div ref={printRef} className="p-4 sm:p-8 max-w-4xl mx-auto print:p-0 print:max-w-none">
        
        {/* ==================== ADMIN GUIDE ==================== */}
        {activeGuide === 'admin' && (
          <div className="space-y-6 print:space-y-4">
            {/* Cover Page */}
            <div className="crisis-panel p-8 print:border-2 print:border-black print:bg-white text-center">
              <div className="mb-6">
                <img 
                  src="https://customer-assets.emergentagent.com/job_crisis-sim-ai/artifacts/bgij8dxp_image.png" 
                  alt="Crisis Simulator AI" 
                  className="w-24 h-24 mx-auto object-cover rounded-full border-2 border-primary print:border-black"
                  style={{ objectPosition: 'center 20%' }}
                />
              </div>
              <h1 className="text-3xl font-bold text-primary print:text-black mb-2">
                📋 MODE D'EMPLOI ADMINISTRATEUR
              </h1>
              <h2 className="text-xl text-muted-foreground print:text-gray-600 mb-4">
                Maître de Jeu / Facilitateur
              </h2>
              <p className="text-sm text-muted-foreground print:text-gray-500">
                Crisis Simulator AI - {APP_VERSION} - {APP_DATE}
              </p>
              <p className="text-xs mt-2">© {APP_EDITOR}</p>
            </div>

            {/* Quick Access URLs */}
            <div className="crisis-panel p-6 print:border-2 print:border-black print:bg-white">
              <h2 className="text-xl font-bold text-primary print:text-black mb-4 flex items-center gap-2">
                🔗 LIENS D'ACCÈS RAPIDE
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-secondary/50 print:bg-gray-100 rounded-lg">
                  <h3 className="font-bold text-primary print:text-black mb-2">🔐 Connexion Admin</h3>
                  <p className="font-mono text-xs break-all text-muted-foreground mb-2">{BASE_URL}/login</p>
                  <p className="text-xs text-muted-foreground mb-3">Email: jb@simulationdecrise.fr</p>
                  <div className="flex gap-2 print:hidden">
                    <Button 
                      size="sm" 
                      className="flex-1 bg-primary text-primary-foreground" 
                      onClick={() => window.open(`${BASE_URL}/login`, '_blank')}
                    >
                      <ArrowLeft className="w-3 h-3 mr-1 rotate-180" /> Accéder
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="flex-1"
                      onClick={() => {
                        navigator.clipboard.writeText(`${BASE_URL}/login`);
                        toast.success("Lien copié !");
                      }}
                    >
                      <Share2 className="w-3 h-3 mr-1" /> Partager
                    </Button>
                  </div>
                </div>
                <div className="p-4 bg-secondary/50 print:bg-gray-100 rounded-lg">
                  <h3 className="font-bold text-primary print:text-black mb-2">📊 Tableau de Bord Admin</h3>
                  <p className="font-mono text-xs break-all text-muted-foreground mb-2">{BASE_URL}/admin</p>
                  <p className="text-xs text-muted-foreground mb-3">Gérer sessions et scénarios</p>
                  <div className="flex gap-2 print:hidden">
                    <Button 
                      size="sm" 
                      className="flex-1 bg-primary text-primary-foreground" 
                      onClick={() => window.open(`${BASE_URL}/admin`, '_blank')}
                    >
                      <ArrowLeft className="w-3 h-3 mr-1 rotate-180" /> Accéder
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="flex-1"
                      onClick={() => {
                        navigator.clipboard.writeText(`${BASE_URL}/admin`);
                        toast.success("Lien copié !");
                      }}
                    >
                      <Share2 className="w-3 h-3 mr-1" /> Partager
                    </Button>
                  </div>
                </div>
                <div className="p-4 bg-secondary/50 print:bg-gray-100 rounded-lg">
                  <h3 className="font-bold text-primary print:text-black mb-2">🎮 Mode Démo Admin</h3>
                  <p className="font-mono text-xs break-all text-muted-foreground mb-2">{BASE_URL}/demo/admin</p>
                  <p className="text-xs text-muted-foreground mb-3">Tester le panneau de contrôle</p>
                  <div className="flex gap-2 print:hidden">
                    <Button 
                      size="sm" 
                      className="flex-1 bg-primary text-primary-foreground" 
                      onClick={() => window.open(`${BASE_URL}/demo/admin`, '_blank')}
                    >
                      <ArrowLeft className="w-3 h-3 mr-1 rotate-180" /> Accéder
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="flex-1"
                      onClick={() => {
                        navigator.clipboard.writeText(`${BASE_URL}/demo/admin`);
                        toast.success("Lien copié !");
                      }}
                    >
                      <Share2 className="w-3 h-3 mr-1" /> Partager
                    </Button>
                  </div>
                </div>
                <div className="p-4 bg-secondary/50 print:bg-gray-100 rounded-lg">
                  <h3 className="font-bold text-primary print:text-black mb-2">👁️ Mode Observateur Démo</h3>
                  <p className="font-mono text-xs break-all text-muted-foreground mb-2">{BASE_URL}/demo/observer</p>
                  <p className="text-xs text-muted-foreground mb-3">Dashboard lecture seule pour DRH/CEO</p>
                  <div className="flex gap-2 print:hidden">
                    <Button 
                      size="sm" 
                      className="flex-1 bg-primary text-primary-foreground" 
                      onClick={() => window.open(`${BASE_URL}/demo/observer`, '_blank')}
                    >
                      <ArrowLeft className="w-3 h-3 mr-1 rotate-180" /> Accéder
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="flex-1"
                      onClick={() => {
                        navigator.clipboard.writeText(`${BASE_URL}/demo/observer`);
                        toast.success("Lien copié !");
                      }}
                    >
                      <Share2 className="w-3 h-3 mr-1" /> Partager
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Step by Step Guide */}
            <div className="crisis-panel p-6 print:border-2 print:border-black print:bg-white">
              <h2 className="text-xl font-bold text-primary print:text-black mb-4 flex items-center gap-2">
                📝 GUIDE PAS À PAS
              </h2>
              
              <div className="space-y-4">
                <div className="p-4 border-l-4 border-primary print:border-black bg-secondary/30 print:bg-gray-50">
                  <h3 className="font-bold mb-2">1️⃣ AVANT LA SIMULATION</h3>
                  <ul className="list-disc list-inside text-sm space-y-1">
                    <li>Connectez-vous sur <span className="font-mono">/login</span> avec vos identifiants</li>
                    <li>Créez une nouvelle session depuis <span className="font-mono">/admin</span></li>
                    <li>Sélectionnez le scénario (Incendie, Cyberattaque, etc.)</li>
                    <li>Ajoutez les participants avec leurs rôles COMEX</li>
                    <li>Imprimez les fiches de connexion (QR codes)</li>
                    <li>Testez le mode démo : <span className="font-mono">/demo/admin</span></li>
                  </ul>
                </div>

                <div className="p-4 border-l-4 border-yellow-500 bg-secondary/30 print:bg-gray-50">
                  <h3 className="font-bold mb-2">2️⃣ PENDANT LA SIMULATION</h3>
                  <ul className="list-disc list-inside text-sm space-y-1">
                    <li>Ouvrez le panneau de contrôle animateur (<span className="font-mono">/animator/[sessionId]</span>)</li>
                    <li>Déclenchez les événements selon le timing prévu</li>
                    <li>Surveillez les réactions des participants en temps réel</li>
                    <li>Utilisez les flashs radio pour augmenter la pression médiatique</li>
                    <li>Injectez des événements manuels si nécessaire</li>
                    <li>Mettez en pause si besoin (les participants verront "PAUSE")</li>
                  </ul>
                </div>

                <div className="p-4 border-l-4 border-green-500 bg-secondary/30 print:bg-gray-50">
                  <h3 className="font-bold mb-2">3️⃣ APRÈS LA SIMULATION (RETEX)</h3>
                  <ul className="list-disc list-inside text-sm space-y-1">
                    <li>Consultez le dashboard observateur pour les métriques</li>
                    <li>Exportez les logs d'actions pour le débriefing</li>
                    <li>Analysez les temps de réponse de l'équipe</li>
                    <li>Révisez les communiqués de presse rédigés</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Control Panel Features */}
            <div className="crisis-panel p-6 print:border-2 print:border-black print:bg-white">
              <h2 className="text-xl font-bold text-primary print:text-black mb-4 flex items-center gap-2">
                🎛️ FONCTIONNALITÉS DU PANNEAU DE CONTRÔLE
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div className="p-3 bg-secondary/30 print:bg-gray-50 rounded">
                  <h4 className="font-bold text-primary print:text-black">📻 Flashs Radio</h4>
                  <p>Déclenchez des flashs info France Info, Europe 1, RTL avec jingle et synthèse vocale</p>
                </div>
                <div className="p-3 bg-secondary/30 print:bg-gray-50 rounded">
                  <h4 className="font-bold text-primary print:text-black">📞 Appels Téléphoniques</h4>
                  <p>Simulez des appels entrants (Préfet, pompiers, journalistes)</p>
                </div>
                <div className="p-3 bg-secondary/30 print:bg-gray-50 rounded">
                  <h4 className="font-bold text-primary print:text-black">📰 Dépêches AFP</h4>
                  <p>Injectez des articles de presse en temps réel</p>
                </div>
                <div className="p-3 bg-secondary/30 print:bg-gray-50 rounded">
                  <h4 className="font-bold text-primary print:text-black">📱 Réseaux Sociaux</h4>
                  <p>Simulez un bad buzz Twitter/X, LinkedIn, Facebook</p>
                </div>
                <div className="p-3 bg-secondary/30 print:bg-gray-50 rounded">
                  <h4 className="font-bold text-primary print:text-black">📺 BFM TV</h4>
                  <p>Micro-trottoir avec témoignages générés par IA</p>
                </div>
                <div className="p-3 bg-secondary/30 print:bg-gray-50 rounded">
                  <h4 className="font-bold text-primary print:text-black">⏯️ Pause/Play</h4>
                  <p>Contrôlez le rythme de la simulation</p>
                </div>
              </div>
            </div>

            {/* Access Info - No credentials shown publicly */}
            <div className="crisis-panel p-6 print:border-2 print:border-black print:bg-white bg-primary/10">
              <h2 className="text-xl font-bold text-primary print:text-black mb-4 flex items-center gap-2">
                🔗 ACCÈS ADMINISTRATION
              </h2>
              <div className="bg-secondary print:bg-gray-100 p-4 rounded font-mono text-sm">
                <p><strong>URL de connexion:</strong> {BASE_URL}/login</p>
                <p className="text-xs text-muted-foreground mt-2">Les identifiants vous seront communiqués par votre administrateur.</p>
              </div>
            </div>
          </div>
        )}

        {/* ==================== CLIENT GUIDE ==================== */}
        {activeGuide === 'client' && (
          <div className="space-y-6 print:space-y-4">
            {/* Cover Page */}
            <div className="crisis-panel p-8 print:border-2 print:border-black print:bg-white text-center">
              <div className="mb-6">
                <img 
                  src="https://customer-assets.emergentagent.com/job_crisis-sim-ai/artifacts/bgij8dxp_image.png" 
                  alt="Crisis Simulator AI" 
                  className="w-24 h-24 mx-auto object-cover rounded-full border-2 border-primary print:border-black"
                  style={{ objectPosition: 'center 20%' }}
                />
              </div>
              <h1 className="text-3xl font-bold text-primary print:text-black mb-2">
                📋 MODE D'EMPLOI PARTICIPANT
              </h1>
              <h2 className="text-xl text-muted-foreground print:text-gray-600 mb-4">
                DRH / CEO / Membres COMEX
              </h2>
              <p className="text-sm text-muted-foreground print:text-gray-500">
                Crisis Simulator AI - {APP_VERSION} - {APP_DATE}
              </p>
              <p className="text-xs mt-2">© {APP_EDITOR}</p>
            </div>

            {/* Quick Access */}
            <div className="crisis-panel p-6 print:border-2 print:border-black print:bg-white">
              <h2 className="text-xl font-bold text-primary print:text-black mb-4 flex items-center gap-2">
                🔗 COMMENT ACCÉDER À LA SIMULATION
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-secondary/50 print:bg-gray-100 rounded-lg">
                  <h3 className="font-bold text-primary print:text-black mb-2">🎮 Mode Démo Joueur</h3>
                  <p className="font-mono text-xs break-all mb-2 text-muted-foreground">{BASE_URL}/demo/client</p>
                  <p className="text-xs text-muted-foreground mb-3">Testez l'interface sans simulation réelle</p>
                  <div className="flex gap-2 print:hidden">
                    <Button 
                      size="sm" 
                      className="flex-1 bg-primary text-primary-foreground" 
                      onClick={() => window.open(`${BASE_URL}/demo/client`, '_blank')}
                    >
                      <ArrowLeft className="w-3 h-3 mr-1 rotate-180" /> Accéder
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="flex-1"
                      onClick={() => {
                        navigator.clipboard.writeText(`${BASE_URL}/demo/client`);
                        toast.success("Lien copié !");
                      }}
                    >
                      <Share2 className="w-3 h-3 mr-1" /> Partager
                    </Button>
                  </div>
                </div>
                <div className="p-4 bg-secondary/50 print:bg-gray-100 rounded-lg">
                  <h3 className="font-bold text-primary print:text-black mb-2">👁️ Mode Observateur Démo</h3>
                  <p className="font-mono text-xs break-all mb-2 text-muted-foreground">{BASE_URL}/demo/observer</p>
                  <p className="text-xs text-muted-foreground mb-3">Vue lecture seule pour DRH/CEO</p>
                  <div className="flex gap-2 print:hidden">
                    <Button 
                      size="sm" 
                      className="flex-1 bg-primary text-primary-foreground" 
                      onClick={() => window.open(`${BASE_URL}/demo/observer`, '_blank')}
                    >
                      <ArrowLeft className="w-3 h-3 mr-1 rotate-180" /> Accéder
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="flex-1"
                      onClick={() => {
                        navigator.clipboard.writeText(`${BASE_URL}/demo/observer`);
                        toast.success("Lien copié !");
                      }}
                    >
                      <Share2 className="w-3 h-3 mr-1" /> Partager
                    </Button>
                  </div>
                </div>
                <div className="p-4 bg-green-500/20 print:bg-green-100 rounded-lg border-2 border-green-500 print:border-green-600 md:col-span-2">
                  <h3 className="font-bold text-green-600 mb-2">✨ SIMULATION RÉELLE</h3>
                  <p className="text-sm mb-2">Lors d'une vraie simulation, vous recevrez :</p>
                  <ul className="list-disc list-inside text-sm space-y-1">
                    <li>Un <strong>QR code personnel</strong> à scanner</li>
                    <li>Ou un <strong>lien unique</strong> avec votre token d'accès</li>
                    <li>Ou un <strong>code session</strong> à entrer sur {BASE_URL}</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* What to Expect */}
            <div className="crisis-panel p-6 print:border-2 print:border-black print:bg-white">
              <h2 className="text-xl font-bold text-primary print:text-black mb-4 flex items-center gap-2">
                🎯 CE QUI VOUS ATTEND
              </h2>
              <div className="space-y-4">
                <div className="p-4 border-l-4 border-red-500 bg-secondary/30 print:bg-gray-50">
                  <h3 className="font-bold mb-2">📰 FLUX D'INFORMATIONS</h3>
                  <p className="text-sm">Vous recevrez en temps réel :</p>
                  <ul className="list-disc list-inside text-sm mt-2 space-y-1">
                    <li>Dépêches AFP et articles de presse</li>
                    <li>Posts sur les réseaux sociaux (X, LinkedIn, Facebook)</li>
                    <li>Flashs radio (France Info, Europe 1, RTL)</li>
                    <li>Reportages TV (BFM TV)</li>
                    <li>Messages internes et appels téléphoniques</li>
                  </ul>
                </div>

                <div className="p-4 border-l-4 border-blue-500 bg-secondary/30 print:bg-gray-50">
                  <h3 className="font-bold mb-2">🛠️ VOS OUTILS</h3>
                  <p className="text-sm">Vous disposerez de :</p>
                  <ul className="list-disc list-inside text-sm mt-2 space-y-1">
                    <li><strong>Éditeur de Communiqué de Presse</strong> - avec assistance IA</li>
                    <li><strong>Éditeur de Posts Réseaux Sociaux</strong> - Twitter, LinkedIn, Facebook</li>
                    <li><strong>Messagerie COMEX</strong> - pour communiquer avec votre équipe</li>
                    <li><strong>Réponse vocale</strong> - pour répondre aux appels téléphoniques</li>
                  </ul>
                </div>

                <div className="p-4 border-l-4 border-amber-500 bg-secondary/30 print:bg-gray-50">
                  <h3 className="font-bold mb-2">⚡ CONSEILS</h3>
                  <ul className="list-disc list-inside text-sm space-y-1">
                    <li>Gardez votre calme - c'est une simulation !</li>
                    <li>Lisez attentivement chaque information avant de réagir</li>
                    <li>Coordonnez-vous avec les autres membres du COMEX</li>
                    <li>Utilisez les outils IA pour gagner du temps</li>
                    <li>Activez le son pour les flashs radio et appels</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Interface Guide */}
            <div className="crisis-panel p-6 print:border-2 print:border-black print:bg-white">
              <h2 className="text-xl font-bold text-primary print:text-black mb-4 flex items-center gap-2">
                📱 NAVIGATION DANS L'INTERFACE
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div className="p-3 bg-secondary/30 print:bg-gray-50 rounded">
                  <h4 className="font-bold text-primary print:text-black">📰 Onglet PRESSE</h4>
                  <p>Dépêches AFP, articles de journaux (Le Monde, Le Figaro...)</p>
                </div>
                <div className="p-3 bg-secondary/30 print:bg-gray-50 rounded">
                  <h4 className="font-bold text-primary print:text-black">📻 Onglet RADIO / TV</h4>
                  <p>Flashs radio et reportages BFM TV</p>
                </div>
                <div className="p-3 bg-secondary/30 print:bg-gray-50 rounded">
                  <h4 className="font-bold text-primary print:text-black">📱 Onglet RÉSEAUX</h4>
                  <p>Posts Twitter/X, LinkedIn, Facebook à surveiller</p>
                </div>
                <div className="p-3 bg-secondary/30 print:bg-gray-50 rounded">
                  <h4 className="font-bold text-primary print:text-black">💬 Onglet INTERNE</h4>
                  <p>Messages internes, emails, messagerie COMEX</p>
                </div>
              </div>
              <div className="mt-4 p-4 bg-primary/10 print:bg-gray-100 rounded">
                <h4 className="font-bold text-primary print:text-black mb-2">🔧 Bouton "Vos Outils"</h4>
                <p className="text-sm">En bas à droite de l'écran, ce bouton vous donne accès à :</p>
                <ul className="list-disc list-inside text-sm mt-2">
                  <li>L'éditeur de communiqué de presse</li>
                  <li>L'éditeur de posts réseaux sociaux</li>
                </ul>
              </div>
            </div>

            {/* Support */}
            <div className="crisis-panel p-6 print:border-2 print:border-black print:bg-white bg-primary/10">
              <h2 className="text-xl font-bold text-primary print:text-black mb-4 flex items-center gap-2">
                ❓ BESOIN D'AIDE ?
              </h2>
              <p className="text-sm mb-4">
                En cas de problème technique pendant la simulation, levez la main pour alerter l'animateur 
                ou utilisez le canal de communication prévu.
              </p>
              <div className="bg-secondary print:bg-gray-100 p-4 rounded font-mono text-sm">
                <p><strong>Contact support:</strong> communication@simulationdecrise.fr</p>
                <p><strong>Site web:</strong> simulationdecrise.fr</p>
              </div>
            </div>

            {/* Demo Quick Start */}
            <div className="crisis-panel p-6 print:border-2 print:border-black print:bg-white border-2 border-green-500">
              <h2 className="text-xl font-bold text-green-600 mb-4 flex items-center gap-2">
                🚀 DÉMARRAGE RAPIDE - MODE DÉMO
              </h2>
              <ol className="list-decimal list-inside text-sm space-y-2">
                <li>Ouvrez votre navigateur (Chrome recommandé)</li>
                <li>Allez sur <span className="font-mono bg-secondary print:bg-gray-100 px-2 py-1 rounded">{BASE_URL}/demo/client</span></li>
                <li>L'interface de simulation s'affiche immédiatement</li>
                <li>Cliquez sur "▶ Commencer la simulation"</li>
                <li>Les événements arrivent progressivement</li>
                <li>Explorez les onglets et les outils disponibles</li>
              </ol>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="text-center mt-8 print:mt-4">
          <p className="font-mono text-xs text-muted-foreground print:text-gray-500">
            Crisis Simulator AI - © {APP_DATE} {APP_EDITOR} - {APP_VERSION}
          </p>
          <p className="font-mono text-xs text-muted-foreground print:text-gray-500 mt-1">
            simulationdecrise.fr
          </p>
        </div>
      </div>

      {/* Print-only styles */}
      <style>{`
        @media print {
          .print\\:hidden { display: none !important; }
          .print\\:bg-white { background: white !important; }
          .print\\:border-black { border-color: black !important; }
          .print\\:border-2 { border-width: 2px !important; }
          .print\\:text-black { color: black !important; }
          .print\\:text-gray-600 { color: #4b5563 !important; }
          .print\\:text-gray-500 { color: #6b7280 !important; }
          .print\\:bg-gray-50 { background: #f9fafb !important; }
          .print\\:bg-gray-100 { background: #f3f4f6 !important; }
          .print\\:bg-green-100 { background: #dcfce7 !important; }
          .print\\:border-green-600 { border-color: #16a34a !important; }
          .scanline-overlay { display: none !important; }
          body { background: white !important; }
          .crisis-panel { 
            page-break-inside: avoid;
            margin-bottom: 15px;
          }
        }
      `}</style>
    </div>
  );
};

export default DemoSelection;
